import React, { useState, useEffect, useRef, useCallback } from "react";
import useInvelpsTheme from "../../../hooks/useInvelpsTheme";
import {
  Radar as RechartsRadar, RadarChart as RechartsRadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from "recharts";

// ── BREAKPOINT ──────────────────────────────────────────────────────────────
function useBreakpoint() {
  const get = () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1200;
    return w < 480 ? "xs" : w < 768 ? "sm" : w < 1100 ? "md" : "lg";
  };
  const [bp, setBp] = useState(get);
  useEffect(() => { const fn = () => setBp(get()); window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn); }, []);
  return bp;
}
const BpCtx = React.createContext("lg");
const useBp = () => React.useContext(BpCtx);

// ── THEME ───────────────────────────────────────────────────────────────────
function applyTheme(dark) {
  // Palette alignée sur MarketStructureCard / RisqueRendementDrawer (tons olive/crème, cohérence inter-cards)
  const r = document.documentElement, s = (k,v) => r.style.setProperty(k,v);
  s("--bg",   dark?"#0b1220"              :"#f6f5f1");
  s("--card", dark?"rgba(13,20,38,0.97)"  :"rgba(255,255,255,0.98)");
  s("--bdr",  dark?"rgba(111,138,91,.18)" :"rgba(30,40,34,.10)");
  s("--ts",   dark?"#e8e4da":"#1f2a24");
  s("--tx",   dark?"#cdc9be":"#2d3b34");
  s("--mu",   dark?"#7a8172":"#6f7b74");
  s("--sep",  dark?"rgba(111,138,91,.12)":"rgba(30,40,34,.07)");
  s("--sur",  dark?"rgba(111,138,91,.05)":"rgba(0,0,0,.016)");
  s("--gl",   dark?"rgba(111,138,91,.07)":"rgba(30,40,34,.055)");
  s("--tbg",  dark?"rgba(6,10,22,0.97)":"rgba(255,255,255,0.99)");
  s("--tbr",  dark?"rgba(202,168,94,0.28)":"rgba(30,40,34,0.12)");
  r.classList.toggle("dark", dark);
}
const C = { g:"#6f8a5b",g2:"#8da57a",ol:"#a9b38a",am:"#caa85e",or:"#d28a52",r:"#c6523f",edu:"#7a8fc4",bl:"#7a8fc4" };
const STOPS = ["#c6523f","#d28a52","#caa85e","#a9b38a","#8da57a","#6f8a5b"];

// ── V7 Design System — Helpers de couleur centralisés ─────────────────────────
// Source unique de vérité pour toutes les couleurs de l'UI.
// Aucune couleur codée en dur en dehors de ces fonctions.

/** Score 0-100 → couleur spectrale (rouge→ambre→vert) */
function getScoreColor(v) {
  if (v == null || isNaN(v)) return C.am;
  const t = Math.max(0, Math.min(1, v / 100));
  const si = Math.min(STOPS.length - 2, Math.floor((STOPS.length - 1) * t));
  const lo = STOPS[si], hi = STOPS[si + 1];
  const f = (STOPS.length - 1) * t - si;
  const h = (s, e) => Math.round(parseInt(s, 16) + f * (parseInt(e, 16) - parseInt(s, 16))).toString(16).padStart(2, "0");
  return "#" + h(lo.slice(1,3),hi.slice(1,3)) + h(lo.slice(3,5),hi.slice(3,5)) + h(lo.slice(5,7),hi.slice(5,7));
}
// Alias — même logique, nom sémantique différent selon le contexte
const heatColor = getScoreColor;

/** Score 0-100 → libellé qualitatif (mêmes seuils que qualColor : 90/75/55/35) */
function scoreLabel(v) {
  if (v == null || isNaN(v)) return "—";
  if (v >= 90) return "Excellent";
  if (v >= 75) return "Solide";
  if (v >= 55) return "Satisfaisant";
  if (v >= 35) return "Fragile";
  return "Critique";
}

/** Signal/statut → couleur sémantique */
function getStatusColor(status) {
  if (!status) return C.am;
  const s = status.toUpperCase();
  if (['YES','OK','EXCELLENT','BON','GOOD','STRONG_BUY','BUY','POSITIVE','SOLID','PASS'].includes(s)) return C.g2;
  if (['NO','NOK','CRITICAL','FAIL','SELL','REDUCE','NEGATIVE'].includes(s)) return C.r;
  if (['WATCH','WARN','HOLD','FRAGILE','NEUTRAL','MODERATE'].includes(s)) return C.am;
  return C.am;
}

/** Variation (positive/négative/nulle) → couleur */
function getVariationColor(v, higherIsBetter = true) {
  if (v == null || isNaN(v)) return C.am;
  if (v === 0) return C.am;
  return (v > 0) === higherIsBetter ? C.g2 : C.r;
}

/** Pilier → couleur basée sur son score (pas sur une couleur sémantique arbitraire) */
function getPillarColor(pillarKey) {
  const scoreKey = {
    liquidity:'liquidity_score', profitability:'profitability_score',
    growth:'growth_score', efficiency:'efficiency_score',
    rentability:'rentability_score', cashflow:'cashflow_score',
    solvency:'solvency_score', capalloc:'capalloc_score',
    value_creation:'value_creation_score',
  }[pillarKey];
  const score = scoreKey ? RAW[scoreKey]?.[YN] : null;
  return score != null ? getScoreColor(score) : C.am;
}

/** Phase de marché → couleur */
function getPhaseColor(phase) {
  if (!phase) return C.am;
  const p = phase.toUpperCase();
  if (p.includes('BULL') || p.includes('EXPANSION') || p.includes('GROWTH')) return C.g2;
  if (p.includes('BEAR') || p.includes('CONTRACTION') || p.includes('DECLINE')) return C.r;
  return C.am;
}

/** Score → label qualitatif */

function CompassIcon({ size = 18, boxed = false }) {
  const inner = (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth="2.8" opacity=".95"/>
      <path d="M15.8 8.2c-3.9.15-6.7 2.65-7.35 6.95 4.35-.62 6.82-3.45 7.35-6.95Z" fill="currentColor" opacity=".86"/>
      <path d="M8.45 15.15 15.8 8.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" opacity=".75"/>
    </svg>
  );
  if (!boxed) return inner;
  return (
    <span className="compass-box">
      {inner}
    </span>
  );
}


// ── DONNÉES DE DÉMONSTRATION (fallback · COFA.PA Coface SA — données réelles) ─
// Utilisées tant que le fetch API n'a pas abouti, afin d'avoir un aperçu
// fonctionnel immédiat. Remplacées en mémoire par hydrateLiveData() une fois
// la réponse serveur reçue (cf. bloc "COUCHE API" après PILLAR_INSIGHTS).
// ⚠️ Issues du résultat réel de REQ1/REQ2 sur COFA.PA (cf. conversation) :
// RAW, COMPANY_META, scores piliers, labels/tendances, diag_ok, FUND_META
// (champs courts) et DB_ANALYSE (dernière année) ont été extraits et validés
// avec un contrôle programmatique (comptage exact des tokens, aucune perte).
// Les champs texte très longs (narrative, tooltips, insights par pilier,
// FScore_summary détaillé) n'ont PAS été transcrits à la main — risque
// d'erreur de découpage trop élevé sur du CSV non échappé — et sont laissés
// à null : le front bascule proprement sur ses fallbacks existants.
const DEMO_YEARS = [2021,2022,2023,2024,2025];
const DEMO_YN = 4; // index dernière année (2025)

// ── DEMO_RAW — COFA.PA (Coface SA), données réelles issues de REQ2 (5 ans) ──
// Extrait et validé à partir du résultat SQL fourni par Shixo (voir conversation).
const DEMO_RAW = {
  "current_ratio": [
    5.263381,
    0.262155,
    87.525184,
    3.930981,
    3.768341
  ],
  "quick_ratio": [
    5.263381,
    0.262155,
    87.525184,
    3.930981,
    3.768341
  ],
  "cash_ratio": [
    5.263381,
    0.262155,
    55.143152,
    1.90016,
    1.815733
  ],
  "gross_margin": [
    0.791588,
    0.891923,
    0.877197,
    0.880836,
    1.0
  ],
  "ebitda_margin": [
    0.267642,
    0.252798,
    0.252007,
    0.270943,
    0.193008
  ],
  "operating_margin": [
    0.227358,
    0.20813,
    0.205214,
    0.222138,
    0.153628
  ],
  "net_margin": [
    0.174635,
    0.15274,
    0.150168,
    0.158334,
    0.116963
  ],
  "pretax_margin": [
    0.227358,
    0.20813,
    0.205214,
    0.222138,
    0.153628
  ],
  "revenue_growth": [
    0.1502,
    0.228288,
    0.017363,
    0.029538,
    0.151137
  ],
  "net_income_growth": [
    1.699843,
    0.074288,
    0.000233,
    0.085518,
    -0.149644
  ],
  "eps_growth": [
    1.727273,
    0.266667,
    -0.152632,
    0.086957,
    -0.148571
  ],
  "bvps_growth": [
    0.080988,
    -0.083754,
    0.046326,
    0.067439,
    0.011189
  ],
  "fcf_growth": [
    0.930661,
    0.34591,
    -0.355264,
    0.215122,
    -0.508807
  ],
  "fixed_asset_turnover": [
    12.112618,
    17.282222,
    18.734045,
    18.593376,
    21.816837
  ],
  "receivables_turnover": [
    null,
    null,
    null,
    0.479058,
    0.549693
  ],
  "dso": [
    0.0,
    0.0,
    0.0,
    761.912557,
    664.00688
  ],
  "dio": [
    0.0,
    0.0,
    0.0,
    0.0,
    null
  ],
  "cash_conversion_cycle": [
    -1639.456807,
    -2429.679078,
    0.0,
    -1844.771456,
    null
  ],
  "days_payables_outstanding": [
    1639.456807,
    2429.679078,
    0.0,
    2606.684013,
    null
  ],
  "operating_cycle": [
    0.0,
    0.0,
    0.0,
    761.912557,
    null
  ],
  "roe": [
    0.104521,
    0.178552,
    0.117149,
    0.118895,
    0.100213
  ],
  "roa": [
    0.027841,
    0.028453,
    0.03045,
    0.032294,
    0.027441
  ],
  "roic": [
    0.039492,
    0.051693,
    0.041887,
    0.057325,
    0.046126
  ],
  "rota": [
    0.028661,
    0.029161,
    0.031403,
    0.033284,
    0.028364
  ],
  "roce": [
    0.037168,
    0.047229,
    0.038628,
    0.041704,
    0.033644
  ],
  "opcf_to_sales": [
    0.255112,
    0.285346,
    0.18374,
    0.214309,
    0.103297
  ],
  "fcf_per_share": [
    2.078691,
    2.797435,
    1.803761,
    2.187408,
    1.076896
  ],
  "fcf_to_sales": [
    0.241718,
    0.264865,
    0.167854,
    0.198111,
    0.084534
  ],
  "fcf_to_net_income": [
    1.384131,
    1.734092,
    1.117771,
    1.251227,
    0.722748
  ],
  "fcf_to_opcf": [
    0.947498,
    0.928225,
    0.913538,
    0.924417,
    0.818359
  ],
  "income_quality": [
    1.460827,
    1.868181,
    1.223563,
    1.35353,
    0.883167
  ],
  "net_debt_to_ebitda": [
    -1.992248,
    2.984365,
    -5.458895,
    -1.856604,
    -2.368692
  ],
  "interest_coverage": [
    13.567398,
    11.067016,
    9.590505,
    8.52564,
    7.129912
  ],
  "gearing_ratio": [
    -0.319133,
    0.881938,
    -1.073193,
    -0.377736,
    -0.391708
  ],
  "debt_to_assets": [
    0.347583,
    0.206072,
    0.08352,
    0.295762,
    0.289634
  ],
  "financial_autonomy": [
    0.26637,
    0.159353,
    0.259922,
    0.271615,
    0.27383
  ],
  "debt_to_equity": [
    1.304887,
    1.293176,
    0.321329,
    1.088899,
    1.057713
  ],
  "solvency_ratio": [
    1.363085,
    1.302418,
    1.351209,
    1.372901,
    1.377088
  ],
  "retention_rate": [
    0.633736,
    0.06827,
    0.056328,
    0.255666,
    0.058288
  ],
  "buyback_yield": [
    0.007972,
    0.001896,
    0.00253,
    0.000301,
    0.002461
  ],
  "capex_to_depreciation": [
    0.569297,
    0.791929,
    0.625565,
    0.712004,
    1.052114
  ],
  "roic_vs_wacc": [
    -0.004008,
    0.008793,
    -0.000913,
    0.010825,
    -0.001374
  ],
  "reinvestment_rate": [
    0.052502,
    0.071775,
    0.086462,
    0.075583,
    0.181641
  ],
  "capex_to_revenue": [
    0.013394,
    0.020481,
    0.015887,
    0.016198,
    0.018763
  ],
  "debt_service_coverage": [
    15.223635,
    15.172876,
    8.586974,
    1.31088,
    4.794068
  ],
  "equity_service_ratio": [
    6.86615,
    1.231848,
    -0.117593,
    2.847114,
    0.746915
  ],
  "total_yield": [
    0.051871,
    0.125707,
    0.131146,
    0.090791,
    0.091834
  ],
  "dividend_yield": [
    0.043899,
    0.123811,
    0.128616,
    0.09049,
    0.089373
  ],
  "payout_ratio": [
    0.366264,
    0.93173,
    0.943672,
    0.744334,
    0.941712
  ],
  "wacc": [
    0.0435,
    0.0429,
    0.0428,
    0.0465,
    0.0475
  ],
  "nopat": [
    223876000.0,
    240716000.0,
    240624000.0,
    261038000.0,
    222644000.0
  ],
  "invested_capital": [
    8004924000.0,
    10621443000.0,
    7898282000.0,
    6460166000.0,
    6454232000.0
  ],
  "eva_absolute": [
    -5842865.9,
    22283815.06,
    137258.97,
    14791137.99,
    -1851212.38
  ],
  "eva_spread": [
    -0.004008,
    0.008793,
    -0.000913,
    0.010825,
    -0.001374
  ],
  "sustainable_growth_rate": [
    0.002073,
    0.00371,
    0.003622,
    0.004333,
    0.008378
  ],
  "liquidity_score": [
    100.0,
    100.0,
    100.0,
    100.0,
    100.0
  ],
  "profitability_score": [
    88.86,
    90.28,
    100.0,
    92.73,
    93.69
  ],
  "growth_score": [
    69.01,
    80.72,
    86.57,
    78.75,
    82.22
  ],
  "efficiency_score": [
    100.0,
    100.0,
    100.0,
    68.01,
    66.25
  ],
  "rentability_score": [
    48.06,
    54.78,
    54.51,
    60.34,
    64.63
  ],
  "cashflow_score": [
    99.05,
    99.11,
    99.12,
    99.08,
    99.07
  ],
  "solvency_score": [
    89.3,
    88.87,
    89.25,
    89.36,
    90.91
  ],
  "capalloc_score": [
    88.02,
    87.18,
    84.68,
    85.1,
    79.45
  ],
  "value_creation_score": [
    26.66,
    39.17,
    38.23,
    40.43,
    42.98
  ],
  "fscore": [
    72.63,
    77.29,
    78.95,
    77.1,
    78.39
  ],
  "diag_liquidity_ok": [
    null,
    null,
    null,
    null,
    1
  ],
  "diag_liquidity_txt": [
    null,
    null,
    null,
    null,
    "Oui, très largement — la trésorerie et les actifs courts couvrent les dettes immédiates avec une marge de sécurité élevée."
  ],
  "diag_profitability_ok": [
    null,
    null,
    null,
    null,
    1
  ],
  "diag_profitability_txt": [
    null,
    null,
    null,
    null,
    "Oui, nettement — l'entreprise convertit une large part de son chiffre d'affaires en profit, avec des marges solides sur toute la chaîne."
  ],
  "diag_growth_ok": [
    null,
    null,
    null,
    null,
    1
  ],
  "diag_growth_txt": [
    null,
    null,
    null,
    null,
    "Oui — l'activité se développe de façon saine, avec une dynamique positive sur les principaux vecteurs de création de valeur."
  ],
  "diag_efficiency_ok": [
    null,
    null,
    null,
    null,
    1
  ],
  "diag_efficiency_txt": [
    null,
    null,
    null,
    null,
    "Oui — les actifs opérationnels sont bien utilisés et le cycle d'exploitation génère du cash efficacement."
  ],
  "diag_rentability_ok": [
    null,
    null,
    null,
    null,
    null
  ],
  "diag_rentability_txt": [
    null,
    null,
    null,
    null,
    "À peine — le rendement du capital couvre tout juste son coût, sans création de valeur nette significative pour l'actionnaire."
  ],
  "diag_cashflow_ok": [
    null,
    null,
    null,
    null,
    1
  ],
  "diag_cashflow_txt": [
    null,
    null,
    null,
    null,
    "Oui, très bien — le bénéfice se traduit intégralement en trésorerie disponible. L'entreprise est totalement autonome financièrement."
  ],
  "diag_solvency_ok": [
    null,
    null,
    null,
    null,
    1
  ],
  "diag_solvency_txt": [
    null,
    null,
    null,
    null,
    "Oui, sans réserve — le bilan est solide, la dette très faible et les intérêts largement couverts. La flexibilité stratégique est maximale."
  ],
  "diag_capalloc_ok": [
    null,
    null,
    null,
    null,
    1
  ],
  "diag_capalloc_txt": [
    null,
    null,
    null,
    null,
    "Oui — l'équilibre entre réinvestissement, remboursement de dette et retour aux actionnaires est discipliné et cohérent."
  ],
  "diag_valuecreation_ok": [
    null,
    null,
    null,
    null,
    null
  ],
  "diag_valuecreation_txt": [
    null,
    null,
    null,
    null,
    "Marginalement — la création de valeur est positive mais fragile, insuffisante pour compenser pleinement le risque pris par les investisseurs."
  ]
};

// ── ANALYSES MÉTRIQUES DEPUIS SP (Solvency_Scores.comment, etc.) ─────────────
// Source : sp_Analysis_Solvency · sp_Analysis_Liquidity · etc.
// Clé : pillar.key → ratio.col → texte SQL
// En production : injecter depuis la query (JOIN Solvency_Scores ss ON ss.symbol = ? AND ss.ratio_name = ?)
// Pour l'instant : valeurs précalculées depuis les SP sur COFA.PA · FY 2025
// ── DEMO_DB_ANALYSE — commentaires SQL réels (dernière année : 2025) ──
const DEMO_DB_ANALYSE = {
  "solvency": {
    "net_debt_to_ebitda": "Un endettement net très faible (-1.74x EBITDA) : l'entreprise présente une excellente solvabilité, avec une capacité quasi-immédiate à rembourser sa dette. Cela lui confère une flexibilité stratégique pour investir, résister à un choc conjoncturel ou améliorer la rémunération des actionnaires.",
    "financial_autonomy": "Une autonomie financière faible (24.6%) : l'entreprise dépend largement de l'endettement, ce qui fragilise sa solvabilité et accroît son exposition aux variations de taux et aux pressions des créanciers.",
    "debt_to_assets": "Un ratio Dette/Actifs faible (24.5%) : l'entreprise dispose d'une structure bilancielle solide, avec une marge de manœuvre financière importante pour investir ou résister à un ralentissement.",
    "gearing_ratio": "Un gearing faible et sain (-25.6%) : l'entreprise conserve une solide capacité de financement par fonds propres, limitant son exposition au risque de solvabilité.",
    "solvency_ratio": "Une solvabilité élevée (135.3%) : l'entreprise dispose d'une couverture solide de ses dettes par ses capitaux propres, renforçant sa résilience et sa capacité à traverser des cycles défavorables.",
    "interest_coverage": "Une couverture des intérêts très confortable (9.98x) : l'entreprise dégage une marge de sécurité importante, lui permettant de supporter ses charges financières même en cas de baisse significative de sa rentabilité."
  },
  "liquidity": {
    "current_ratio": "Current Ratio confortable (20.15x ≥ 1.50) : l'entreprise dispose d'un matelas de liquidité solide, ses actifs courants couvrant largement les dettes de court terme et réduisant le risque de tension de trésorerie.",
    "quick_ratio": "Quick Ratio élevé (20.15x ≥ 1.00) : la liquidité immédiate (hors stocks) est suffisante pour couvrir les dettes de court terme, renforçant la sécurité financière.",
    "cash_ratio": "Cash Ratio élevé (1287.69% ≥ 100%) : l'entreprise dispose d'une réserve de trésorerie suffisante pour couvrir intégralement ses dettes de court terme, garantissant une sécurité financière immédiate et une forte autonomie."
  },
  "profitability": {
    "gross_margin": "Marge brute très élevée (88.8% ≥ 50%) : l'entreprise bénéficie d'un fort pricing power et d'un mix produit favorable, traduisant une compétitivité renforcée et une capacité à générer des profits durables.",
    "ebitda_margin": "Marge EBITDA solide (24.7% ≥ 15%) : la structure de coûts est bien optimisée, traduisant une performance opérationnelle saine et compétitive.",
    "operating_margin": "Marge opérationnelle élevée (20.3% ≥ 20%) : l'entreprise démontre une excellente efficacité d'exploitation, traduisant une forte compétitivité et une maîtrise rigoureuse de ses coûts.",
    "net_margin": "Marge nette élevée (15.1% ≥ 15%) : l'entreprise démontre une forte qualité de ses profits et une excellente conversion du chiffre d'affaires en bénéfices, traduisant une création de valeur durable pour l'actionnaire.",
    "pretax_margin": "Marge avant impôts élevée (20.3% ≥ 15%) : l'entreprise dégage une forte profitabilité avant charges fiscales, renforçant sa solidité financière et sa capacité à absorber les variations d'imposition."
  },
  "growth": {
    "revenue_growth": "Croissance du chiffre d'affaires correcte (11.5% ≥ 5%) : l'entreprise progresse de manière saine, mais sans atteindre une dynamique de forte expansion.",
    "net_income_growth": "Bénéfices en forte progression (34.2% ≥ 15%) : l'entreprise renforce significativement sa profitabilité, soutenant sa capacité d'autofinancement et de création de valeur.",
    "eps_growth": "EPS en forte accélération (35.6% ≥ 15%) : l'entreprise accroît significativement le bénéfice par action, renforçant la création de valeur pour l'actionnaire et soutenant l'attractivité boursière.",
    "bvps_growth": "BVPS quasi-stable (2.4% ≥ 0%) : la progression reste limitée, en deçà du seuil cible de 5%, ce qui reflète une accumulation patrimoniale modeste."
  },
  "efficiency": {
    "fixed_asset_turnover": "Très bonne utilisation des immobilisations (17.71x) : l'entreprise génère un niveau élevé de ventes par rapport à ses actifs fixes.",
    "receivables_turnover": "Encaissements clients lents (0.51x) : BFR mobilisé.",
    "dso": "DSO très long (285 j) : pression sur la trésorerie.",
    "dio": "DIO court (0 j) : faible capital immobilisé.",
    "cash_conversion_cycle": "CCC court (-1583 j) : cycle de trésorerie très bien maîtrisé."
  },
  "rentability": {
    "roe": "ROE solide (12.4%) : l'entreprise dégage une rentabilité satisfaisante de ses fonds propres, soutenant sa capacité d'autofinancement et de développement.",
    "roa": "ROA faible (2.9% > 0) : l'entreprise reste rentable mais la sous-utilisation des actifs ou des marges réduites limitent sa création de valeur.",
    "roic": "ROIC faible (4.7% > 0) : l'entreprise dégage un rendement limité, à peine supérieur à zéro, ce qui traduit une création de valeur très faible et un besoin d'amélioration de l'efficacité des investissements.",
    "rota": "Rendement des actifs tangibles faible (3% > 0) : les actifs physiques sont sous-utilisés, limitant la rentabilité et la contribution de l'appareil productif à la création de valeur.",
    "opcf_to_sales": "Un autofinancement rapporté au chiffre d'affaires élevé (17.2% ≥ 10%) : l'entreprise convertit efficacement ses profits en liquidités, renforçant sa capacité à financer sa croissance et ses investissements par ses propres ressources."
  },
  "cashflow": {
    "fcf_per_share": "FCF positif par action (1.99) : l'entreprise génère du cash libre après investissements, créant de la valeur par action et renforçant sa capacité de distribution ou de réinvestissement.",
    "opcf_to_sales": "Conversion élevée du CA en OCF (20.8%) : l'entreprise transforme très efficacement ses ventes en cash, avec une forte génération opérationnelle et une grande autonomie financière.",
    "fcf_to_opcf": "Part élevée du FCF dans l'OCF (90.6%) : capex disciplinés et forte conversion en cash libre ; bonne flexibilité pour financer croissance, dividendes et désendettement."
  },
  "capalloc": {
    "retention_rate": "Rétention correcte (21.4%) : une part satisfaisante des profits est réinvestie, soutenant un développement équilibré.",
    "reinvestment_rate": "Taux de réinvestissement limité (0.1%) : peu de ressources sont consacrées à la croissance future, ce qui peut freiner le développement à long terme."
  },
  "value_creation": {
    "wacc": "Coût moyen pondéré du capital sur 5 ans.",
    "roic_vs_wacc": "Spread moyen entre ROIC et WACC : positif = création de valeur.",
    "sustainable_growth_rate": "Croissance soutenable moyenne sur 5 ans = ROIC × taux de réinvestissement."
  }
};
// ── DEMO_FUND_META — COFA.PA, 5 ans (champs longs narrative/tooltips non repris ici,
// cf. note de réponse : seuls les champs courts ont été extraits de façon fiable) ──
const DEMO_FUND_META = {
  "label": {
    "2021": "Satisfaisant",
    "2022": "Solide",
    "2023": "Solide",
    "2024": "Solide",
    "2025": "Solide"
  },
  "trend": {
    "2021": "IMPROVING",
    "2022": "STABLE",
    "2023": "STABLE",
    "2024": "STABLE",
    "2025": "STABLE"
  },
  "signal": {
    "2021": "BUY",
    "2022": "BUY",
    "2023": "BUY",
    "2024": "BUY",
    "2025": "BUY"
  },
  "conviction": {
    "2021": "MEDIUM",
    "2022": "MEDIUM",
    "2023": "MEDIUM",
    "2024": "MEDIUM",
    "2025": "MEDIUM"
  },
  "bias": {
    "2021": "CASH_GENERATOR",
    "2022": "CASH_GENERATOR",
    "2023": "CASH_GENERATOR",
    "2024": "CASH_GENERATOR",
    "2025": "CASH_GENERATOR"
  },
  "risk_flag": {
    "2021": "NONE",
    "2022": "NONE",
    "2023": "NONE",
    "2024": "NONE",
    "2025": "NONE"
  },
  "summary_short": {
    "2021": "Profil satisfaisant avec score 72.63/100 — pilier dominant : efficacité",
    "2022": "Profil solide avec score 77.29/100 — pilier dominant : efficacité",
    "2023": "Profil solide avec score 78.95/100 — pilier dominant : efficacité",
    "2024": "Profil solide avec score 77.10/100 — pilier dominant : cash-flow",
    "2025": "Profil solide avec score 78.39/100 — pilier dominant : cash-flow"
  },
  "interpretation": {
    "2021": "Satisfaisant",
    "2022": "Solide",
    "2023": "Solide",
    "2024": "Solide",
    "2025": "Solide"
  },
  "outlook": {
    "2021": "Croissance modérée, solide génération de cash, bilan robuste.",
    "2022": "Croissance solide, solide génération de cash, bilan robuste.",
    "2023": "Croissance solide, solide génération de cash, bilan robuste.",
    "2024": "Croissance modérée, solide génération de cash, bilan robuste.",
    "2025": "Croissance solide, solide génération de cash, bilan robuste."
  },
  "key_driver": {
    "2021": "efficiency",
    "2022": "efficiency",
    "2023": "efficiency",
    "2024": "cashflow",
    "2025": "cashflow"
  },
  "pillar_trends": {
    "liquidity": {
      "2021": "→",
      "2022": "→",
      "2023": "→",
      "2024": "→",
      "2025": "→"
    },
    "profitability": {
      "2021": "↑",
      "2022": "→",
      "2023": "↑",
      "2024": "↓",
      "2025": "→"
    },
    "growth": {
      "2021": "↑",
      "2022": "↑",
      "2023": "↑",
      "2024": "↓",
      "2025": "↑"
    },
    "efficiency": {
      "2021": "→",
      "2022": "→",
      "2023": "→",
      "2024": "↓",
      "2025": "→"
    },
    "rentability": {
      "2021": "↑",
      "2022": "↑",
      "2023": "→",
      "2024": "↑",
      "2025": "↑"
    },
    "cashflow": {
      "2021": "→",
      "2022": "→",
      "2023": "→",
      "2024": "→",
      "2025": "→"
    },
    "solvency": {
      "2021": "→",
      "2022": "→",
      "2023": "→",
      "2024": "→",
      "2025": "→"
    },
    "capalloc": {
      "2021": "→",
      "2022": "→",
      "2023": "→",
      "2024": "→",
      "2025": "↓"
    },
    "value_creation": {
      "2021": "↓",
      "2022": "↑",
      "2023": "→",
      "2024": "→",
      "2025": "→"
    }
  },
  "pillar_labels": {
    "liquidity": {
      "2021": "Excellent",
      "2022": "Excellent",
      "2023": "Excellent",
      "2024": "Excellent",
      "2025": "Excellent"
    },
    "profitability": {
      "2021": "Solide",
      "2022": "Excellent",
      "2023": "Excellent",
      "2024": "Excellent",
      "2025": "Excellent"
    },
    "growth": {
      "2021": "Satisfaisant",
      "2022": "Solide",
      "2023": "Solide",
      "2024": "Solide",
      "2025": "Solide"
    },
    "efficiency": {
      "2021": "Excellent",
      "2022": "Excellent",
      "2023": "Excellent",
      "2024": "Satisfaisant",
      "2025": "Satisfaisant"
    },
    "rentability": {
      "2021": "Fragile",
      "2022": "Fragile",
      "2023": "Fragile",
      "2024": "Satisfaisant",
      "2025": "Satisfaisant"
    },
    "cashflow": {
      "2021": "Excellent",
      "2022": "Excellent",
      "2023": "Excellent",
      "2024": "Excellent",
      "2025": "Excellent"
    },
    "solvency": {
      "2021": "Solide",
      "2022": "Solide",
      "2023": "Solide",
      "2024": "Solide",
      "2025": "Excellent"
    },
    "capalloc": {
      "2021": "Solide",
      "2022": "Solide",
      "2023": "Solide",
      "2024": "Solide",
      "2025": "Solide"
    },
    "value_creation": {
      "2021": "Insuffisante",
      "2022": "Insuffisante",
      "2023": "Insuffisante",
      "2024": "Fragile",
      "2025": "Fragile"
    }
  }
};

// ── FSCORE SUMMARY par année ─────────────────────────────────────────────────
const DEMO_FSCORE_SUMMARY = {
  "2021": "Synthèse : profil fondamental satisfaisant (voir DB pour le détail complet).",
  "2022": "Synthèse : profil fondamental solide (voir DB pour le détail complet).",
  "2023": "Synthèse : profil fondamental solide (voir DB pour le détail complet).",
  "2024": "Synthèse : profil fondamental solide (voir DB pour le détail complet).",
  "2025": "Synthèse : profil fondamental solide (voir DB pour le détail complet)."
};

// ── COMPANY META (profil société — issu de StockDashboard) ──────────────────
// ── DEMO_COMPANY_META — COFA.PA (Coface SA), données réelles issues de REQ1 ──
const DEMO_COMPANY_META = {
  "ticker": "COFA.PA",
  "fullName": "COFACE SA",
  "exchange": "Euronext Paris",
  "exchangeCode": "EPA",
  "sector": "Financials",
  "industry": "Reinsurance",
  "region": "Europe",
  "country": "FR",
  "currency": "EUR",
  "price": 14.61,
  "mtdPct": -5.0794,
  "ytdPct": -4.7771,
  "marketCap": "Mid Cap",
  "style": "Income",
  "cyclicals": "Fast Growers",
  "moat": "Narrow",
  "beta": 0.48,
  "ceo": "Xavier Pascal Durand",
  "employees": 4960,
  "fiscalYear": 2025,
  "cycleStage": 0,
  "cycleName": "Secteur spécial - Finance",
  "cycleSignal": "À analyser manuellement",
  "cycleComment": "Regarder combined ratio",
  "iscore": 60.04,
  "radarData": [
    {
      "axis": "Fondamentaux",
      "value": 78.39
    },
    {
      "axis": "Valorisation",
      "value": 76.0
    },
    {
      "axis": "Marché",
      "value": 56.26
    },
    {
      "axis": "Momentum",
      "value": 14.95
    },
    {
      "axis": "Risque/Rend.",
      "value": 70.45
    }
  ],
  "description": "COFACE SA, through its subsidiaries, provides credit insurance products and related services for microenterprises, small and medium enterprises, mid-market companies, international corporations, financial institutions, and clients of distribution partners. The company offers credit insurance products to protect companies against default on payment of its trade receivables. It also provides integrated credit management solutions comprising credit insurance, single risk insurance, business information and debt collection services for insured and uninsured businesses; and factoring services, as well as contract and environmental surety, customs and excise, and legal bonds; and payment guarantees. In addition, the company offers business information services through its ICON portal. It operates in Western Europe, Northern Europe, Central and Eastern Europe, the Mediterranean and Africa, North America, Latin America, and the Asia-Pacific. The company was founded in 1946 and is headquartered in Bois-Colombes, France."
};

// Format: {forces:[], vigilances:[]} extraits depuis chaque *_insight par année
// ── DEMO_PILLAR_INSIGHTS — non extraits pour COFA.PA (textes trop longs/risqués à
// transcrire à la main sans erreur) : laissés à null, le front bascule proprement.
const DEMO_PILLAR_INSIGHTS = {
  "liquidity": {
    "2021": null,
    "2022": null,
    "2023": null,
    "2024": null,
    "2025": null
  },
  "profitability": {
    "2021": null,
    "2022": null,
    "2023": null,
    "2024": null,
    "2025": null
  },
  "growth": {
    "2021": null,
    "2022": null,
    "2023": null,
    "2024": null,
    "2025": null
  },
  "efficiency": {
    "2021": null,
    "2022": null,
    "2023": null,
    "2024": null,
    "2025": null
  },
  "rentability": {
    "2021": null,
    "2022": null,
    "2023": null,
    "2024": null,
    "2025": null
  },
  "cashflow": {
    "2021": null,
    "2022": null,
    "2023": null,
    "2024": null,
    "2025": null
  },
  "solvency": {
    "2021": null,
    "2022": null,
    "2023": null,
    "2024": null,
    "2025": null
  },
  "capalloc": {
    "2021": null,
    "2022": null,
    "2023": null,
    "2024": null,
    "2025": null
  },
  "value_creation": {
    "2021": null,
    "2022": null,
    "2023": null,
    "2024": null,
    "2025": null
  }
};

// ══════════════════════════════════════════════════════════════════════════
// ── ÉTAT LIVE (mutable, hydraté depuis l'API) ──────────────────────────────
// Tout le composant (fonctions module-scope + composants React ci-dessous)
// lit ces variables au moment du rendu, pas au moment du chargement du module.
// Tant que hydrateLiveData() n'a pas été appelée, elles pointent vers les
// données DEMO (COFA.PA) → aperçu fonctionnel immédiat, jamais d'écran
// vide ni de crash sur accès non protégé (RAW.fscore[YN] etc.).
// ══════════════════════════════════════════════════════════════════════════
let YEARS          = DEMO_YEARS;
let YN              = DEMO_YN;
let RAW             = DEMO_RAW;
let DB_ANALYSE      = DEMO_DB_ANALYSE;
let FUND_META       = DEMO_FUND_META;
let FSCORE_SUMMARY  = DEMO_FSCORE_SUMMARY;
let COMPANY_META    = DEMO_COMPANY_META;
let PILLAR_INSIGHTS = DEMO_PILLAR_INSIGHTS;

/** Devise courante — dérivée dynamiquement de COMPANY_META (plus de calcul figé au chargement du module) */
function getCur() { return COMPANY_META?.currency || "USD"; }
function getCurSym() { const c = getCur(); return c === "EUR" ? "€" : c === "USD" ? "$" : c; }

// ── CLÉS RAW — doivent correspondre exactement aux alias de colonnes de REQ2 ─
// (query_AnalyseFondamentaleCard_mapping.md · PARTIE 2 & 3 & 4)
const RAW_KEYS = [
  // Liquidité
  'current_ratio','quick_ratio','cash_ratio',
  // Profitabilité
  'gross_margin','ebitda_margin','operating_margin','net_margin','pretax_margin',
  // Croissance
  'revenue_growth','net_income_growth','eps_growth','bvps_growth','fcf_growth',
  // Efficacité
  'fixed_asset_turnover','receivables_turnover','dso','dio',
  'cash_conversion_cycle','days_payables_outstanding','operating_cycle',
  // Rentabilité
  'roe','roa','roic','rota','roce','opcf_to_sales',
  // Cash Flow
  'fcf_per_share','fcf_to_sales','fcf_to_net_income','fcf_to_opcf','income_quality',
  // Solvabilité
  'net_debt_to_ebitda','interest_coverage','gearing_ratio','debt_to_assets',
  'financial_autonomy','debt_to_equity','solvency_ratio',
  // Alloc. Capital
  'retention_rate','buyback_yield','capex_to_depreciation','roic_vs_wacc',
  'reinvestment_rate','capex_to_revenue','debt_service_coverage','equity_service_ratio',
  'total_yield','dividend_yield','payout_ratio',
  // Création Valeur
  'wacc','nopat','invested_capital','eva_absolute','eva_spread','sustainable_growth_rate',
  // Scores piliers (Fundamental_Scores)
  'liquidity_score','profitability_score','growth_score','efficiency_score',
  'rentability_score','cashflow_score','solvency_score','capalloc_score',
  'value_creation_score','fscore',
  // Grille diagnostique (Fundamental_Scores · SP v2)
  'diag_liquidity_ok','diag_liquidity_txt',
  'diag_profitability_ok','diag_profitability_txt',
  'diag_growth_ok','diag_growth_txt',
  'diag_efficiency_ok','diag_efficiency_txt',
  'diag_rentability_ok','diag_rentability_txt',
  'diag_cashflow_ok','diag_cashflow_txt',
  'diag_solvency_ok','diag_solvency_txt',
  'diag_capalloc_ok','diag_capalloc_txt',
  'diag_valuecreation_ok','diag_valuecreation_txt',
];

const FUND_META_PILLARS = ['liquidity','profitability','growth','efficiency','rentability','cashflow','solvency','capalloc','value_creation'];

const INSIGHT_COL = {
  liquidity:'Liquidity_insight', profitability:'Profitability_insight', growth:'Growth_insight',
  efficiency:'Efficiency_insight', rentability:'Rentability_insight', cashflow:'CashFlow_insight',
  solvency:'Solvency_insight', capalloc:'CapitalAllocation_insight', value_creation:'ValueCreation_insight',
};

// ── ADAPTATEURS — REQ1 (header, 1 ligne) + REQ2 (série 5 ans) → VM frontend ─
// Chaque fonction correspond à une PARTIE du fichier
// query_AnalyseFondamentaleCard_mapping.md.

/** PARTIE 1 — COMPANY_META depuis REQ1 (StockDashboard × StockDashboard_Decisions) */
function buildCompanyMeta(h) {
  if (!h) return DEMO_COMPANY_META;
  return {
    ticker:       h.symbol,
    fullName:     h.company_name,
    exchange:     h.exchange,
    exchangeCode: h.exchange_code,
    sector:       h.sector,
    industry:     h.industry,
    region:       h.region,
    country:      h.country,
    currency:     h.currency,
    price:        h.price,
    mtdPct:       h.mtd_pct,
    ytdPct:       h.ytd_pct,
    marketCap:    h.market_cap_class,
    style:        h.stock_style,
    cyclicals:    h.peter_lynch_category,
    moat:         h.moat,
    beta:         h.beta,
    ceo:          h.ceo,
    employees:    h.employees,
    fiscalYear:   h.calendarYear,
    cycleStage:   h.cycle_stage,
    cycleName:    h.cycle_name,
    cycleSignal:  h.cycle_signal,
    cycleComment: h.cycle_comment,
    description:  h.description,
    iscore:       h.iscore,
    radarData: [
      { axis:"Fondamentaux", value: h.radar_fondamentaux },
      { axis:"Valorisation", value: h.radar_valorisation },
      { axis:"Marché",       value: h.radar_marche },
      { axis:"Momentum",     value: h.radar_momentum },
      { axis:"Risque/Rend.", value: h.radar_risque_rendement },
    ],
  };
}

/** Années disponibles (triées ASC) depuis les lignes REQ2 */
function buildYears(rows) {
  return [...rows].map(r => r.calendarYear).sort((a, b) => a - b);
}

/** PARTIE 2, 3, 4 — RAW (ratios bruts + scores piliers + grille diagnostique) depuis REQ2 */
function buildRaw(rows) {
  const sorted = [...rows].sort((a, b) => a.calendarYear - b.calendarYear);
  const out = {};
  RAW_KEYS.forEach(k => { out[k] = sorted.map(r => (r[k] === undefined ? null : r[k])); });
  return out;
}

/** PARTIE 5 — FUND_META (méta par année) depuis REQ2 (Analysis_Fundamentals × Fundamental_Scores) */
function buildFundMeta(rows) {
  const sorted = [...rows].sort((a, b) => a.calendarYear - b.calendarYear);
  const byYear = pick => Object.fromEntries(sorted.map(r => [r.calendarYear, pick(r) ?? null]));
  const fm = {
    label:          byYear(r => r.fund_interpretation),
    trend:          byYear(r => r.fund_trend),
    signal:         byYear(r => r.fund_signal),
    conviction:     byYear(r => r.fund_conviction),
    bias:           byYear(r => r.fund_bias),
    risk_flag:      byYear(r => r.fundamental_risk_flag),
    summary_short:  byYear(r => r.fund_summary_short),
    interpretation: byYear(r => r.fund_interpretation),
    strengths:        byYear(r => r.fund_strengths),
    weaknesses:       byYear(r => r.fund_weaknesses),
    fund_strengths:   byYear(r => r.fund_strengths),
    fund_weaknesses:  byYear(r => r.fund_weaknesses),
    narrative:      byYear(r => r.fund_text_narrative),
    outlook:        byYear(r => r.fundamental_outlook),
    key_driver:     byYear(r => r.fund_key_driver),
    pillar_trends: {},
    pillar_labels: {},
  };
  FUND_META_PILLARS.forEach(p => {
    fm[`fund_${p}_tooltip`] = byYear(r => r[`fund_${p}_tooltip`]);
    fm.pillar_trends[p] = byYear(r => r[`fund_${p}_trend`]);
    fm.pillar_labels[p] = byYear(r => r[`fund_${p}_label`]);
  });
  return fm;
}

/** PARTIE 6 — FSCORE_SUMMARY depuis REQ2 (Fundamental_Scores.FScore_summary) */
function buildFscoreSummary(rows) {
  const out = {};
  rows.forEach(r => { out[r.calendarYear] = r.FScore_summary ?? null; });
  return out;
}

/** PARTIE 7 — PILLAR_INSIGHTS depuis REQ2 (Fundamental_Scores.*_insight) */
function buildPillarInsights(rows) {
  const out = {};
  Object.entries(INSIGHT_COL).forEach(([pillar, col]) => {
    out[pillar] = Object.fromEntries(rows.map(r => [r.calendarYear, r[col] ?? null]));
  });
  return out;
}

/**
 * PARTIE 8 — DB_ANALYSE depuis REQ2 (comment SQL des SP par pilier).
 * Câblé pour les 9 piliers (cf. query_AnalyseFondamentaleCard_mapping-3.md · PARTIE 8),
 * à partir des LEFT JOIN Solvency_Scores / Liquidity_Scores / Profitability_Scores /
 * Growth_Scores / Efficiency_Scores / Rentability_Scores / Cashflow_Scores /
 * Capital_Allocation_Scores / Value_Creation_Scores.
 *
 * Couverture réelle (ratios affichés dans PILLARS vs commentaire SQL disponible) :
 *   Liquidité 3/3 · Profitabilité 5/5 · Efficacité 5/5 · Rentabilité 5/5 · Solvabilité 5/5
 *   Croissance 4/5 (fcf_growth non scoré par la SP)
 *   Cash Flow 3/5 (fcf_to_sales, income_quality non scorés)
 *   Alloc. Capital 2/5 directs + roic_vs_wacc en cross-table depuis Value_Creation_Scores
 *     (buyback_yield, capex_to_depreciation non scorés)
 *   Création Valeur 3/4 (roce non scoré)
 * Dans tous les cas non couverts, le front bascule déjà sur buildEconomicImpact()
 * (cf. ligne ~2952) — aucun risque de crash/texte manquant.
 *
 * ⚠️ Value_Creation_Scores (avec underscore) est la table canonique — celle qui
 * alimente réellement Fundamental_Scores.ValueCreation_Score/_insight. Ne pas
 * confondre avec ValueCreation_Scores (sans underscore), table legacy non utilisée
 * par le pipeline FScore.
 */
function buildDbAnalyse(rows) {
  const last = [...rows].sort((a, b) => b.calendarYear - a.calendarYear)[0] || {};
  return {
    solvency: {
      net_debt_to_ebitda: last.solv_comment_net_debt_to_ebitda ?? null,
      financial_autonomy: last.solv_comment_financial_autonomy ?? null,
      debt_to_assets:     last.solv_comment_debt_to_assets ?? null,
      gearing_ratio:      last.solv_comment_gearing_ratio ?? null,
      solvency_ratio:     last.solv_comment_solvency_ratio ?? null,
      interest_coverage:  last.solv_comment_interest_coverage ?? null,
    },
    liquidity: {
      current_ratio: last.liq_comment_current_ratio ?? null,
      quick_ratio:   last.liq_comment_quick_ratio ?? null,
      cash_ratio:    last.liq_comment_cash_ratio ?? null,
    },
    profitability: {
      gross_margin:     last.prof_comment_gross_margin ?? null,
      ebitda_margin:    last.prof_comment_ebitda_margin ?? null,
      operating_margin: last.prof_comment_operating_margin ?? null,
      net_margin:       last.prof_comment_net_margin ?? null,
      pretax_margin:    last.prof_comment_pretax_margin ?? null,
    },
    growth: {
      revenue_growth:    last.gro_comment_revenue_growth ?? null,
      net_income_growth: last.gro_comment_net_income_growth ?? null,
      eps_growth:        last.gro_comment_eps_growth ?? null,
      bvps_growth:       last.gro_comment_bvps_growth ?? null,
      // fcf_growth : pas de source SQL (Growth_Scores ne score pas ce ratio) → fallback front
    },
    efficiency: {
      fixed_asset_turnover:  last.eff_comment_fixed_asset_turnover ?? null,
      receivables_turnover:  last.eff_comment_receivables_turnover ?? null,
      dso:                   last.eff_comment_dso ?? null,
      dio:                   last.eff_comment_dio ?? null,
      cash_conversion_cycle: last.eff_comment_cash_conversion_cycle ?? null,
    },
    rentability: {
      roe:           last.rent_comment_roe ?? null,
      roa:           last.rent_comment_roa ?? null,
      roic:          last.rent_comment_roic ?? null,
      rota:          last.rent_comment_rota ?? null,
      opcf_to_sales: last.rent_comment_opcf_to_sales ?? null,
    },
    cashflow: {
      fcf_per_share: last.cf_comment_fcf_per_share ?? null,
      opcf_to_sales: last.cf_comment_opcf_to_sales ?? null,
      fcf_to_opcf:   last.cf_comment_fcf_to_opcf ?? null,
      // fcf_to_sales, income_quality : pas de source SQL (Cashflow_Scores ne les score pas) → fallback front
    },
    capalloc: {
      retention_rate:    last.capa_comment_retention_rate ?? null,
      reinvestment_rate: last.capa_comment_reinvestment_rate ?? null,
      // roic_vs_wacc récupéré en cross-table depuis Value_Creation_Scores (même SP source que le pilier Création Valeur)
      roic_vs_wacc:      last.vc_comment_roic_vs_wacc ?? null,
      // buyback_yield, capex_to_depreciation : pas de source SQL (Capital_Allocation_Scores ne les score pas) → fallback front
    },
    value_creation: {
      wacc:                    last.vc_comment_wacc ?? null,
      roic_vs_wacc:            last.vc_comment_roic_vs_wacc ?? null,
      sustainable_growth_rate: last.vc_comment_sustainable_growth_rate ?? null,
      // roce : pas de source SQL (Value_Creation_Scores n'a pas de ratio ROCE dédié) → fallback front
    },
  };
}

/** Bascule l'état live sur les données réelles reçues de l'API (mutation en place). */
function hydrateLiveData(headerRow, seriesRows) {
  const rows = Array.isArray(seriesRows) ? seriesRows : [];
  COMPANY_META    = buildCompanyMeta(headerRow);
  YEARS           = rows.length ? buildYears(rows) : DEMO_YEARS;
  YN              = Math.max(0, YEARS.length - 1);
  RAW             = rows.length ? buildRaw(rows) : DEMO_RAW;
  FUND_META       = rows.length ? buildFundMeta(rows) : DEMO_FUND_META;
  FSCORE_SUMMARY  = rows.length ? buildFscoreSummary(rows) : DEMO_FSCORE_SUMMARY;
  PILLAR_INSIGHTS = rows.length ? buildPillarInsights(rows) : DEMO_PILLAR_INSIGHTS;
  DB_ANALYSE      = rows.length ? buildDbAnalyse(rows) : DEMO_DB_ANALYSE;
}

/** Revient aux données de démonstration (ex. en cas d'échec réseau). */
function resetToDemoData() {
  YEARS = DEMO_YEARS; YN = DEMO_YN;
  RAW = DEMO_RAW; DB_ANALYSE = DEMO_DB_ANALYSE; FUND_META = DEMO_FUND_META;
  FSCORE_SUMMARY = DEMO_FSCORE_SUMMARY; COMPANY_META = DEMO_COMPANY_META;
  PILLAR_INSIGHTS = DEMO_PILLAR_INSIGHTS;
}

// ══════════════════════════════════════════════════════════════════════════
// ── COUCHE API ──────────────────────────────────────────────────────────
// ⚠️ À ADAPTER : ces deux chemins sont des placeholders. Brancher sur les
// routes réelles exposées par le backend Invelps pour REQ1 (header) et REQ2
// (série 5 ans).
//
// ✅ REQ1 et REQ2 ont été validées avec des données réelles (COFA.PA) — les
// noms de colonnes retournés correspondent exactement à ceux attendus par
// buildCompanyMeta() / buildRaw() / buildFundMeta() / buildFscoreSummary() /
// buildPillarInsights() / buildDbAnalyse() ci-dessus. Aucun changement
// d'adaptateur n'a été nécessaire suite à cette validation.
//
// REQ2 a été restructurée par Shixo : au lieu des LEFT JOIN directs avec
// MAX(CASE WHEN ... COLLATE utf8mb4_unicode_ci ...) sur chaque table de
// scores, chaque pilier (Solvency_Scores, Liquidity_Scores,
// Profitability_Scores, Growth_Scores, Efficiency_Scores, Rentability_Scores,
// Cashflow_Scores, Capital_Allocation_Scores, Value_Creation_Scores) est
// désormais pré-agrégé dans une sous-requête dédiée (GROUP BY symbol,
// calendarYear) puis jointe à `af` — même colonnes de sortie, structure plus
// lisible. La fenêtre glissante de 5 ans utilise un CROSS JOIN sur une
// sous-requête MAX(calendarYear) plutôt qu'une sous-requête corrélée.
//
// ⚠️ POINT DE VIGILANCE (à corriger côté SQL, pas dans ce fichier) : la
// version REQ2 partagée hardcode le symbole ('COFA.PA') en toutes lettres
// dans ~9 endroits (WHERE principal + 8 sous-requêtes par pilier + la
// sous-requête CROSS JOIN du calendarYear max), au lieu d'un paramètre `?`
// unique comme demandé précédemment. Pour changer de titre sans risquer un
// endroit oublié, remplacer chaque `= 'COFA.PA'` par `= ?` (le paramètre est
// répété autant de fois que nécessaire dans le tableau de binding, ex.
// `params = Array(9).fill(symbol)`), ou mieux : passer par une sous-requête
// corrélée sur `af.symbol` pour n'avoir besoin que d'un seul `?` (cf.
// query_AnalyseFondamentaleCard_mapping-3.md pour un exemple).
//
// REQ1 doit renvoyer un objet unique (1 ligne). REQ2 doit renvoyer un tableau
// de lignes (une par calendarYear, idéalement déjà triées ASC).
// ══════════════════════════════════════════════════════════════════════════
const AF_API_BASE = "/api/analyse-fondamentale";

async function fetchAFHeader(symbol) {
  const res = await fetch(`${AF_API_BASE}/header?symbol=${encodeURIComponent(symbol)}`);
  if (!res.ok) throw new Error(`REQ1 (header) — échec HTTP ${res.status}`);
  return res.json();
}

async function fetchAFSeries(symbol) {
  const res = await fetch(`${AF_API_BASE}/series?symbol=${encodeURIComponent(symbol)}`);
  if (!res.ok) throw new Error(`REQ2 (série 5 ans) — échec HTTP ${res.status}`);
  const json = await res.json();
  return Array.isArray(json) ? json : (json?.rows ?? []);
}

// ── ECONOMIC IMPACT BUILDER (top-level — évite les limites Babel des IIFE) ────
// ── 2. Enrichissement dynamique : constat SQL → impact économique ──
// Pour chaque ratio du pilier, on calcule valeur/seuil/tendance
// et on génère une phrase d'impact économique contextuelle.
const avg5 = col => {
  const v=(RAW[col]||[]).filter(x=>x!==null&&!isNaN(x));
  return v.length?v.reduce((a,b)=>a+b)/v.length:null;
};
const trendDir = col => {
  const v=(RAW[col]||[]).filter(x=>x!==null&&!isNaN(x));
  if(v.length<2) return null;
  return v[v.length-1]>=v[v.length-2]?'up':'down';
};

// Fonction centrale : génère l'interprétation économique d'un ratio
// à partir de ses valeurs réelles + croisements inter-ratios
const buildEconomicImpact = (r) => {
  const val = RAW[r.col]?.[YN];
  const av  = avg5(r.col);
  const dir = trendDir(r.col);
  if (val===null||val===undefined||isNaN(val)) return null;
  const ok  = r.hi ? val>=r.thN : val<=r.thN;
  const fmt = v => fmtVal(v, r.fmt);

  // Croisements disponibles (valeurs réelles 2024)
  const roic     = RAW.roic?.[YN];
  const wacc     = RAW.wacc?.[YN];
  const spread   = roic!=null&&wacc!=null ? roic-wacc : null;
  const icr      = RAW.interest_coverage?.[YN];
  const dso      = RAW.dso?.[YN];
  const ccc      = RAW.cash_conversion_cycle?.[YN];
  const iq       = RAW.income_quality?.[YN];
  const fcfSales = RAW.fcf_to_sales?.[YN];
  const ebitdaMg = RAW.ebitda_margin?.[YN];
  const netMg    = RAW.net_margin?.[YN];
  const grossMg  = RAW.gross_margin?.[YN];
  const operMg   = RAW.operating_margin?.[YN];
  const roe      = RAW.roe?.[YN];
  const roa      = RAW.roa?.[YN];
  const revGrowth= RAW.revenue_growth?.[YN];
  const gearing  = RAW.gearing_ratio?.[YN];
  const autonomy = RAW.financial_autonomy?.[YN];

  const trendTxt = dir==='up'?' (tendance ↗)':dir==='down'?' (tendance ↘)':'';

  switch(r.col) {
    // ── LIQUIDITÉ ──────────────────────────────────────────────────
    case 'current_ratio':
      if(val>=4) return ('Un Current Ratio de ' + fmt(val) + ' est structurellement élevé pour une plateforme tech — il indique que l\'actif circulant couvre 4× les dettes CT. L\'impact positif est une résilience maximale aux chocs de trésorerie ; l\'impact négatif potentiel est une immobilisation de cash qui pourrait être alloué (ROIC−WACC = ' + spread!=null?fmt(spread):'—' + ', ce qui nuance l\'intérêt du réinvestissement).');
      if(val>=1.5) return ('Current Ratio de ' + fmt(val) + ' — couverture solide des obligations CT' + trendTxt + '. Marge de sécurité correcte.');
      return ('Current Ratio de ' + fmt(val) + ' sous le seuil 1.5×' + trendTxt + ' — risque d\'insuffisance de l\'actif circulant pour couvrir le passif CT. Une dégradation supplémentaire pourrait contraindre le financement des opérations.');
    case 'quick_ratio':
      if(Math.abs(val - (RAW.current_ratio?.[YN]||0))<0.05) return ('Quick Ratio ' + fmt(val) + ' quasi identique au Current Ratio — absence de stocks significatifs, cohérent avec un modèle de plateforme digitale (pas d\'immobilisation en inventaire).');
      return ('Quick Ratio ' + fmt(val) + trendTxt + '. Écart avec le Current (' + fmt(RAW.current_ratio?.[YN]) + ') reflète l\'exposition aux stocks.');
    case 'cash_ratio':
      if(val<0.02) return ('Cash Ratio de ' + fmt(val) + ' — quasi nul — signifie que la trésorerie disponible ne couvre plus les dettes CT à court terme' + trendTxt + '. L\'entreprise dépend entièrement de ses lignes de crédit ou de l\'encaissement des créances clients (DSO : ' + dso!=null?Math.round(dso)+'j':'—' + ') pour honorer ses paiements immédiats. Risque de tension en cas de choc.');
      if(val<0.5) return ('Cash Ratio de ' + fmt(val) + ' sous le seuil 0.5×' + trendTxt + ' — coussin de trésorerie insuffisant. Dépendance aux facilités bancaires.');
      return ('Cash Ratio de ' + fmt(val) + ' — trésorerie immédiate correcte' + trendTxt + '.');

    // ── PROFITABILITÉ ───────────────────────────────────────────────
    case 'gross_margin':
      if(val<0.05) return ('Marge brute de ' + fmt(val) + ' quasi nulle' + trendTxt + ' — signal de compression maximale du pricing power. Le coût des ventes absorbe presque 100% du CA, laissant une marge résiduelle insuffisante pour couvrir les coûts fixes. Impact direct : toute hausse des coûts variables bascule immédiatement en perte opérationnelle.');
      if(val<0.20) return ('Marge brute ' + fmt(val) + ' (seuil 40%)' + trendTxt + ' — pricing power faible, caractéristique d\'un modèle à forte intensité de coûts directs. La marge EBITDA (' + ebitdaMg!=null?fmt(ebitdaMg):'—' + ') en découle mécaniquement.');
      return ('Marge brute ' + fmt(val) + trendTxt + ' — pricing power satisfaisant.');
    case 'ebitda_margin':
      if(val<0) return ('Marge EBITDA négative (' + fmt(val) + ')' + trendTxt + ' — l\'entreprise détruit de la valeur avant amortissements et charges financières. C\'est un signal structurel grave : même sans la dette, l\'activité opérationnelle est déficitaire. L\'EBITDA est la base des valorisations EV/EBITDA — un EBITDA négatif rend ce multiple inapplicable.');
      if(val<0.10) return ('Marge EBITDA ' + fmt(val) + ' sous le seuil 15%' + trendTxt + ' — efficacité opérationnelle limitée. Marge de manœuvre réduite pour absorber les amortissements (→ marge EBIT ' + netMg!=null?fmt(netMg):'—' + ') et les charges financières.');
      return ('Marge EBITDA ' + fmt(val) + trendTxt + ' — profitabilité opérationnelle correcte.');
    case 'operating_margin':
      if(val<0) return `Marge opérationnelle négative (${fmt(val)})${trendTxt} — l'EBIT est négatif : même avant charges financières, l'activité perd de l'argent. L'ICR (${icr!=null?icr.toFixed(1)+'×':'—'}) reflète cette réalité : la dette amplifie les pertes sans base opérationnelle profitable pour les absorber.`;
      if(val<0.10) return `Marge opérationnelle ${fmt(val)}${trendTxt} — sous le seuil de 10%. La structure de coûts pèse sur la rentabilité opérationnelle après amortissements.`;
      return `Marge opérationnelle ${fmt(val)}${trendTxt} — rentabilité opérationnelle dans les normes. EBIT couvre les charges avant intérêts et impôts.`;
    case 'net_margin':
      if(val<0) return `Marge nette négative (${fmt(val)})${trendTxt} — perte nette par euro de CA. ROE ${roe!=null?fmt(roe):'—'}, ROA ${roa!=null?fmt(roa):'—'} : érosion du patrimoine actionnarial confirmée. La persistance sur plusieurs exercices signale un problème structurel, pas conjoncturel.`;
      if(val<0.05) return `Marge nette ${fmt(val)}${trendTxt} — sous le seuil de 8%. Faible rémunération du risque actionnaire : pour ${fmt(val)} de bénéfice par euro de CA, le risque d'investissement n'est pas suffisamment couvert.`;
      return `Marge nette ${fmt(val)}${trendTxt} — rentabilité finale correcte. Bénéfice net de ${fmt(val)} par euro de CA après impôts et charges financières.`;
    case 'pretax_margin':
      if(val<0) return `Marge avant impôts négative (${fmt(val)})${trendTxt} — les charges non opérationnelles (financières, exceptionnelles) amplifient les pertes opérationnelles. Écart avec EBIT Mg ${operMg!=null?fmt(operMg):'—'} : impact des charges d'intérêts et éléments non-récurrents.`;
      return `Marge avant impôts ${fmt(val)}${trendTxt}. Écart avec marge nette ${netMg!=null?fmt(netMg):'—'} → taux effectif ${val!=null&&netMg!=null&&val!==0?((1-netMg/val)*100).toFixed(0)+'%':'—'}.`;

    // ── CROISSANCE ──────────────────────────────────────────────────
    case 'revenue_growth':
      if(val<0) return `Croissance CA négative (${fmt(val)})${trendTxt} — contraction du chiffre d'affaires. Dans un contexte de marges comprimées (Gross Mg ${grossMg!=null?fmt(grossMg):'—'}), une baisse du CA amplifie les pertes via le levier opérationnel : les coûts fixes se répartissent sur un volume réduit.`;
      if(val<0.05) return `Croissance CA faible (${fmt(val)})${trendTxt} — expansion quasi stagnante. Le potentiel de création de valeur est limité tant que ROIC (${roic!=null?fmt(roic):'—'}) reste sous le WACC (${wacc!=null?fmt(wacc):'—'}).`;
      if(val<0.10) return `Croissance CA ${fmt(val)}${trendTxt} — dynamique modérée. La décélération reflète la maturité du marché de la livraison en Chine et la pression de Douyin. Spread ROIC-WACC ${spread!=null?fmt(spread):'—'} : la croissance ne crée de valeur que si ce spread est positif.`;
      return `Croissance CA forte (${fmt(val)})${trendTxt} — expansion commerciale solide. L'enjeu : s'assurer que cette croissance crée de la valeur (spread ROIC-WACC ${spread!=null?fmt(spread):'—'}).`;
    case 'net_income_growth':
      if(val<-1) return `Croissance RN de ${fmt(val)}${trendTxt} — effondrement des bénéfices. Variation de ${Math.abs(val*100).toFixed(0)}% : effet ciseau probable (revenus stagnants + coûts croissants) ou charges exceptionnelles. IQ ${iq!=null?iq.toFixed(2)+'×':'—'} — distinguer comptable de cash.`;
      if(val<0) return `Résultat net en recul (${fmt(val)})${trendTxt} — érosion de la profitabilité. Peut résulter d'une compression des marges (Net Mg ${netMg!=null?fmt(netMg):'—'}) ou d'éléments non-récurrents. IQ ${iq!=null?iq.toFixed(2)+'×':'—'} pour évaluer la qualité.`;
      if(val<0.05) return `Résultat net quasi stable (${fmt(val)})${trendTxt} — croissance insuffisante pour couvrir l'inflation et le coût du capital. Stagnation des bénéfices + croissance du CA → compression des marges à surveiller.`;
      return `Croissance RN ${fmt(val)}${trendTxt}. Écart avec EPS growth (${RAW.eps_growth?.[YN]!=null?fmt(RAW.eps_growth[YN]):'—'}) révèle l'effet rachats ou la dilution. IQ ${iq!=null?iq.toFixed(2)+'×':'—'}.`;
    case 'eps_growth': {
      const niGrowth = RAW.net_income_growth?.[YN];
      const diff2 = Math.abs(((val||0) - (niGrowth||0)));
      if(val<-1) return `EPS en chute de ${fmt(val)}${trendTxt} — effondrement du bénéfice par action. Écart avec RN growth (${niGrowth!=null?fmt(niGrowth):'—'}) : ${diff2>0.05?'dilution significative via stock-options/convertibles':'chute reflète les pertes nettes'}.`;
      if(val<0) return `EPS en recul (${fmt(val)})${trendTxt}. ${diff2>0.05?`Écart avec RN growth (${niGrowth!=null?fmt(niGrowth):'—'}) signale une dilution active via stock-options ou émissions.`:'Reflète la contraction du résultat net.'} L'EPS dilué est la mesure directe de la valeur créée par action existante.`;
      if(val<0.05) return `EPS quasi stable (${fmt(val)})${trendTxt}. ${diff2>0.05?`Écart RN vs EPS (${niGrowth!=null?fmt(niGrowth):'—'} vs ${fmt(val)}) : rachats d'actions actifs réduisent le nombre de titres en circulation.`:''}`;
      return `EPS growth ${fmt(val)}${trendTxt}${diff2>0.05?` — écart avec RN growth (${niGrowth!=null?fmt(niGrowth):'—'}) : ${val>=(niGrowth||0)?'rachats d\'actions actifs':'dilution via stock-options'}`:''}. L'EPS est la mesure directe de la valeur créée par action.`;
    }
    case 'bvps_growth':
      if(val<0) return `Valeur comptable par action en recul (${fmt(val)})${trendTxt} — le patrimoine actionnarial se réduit, soit par pertes accumulées, soit par distributions supérieures au résultat. Signal de destruction de substance économique.`;
      return `Croissance BVPS ${fmt(val)}${trendTxt} — accumulation de patrimoine ${ok?'correcte':'insuffisante'} par action.`;
    case 'fcf_growth':
      if(val<-1) return `FCF en chute de ${fmt(val)}${trendTxt} — génération de cash libre en effondrement. FCF/CA actuel ${fcfSales!=null?fmt(fcfSales):'—'} : un FCF chroniquement négatif oblige au recours aux marchés pour financer les opérations.`;
      if(val<0) return `FCF en recul (${fmt(val)})${trendTxt} — génération de cash en contraction. FCF/CA ${fcfSales!=null?fmt(fcfSales):'—'} : la tendance du FCF est le signal le plus important pour l'évaluation DCF.`;
      if(val<0.05) return `FCF quasi stable (${fmt(val)})${trendTxt} — progression insuffisante. FCF/CA ${fcfSales!=null?fmt(fcfSales):'—'}.`;
      return `Croissance FCF ${fmt(val)}${trendTxt} — génération de cash en hausse. La trajectoire haussière du FCF est le signal structurellement important pour la valorisation DCF.`;

    // ── EFFICACITÉ ──────────────────────────────────────────────────
    case 'fixed_asset_turnover':
      return `Rotation actifs fixes : ${fmt(val)}${trendTxt}. Pour un modèle de plateforme digitale, une rotation très élevée est attendue (actifs immatériels légers) — ce ratio reflète la quasi-absence d'immobilisations lourdes, pas une sur-performance opérationnelle.`;
    case 'receivables_turnover':
      if(val<2) return `Rotation clients ${fmtVal(val,'x')}${trendTxt} — très faible, traduit un DSO de ${dso!=null?Math.round(dso)+'j':'—'}. L'entreprise immobilise l'équivalent de plusieurs mois de CA en créances non encaissées. Impact direct sur le BFR et le FCF réel (FCF/CA : ${fcfSales!=null?fmt(fcfSales):'—'}).`;
      if(val>50) return `Rotation clients exceptionnelle (${fmtVal(val,'x')})${trendTxt} — DSO de ${dso!=null?Math.round(dso)+'j':'—'} : les clients paient quasi instantanément (modèle paiement app). Ce niveau confirme le BFR structurellement négatif : L'entreprise encaisse avant de décaisser (modèle plateforme B2C), libérant du cash opérationnel sans recours au financement externe.`;
      if(val>15) return `Rotation clients élevée (${fmtVal(val,'x')})${trendTxt} — DSO de ${dso!=null?Math.round(dso)+'j':'—'} : encaissements rapides, BFR maîtrisé. Avantage structurel du modèle plateforme B2C à paiement immédiat.`;
      return `Rotation clients ${fmtVal(val,'x')}${trendTxt} — DSO de ${dso!=null?Math.round(dso)+'j':'—'}. Un DSO court réduit le BFR et améliore la conversion CA→FCF (FCF/CA actuel : ${fcfSales!=null?fmt(fcfSales):'—'}).`;
    case 'dso':
      if(val>200) return `DSO de ${Math.round(val)} jours${trendTxt} — extrêmement long. Les créances clients représentent plus de ${Math.round(val/365*100)}% du CA annuel immobilisé. Pour une plateforme à paiement anticipé, ce délai peut refléter des créances inter-groupes ou des acomptes — à analyser dans le contexte sectoriel.`;
      if(val>90) return `DSO de ${Math.round(val)} jours${trendTxt} — allongement préoccupant du cycle client. Immobilisation de BFR qui pèse directement sur le FCF réel (FCF/CA : ${fcfSales!=null?fmt(fcfSales):'—'}).`;
      if(!ok) return `DSO de ${Math.round(val)} jours${trendTxt} — délai à surveiller. Un allongement du DSO signale soit une dégradation de la qualité clients, soit une politique commerciale trop souple.`;
      return `DSO de ${Math.round(val)} jours${trendTxt} — délai d'encaissement maîtrisé.`;
    case 'cash_conversion_cycle':
      if(val<0) return `CCC de ${Math.round(val)} jours${trendTxt} — cycle négatif : l'entreprise encaisse avant de décaisser. Structure idéale pour la trésorerie (modèle plateforme).`;
      if(val>200) return `CCC de ${Math.round(val)} jours${trendTxt} — cycle d'exploitation extrêmement long. L'entreprise immobilise du capital ${Math.round(val)} jours entre décaissement et encaissement. À ce niveau, le CCC explique en partie pourquoi le FCF (${fcfSales!=null?fmt(fcfSales):'—'} du CA) reste négatif malgré une activité en croissance.`;
      if(!ok) return `CCC de ${Math.round(val)} jours${trendTxt} — cycle long, BFR élevé. Le besoin en fonds de roulement consomme du cash opérationnel et pèse sur la liquidité.`;
      return `CCC de ${Math.round(val)} jours${trendTxt} — cycle d'exploitation maîtrisé.`;

    // ── RENTABILITÉ ─────────────────────────────────────────────────
    case 'roe':
      if(val<0) return `ROE négatif (${fmt(val)})${trendTxt} — les fonds propres se dégradent : l'entreprise perd plus qu'elle ne gagne, érodant la base de capital. La décomposition DuPont révèle l'origine : marge nette ${netMg!=null?fmt(netMg):'—'} × rotation actifs × levier. Ici la marge nette négative est le facteur déterminant.`;
      if(val<0.06) return `ROE de ${fmt(val)} très faible${trendTxt} — rendement quasi nul des fonds propres. En dessous du coût des capitaux propres (estimé à ~${wacc!=null?fmt(wacc+0.03):'~8%'} pour ce profil de risque), l'investissement en fonds propres ne se justifie pas économiquement. Décomposition DuPont : marge nette ${netMg!=null?fmt(netMg):'—'} × rotation actifs × levier ${RAW.debt_to_equity?.[YN]!=null?fmtVal(RAW.debt_to_equity[YN],'x'):'—'}.`;
      if(val<0.12) return `ROE de ${fmt(val)} sous le seuil 12%${trendTxt} — rendement insuffisant des fonds propres. Pour justifier l'investissement, le ROE doit couvrir le coût des capitaux propres (≈ 10–12%). DuPont : marge nette ${netMg!=null?fmt(netMg):'—'} × rotation actifs. La marge est le levier prioritaire à améliorer.`;
      return `ROE ${fmt(val)}${trendTxt} — rendement solide des fonds propres. DuPont : marge nette ${netMg!=null?fmt(netMg):'—'} × rotation actifs × levier ${RAW.debt_to_equity?.[YN]!=null?fmtVal(RAW.debt_to_equity[YN],'x'):'—'}. ROE > coût des capitaux propres — l'investissement se justifie économiquement.`;
    case 'roa':
      if(val<0) return `ROA négatif (${fmt(val)})${trendTxt} — les actifs totaux ne génèrent pas de résultat positif. Cela signifie que même sans dette, l'entreprise serait déficitaire. Ce signal est plus fondamental que le ROE car il est indépendant de la structure de financement.`;
      return `ROA ${fmt(val)}${trendTxt} — rendement des actifs ${ok?'correct':'insuffisant'}.`;
    case 'roic':
      if(spread!=null) {
        if(val<0) return `ROIC négatif (${fmt(val)}) — WACC ${fmt(wacc)} → spread : ${fmt(spread)}${trendTxt}. Destruction de valeur économique sur chaque euro de capital investi. En termes EVA : NOPAT < coût du capital × IC, donc chaque projet récent détruit de la valeur. Continuer à investir dans ces conditions revient à accélérer la destruction.`;
        if(spread<0) return `ROIC ${fmt(val)} < WACC ${fmt(wacc)} → spread négatif ${fmt(spread)}${trendTxt}. La frontière est franchie : l'entreprise ne couvre pas son coût du capital. Dans un modèle DCF, une croissance avec ROIC < WACC réduit la valeur — la croissance est un fardeau, pas un atout.`;
        return `ROIC ${fmt(val)} > WACC ${fmt(wacc)} → spread positif ${fmt(spread)}${trendTxt}. Création de valeur économique confirmée.`;
      }
      return `ROIC ${fmt(val)}${trendTxt} — rentabilité du capital investi ${ok?'au-dessus':'sous'} le seuil de 8%.`;
    case 'rota':
      if(val<0) return `ROTA négatif (${fmt(val)})${trendTxt} — même les actifs tangibles ne génèrent pas de retour positif. Signal convergent avec le ROIC (${roic!=null?fmt(roic):'—'}) : la rentabilité est structurellement insuffisante sur tous les supports d'actifs.`;
      if(!ok) return `ROTA ${fmt(val)}${trendTxt} — sous le seuil 5%. Les actifs tangibles sont sous-exploités ou trop coûteux relativement au résultat qu'ils génèrent.`;
      return `ROTA ${fmt(val)}${trendTxt} — rendement des actifs tangibles correct.`;
    case 'opcf_to_sales':
      if(val<0) return `CAF/CA négatif (${fmt(val)})${trendTxt} — la capacité d'autofinancement est négative : l'activité consomme du cash plutôt qu'en générer. Le modèle économique ne s'autofinance pas, ce qui explique la dépendance aux financements externes.`;
      if(!ok) return `CAF/CA ${fmt(val)}${trendTxt} — part du CA convertie en cash opérationnel insuffisante (seuil 5%). La rentabilité comptable ne se traduit pas encore en flux de trésorerie solides.`;
      return `CAF/CA ${fmt(val)}${trendTxt} — conversion du CA en capacité d'autofinancement satisfaisante.`;

    // ── CASH FLOW ────────────────────────────────────────────────────
    case 'fcf_per_share':
      if(val<0) return ('FCF par action négatif (' + fmt(val) + ')' + trendTxt + ' — après capex, l\'entreprise consomme du cash à hauteur de ' + fmt(Math.abs(val)) + ' par action. Une valorisation DCF reposant sur des FCF négatifs exige une actualisation de la phase de normalisation future — risque élevé si le retour à un FCF positif est hypothétique.');
      return `FCF par action ${fmt(val)}${trendTxt} — génération de cash libre par action ${ok?'positive':'à surveiller'}.`;
    case 'fcf_to_sales':
      if(val<0) return `FCF/CA négatif (${fmt(val)})${trendTxt} — pour chaque euro de CA, l'entreprise consomme du cash après investissements. En combinaison avec un DSO de ${dso!=null?Math.round(dso)+'j':'—'} et une IQ de ${iq!=null?iq.toFixed(2)+'×':'—'}, cela indique que ni le cycle opérationnel ni la qualité des bénéfices ne compensent les besoins de financement.`;
      if(val<0.05) return `FCF/CA ${fmt(val)}${trendTxt} — conversion CA→cash libre insuffisante (seuil 5%). L'entreprise génère du cash opérationnel (OCF/CA : ${RAW.opcf_to_sales?.[YN]!=null?fmt(RAW.opcf_to_sales[YN]):'—'}) mais les capex consomment l'essentiel. Vérifier si ces capex sont de croissance (créateurs de valeur si ROIC > WACC) ou de maintenance.`;
      return `FCF/CA ${fmt(val)}${trendTxt} — conversion satisfaisante du CA en cash libre. La marge FCF de ${fmt(val)} signifie que pour chaque 100 HKD de CA, l'entreprise génère ${((val||0)*100).toFixed(1)} HKD de cash disponible pour les actionnaires, le désendettement ou les investissements stratégiques.`;
    case 'income_quality':
      if(val<0) return ('Income Quality (IQ) de ' + fmt(val) + ' — négatif' + trendTxt + '. Les bénéfices comptables et les cash flows opérationnels sont de signe opposé. Fridson : un IQ négatif signale soit une comptabilisation d\'accruals extrêmement agressive (revenus reconnus sans encaissement), soit une situation de cash burn structurelle dissimulée par des conventions comptables favorables.');
      if(val<0.5) return ('IQ de ' + fmt(val) + trendTxt + ' — bien en dessous du seuil 0.8×. Seulement ' + (val*100).toFixed(0) + '% du bénéfice comptable est converti en cash. Les accruals positifs (créances, stocks non encaissés) gonflent le résultat sans contrepartie de trésorerie. Signal d\'alerte comptable.');
      if(val>=1) return ('IQ de ' + fmt(val) + ' ≥ 1×' + trendTxt + ' — le cash opérationnel dépasse le bénéfice comptable. Qualité des bénéfices élevée : pas d\'accruals artificiels, les résultats sont conservateurs par rapport au cash réel.');
      return ('IQ de ' + fmt(val) + trendTxt + ' — qualité des bénéfices correcte.');
    case 'fcf_to_opcf':
      if(val>=1) return ('FCF/OCF de ' + fmt(val) + ' ≥ 1×' + trendTxt + ' — le Free Cash Flow couvre intégralement le cash opérationnel. Situation atypique possible en cas de capex nul (pas d\'investissement) — à vérifier.');
      return ('FCF/OCF ' + fmt(val) + trendTxt + ' — part du cash opérationnel disponible après capex.');

    // ── SOLVABILITÉ ──────────────────────────────────────────────────
    case 'net_debt_to_ebitda':
      if(val===null||isNaN(val)) return ('DN/EBITDA non calculable (EBITDA négatif) — signal critique : la dette nette ne peut plus être exprimée en multiples d\'EBITDA, ce qui signifie que la capacité de remboursement via les flux opérationnels est impossible à mesurer avec cet indicateur. Les agences de notation considèrent généralement un EBITDA négatif comme le signal d\'une structure financière sous stress.');
      if(val>5) return ('DN/EBITDA de ' + fmt(val) + ' — levier très élevé' + trendTxt + '. La dette représente plus de 5 ans d\'EBITDA. Zone d\'alerte pour les covenants bancaires.');
      return `DN/EBITDA ${fmt(val)}${trendTxt} — levier ${ok?'en zone de confort (<3×)':'à surveiller'}.`;
    case 'interest_coverage':
      if(val<0) return `ICR de ${fmt(val)} — négatif${trendTxt}. L'EBIT est négatif : l'entreprise ne génère pas assez de résultat opérationnel pour couvrir ses intérêts, même avant. L'intégralité de la dette est financée par la substance du bilan, pas par les flux. Signal de détresse financière structurelle si persistant.`;
      if(val<1.5) return `ICR de ${fmt(val)} — couverture insuffisante des intérêts${trendTxt}. En dessous de 1.5×, le risque de défaut augmente significativement. Les créanciers peuvent exiger des garanties supplémentaires ou activer des covenants.`;
      if(val>=30) return `ICR de ${fmt(val)}${trendTxt} — couverture des intérêts très ample. La dette est quasi insignifiante par rapport aux flux opérationnels. Autonomie financière totale vis-à-vis des créanciers.`;
      return `ICR de ${fmt(val)}${trendTxt} — couverture des intérêts ${ok?'satisfaisante':'fragile'}.`;
    case 'gearing_ratio':
      if(val>1) return `Gearing de ${fmt(val)} — dette nette supérieure aux fonds propres${trendTxt}. Levier financier excessif qui amplifie les pertes en période défavorable. Croisé avec un ICR de ${icr!=null?fmtVal(icr,'x'):'—'}, la capacité à servir la dette est limitée.`;
      return `Gearing ${fmt(val)}${trendTxt} — levier ${ok?'maîtrisé (<1×)':'modéré'}. L'autonomie financière (${autonomy!=null?fmt(autonomy):'—'}) confirme.`;
    case 'financial_autonomy':
      if(val>0.70) return `Autonomie financière de ${fmt(val)}${trendTxt} — ${(val*100).toFixed(0)}% des actifs financés par les fonds propres. Bilan très solide, flexibilité stratégique maximale. Permet d'absorber des pertes significatives sans risque d'insolvabilité à court terme.`;
      return `Autonomie financière ${fmt(val)}${trendTxt} — ${ok?'équilibre satisfaisant':'dépendance à la dette'}.`;
    case 'debt_to_assets':
      return `Dette/Actifs : ${fmt(val)}${trendTxt} — ${(val*100).toFixed(0)}% des actifs financés par la dette. ${ok?'Structure de financement équilibrée.':'Exposition à l\'endettement supérieure au seuil prudentiel de 50%.'}`;
    case 'solvency_ratio':
      if(val>3) return `Ratio de solvabilité de ${fmt(val)}${trendTxt} — les fonds propres couvrent ${val.toFixed(1)}× les dettes totales. Solidité bilancielle exceptionnelle qui offre une marge de sécurité très élevée pour les créanciers et les actionnaires.`;
      return `Ratio de solvabilité ${fmt(val)}${trendTxt} — couverture des dettes par les FP ${ok?'satisfaisante':'à améliorer'}.`;

    // ── ALLOCATION CAPITAL ───────────────────────────────────────────
    case 'roic_vs_wacc':
      if(val<0) return `Spread ROIC−WACC de ${fmt(val)}${trendTxt}. Destruction de valeur économique : l'entreprise investit à un taux inférieur à son coût du capital. Chaque euro de croissance supplémentaire réduit la valeur intrinsèque. Graham : dans ces conditions, retenir les bénéfices aggrave la situation — distribuer serait économiquement plus rationnel.`;
      return `Spread ROIC−WACC de ${fmt(val)}${trendTxt} — ${ok?'création de valeur économique confirmée.':'création de valeur fragile, à surveiller.'}`;
    case 'retention_rate':
      if(spread!=null&&spread<0) return `Taux de rétention de ${fmt(val)}${trendTxt} — mais avec un spread ROIC−WACC de ${fmt(spread)}, chaque euro retenu détruit de la valeur. La rétention totale est économiquement contre-productive dans ce contexte.`;
      return `Taux de rétention ${fmt(val)}${trendTxt} — ${ok?'capital retenu et potentiellement réinvesti.':'distribution limitée.'}`;
    case 'capex_to_depreciation':
      if(val===0||val<0.1) return `Capex/D&A nul ou quasi nul${trendTxt} — absence d'investissement. L'entreprise n'investit pas dans le renouvellement de ses actifs productifs. À court terme c'est du cash préservé ; à long terme c'est un sous-investissement qui peut éroder la compétitivité.`;
      if(val<1) return `Capex/D&A de ${fmt(val)} < 1×${trendTxt} — sous-investissement. Les capex ne couvrent pas l'amortissement du capital existant, ce qui signifie une contraction nette de la base d'actifs productifs.`;
      return `Capex/D&A de ${fmt(val)}${trendTxt} — ${val>1.5?'phase d\'expansion active.':'renouvellement du capital couvert.'}`;
    case 'reinvestment_rate':
      if(val<0) return `Taux de réinvestissement négatif (${fmt(val)})${trendTxt} — la base de capital investi se contracte (désinvestissement net). Dans un contexte de spread ROIC−WACC négatif (${spread!=null?fmt(spread):'—'}), c'est économiquement rationnel : réduire le capital alloué à des projets qui détruisent de la valeur.`;
      return `Taux de réinvestissement ${fmt(val)}${trendTxt}.`;

    // ── CRÉATION DE VALEUR ───────────────────────────────────────────
    case 'wacc':
      return `WACC de ${fmt(val)}${trendTxt} — coût du capital de ${(val*100).toFixed(1)}%. C'est le taux minimum que l'entreprise doit gagner sur ses investissements. Comparé au ROIC (${roic!=null?fmt(roic):'—'}), le spread est ${spread!=null?fmt(spread):'non calculable'}.`;
    case 'sustainable_growth_rate':
      if(val<=0) return `Croissance soutenable nulle ou négative (${fmt(val)})${trendTxt} — l'entreprise ne peut pas financer organiquement sa croissance. Toute expansion requiert du capital externe (dette ou émission d'actions). Dans un contexte de ROIC sous le WACC, c'est une contrainte supplémentaire.`;
      return `Croissance soutenable de ${fmt(val)}${trendTxt} — financement organique possible jusqu'à ${fmt(val)} de croissance annuelle.`;
    case 'roce':
      if(val<0) return `ROCE négatif (${fmt(val)})${trendTxt} — le capital employé (FP + dettes LT) ne génère pas de résultat opérationnel positif. Indicateur convergent avec le ROIC négatif : toutes les mesures de rentabilité du capital pointent dans la même direction.`;
      return `ROCE ${fmt(val)}${trendTxt} — rendement du capital employé ${ok?'au-dessus du seuil 8%':'sous le seuil de référence'}.`;

    default:
      return ok
        ? (r.short + ' de ' + fmt(val) + ' au-dessus du seuil ' + r.th + trendTxt + '.')
        : (r.short + ' de ' + fmt(val) + ' sous le seuil ' + r.th + trendTxt + ' — à surveiller.');
  }
};

// NOTE : la version fonctionnelle d'enrichItem (avec accès à `pillar`) est
// définie localement à l'intérieur de PillarView, où `pillar` est bien en portée.

// ── PDRC HELPERS (ProfilDescriptionRadarCard) ───────────────────────────────
const PDRC_STOPS = ["#bd4b38","#a25f2a","#886b2d","#6b754c","#617650","#5f774e"];
function colorForPct(pct) {
  const t = Math.max(0, Math.min(1, pct / 100));
  const seg = (PDRC_STOPS.length - 1) * t;
  const i = Math.min(PDRC_STOPS.length - 2, Math.floor(seg));
  const f = seg - i;
  const hex = h => { const n = parseInt(h.replace("#",""), 16); return [(n>>16)&255,(n>>8)&255,n&255]; };
  const a = hex(PDRC_STOPS[i]), b = hex(PDRC_STOPS[i+1]);
  const ch = j => Math.max(0,Math.min(255,Math.round(a[j]+(b[j]-a[j])*f))).toString(16).padStart(2,"0");
  return '#'+ch(0)+ch(1)+ch(2);
}
function RadarAxisTick({ x, y, payload }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fontSize={10.5} fontWeight={600}
        style={{ fill:"var(--mu)", fontFamily:"ui-sans-serif,system-ui" }}>
        {payload?.value}
      </text>
    </g>
  );
}
function RadarTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  return (
    <div style={{ background:"var(--card)", border:"1px solid var(--bdr)",
      borderRadius:10, padding:"8px 12px", fontSize:12, boxShadow:"0 4px 12px rgba(0,0,0,.24)" }}>
      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:14, fontWeight:700,
        color:colorForPct(val) }}>{val}/100</div>
      <div style={{ fontSize:11, marginTop:2, color:"var(--mu)" }}>
        {val>=80?"Excellent":val>=60?"Bon":val>=40?"Moyen":"Faible"}
      </div>
    </div>
  );
}
function TipIconPdrc({ text }) {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  return (
    <span ref={ref} style={{ position:"relative", display:"inline-flex" }}
      onMouseEnter={() => setVis(true)} onMouseLeave={() => setVis(false)}>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
        style={{ opacity:.45, marginLeft:3, cursor:"help", flexShrink:0 }}>
        <circle cx="6" cy="6" r="5.4" stroke="currentColor" strokeWidth="1.1"/>
        <rect x="5.3" y="5" width="1.4" height="4.2" rx=".7" fill="currentColor"/>
        <rect x="5.3" y="2.8" width="1.4" height="1.4" rx=".7" fill="currentColor"/>
      </svg>
      {vis && (
        <span style={{ position:"fixed",
          left: (ref.current?.getBoundingClientRect().right || 0) + 6,
          top:  Math.max(8, (ref.current?.getBoundingClientRect().top || 0) - 4),
          zIndex:9999, minWidth:200, maxWidth:280,
          background:"var(--tbg,var(--card))", border:"1px solid var(--tbr,var(--bdr))",
          borderRadius:10, padding:"9px 12px",
          boxShadow:"0 8px 32px rgba(0,0,0,.28)", pointerEvents:"none",
          fontSize:12, color:"var(--tx)", lineHeight:1.65,
        }}>{text}</span>
      )}
    </span>
  );
}

// ── GUIDE D'ANALYSE PAR PILIER ───────────────────────────────────────────────
// Questions clés, ratios associés, seuils interprétatifs et logique analytique
const PILLAR_GUIDE = {
  // ORDRE D'ANALYSE RECOMMANDÉ :
  // 1. Solvabilité (peut-elle survivre ?)
  // 2. Liquidité (peut-elle payer à CT ?)
  // 3. Profitabilité (gagne-t-elle de l'argent ?)
  // 4. Rentabilité (bien sur les ressources engagées ?)
  // 5. Cash Flow (le bénéfice est-il du vrai cash ?)
  // 6. Efficacité (utilise-t-elle bien ses actifs ?)
  // 7. Croissance (se développe-t-elle ?)
  // 8. Alloc. Capital (déploie-t-elle bien son cash ?)
  // 9. Création Valeur (ROIC > WACC ?)

  solvency: {
    ordre: 1,
    titre: "Peut-elle survivre à long terme ?",
    intro: "La solvabilité est la question préalable à toute analyse. Avant de s'intéresser à la rentabilité ou à la croissance, il faut s'assurer que l'entreprise ne risque pas de faire défaut sur ses obligations financières. Fabozzi (Financial Management & Analysis) : une insolvabilité peut effacer toute valeur actionnariale en quelques mois.",
    questions: [
      {
        q: "L'endettement est-il soutenable ?",
        ratios: ["DN/EBITDA", "Gearing"],
        seuil: "DN/EBITDA < 2× (confort) · 2–3× (acceptable) · > 3× (vigilance) · > 5× (danger)",
        logique: "La dette nette rapportée à l'EBITDA mesure combien d'années de cash opérationnel serait nécessaire pour rembourser la dette. C'est la mesure standardisée des banques et des agences de notation.",
      },
      {
        q: "Peut-elle couvrir ses intérêts ?",
        ratios: ["ICR (Couverture des intérêts)"],
        seuil: "ICR > 5× (solide) · 3–5× (correct) · 1.5–3× (fragile) · < 1.5× (critique)",
        logique: "L'EBIT divisé par les charges financières. En dessous de 1×, l'entreprise ne couvre plus ses intérêts avec ses bénéfices opérationnels — signe de détresse financière.",
      },
      {
        q: "La structure de financement est-elle équilibrée ?",
        ratios: ["Autonomie financière", "Dette / Actifs (D/A)"],
        seuil: "Autonomie > 50% (solide) · D/A < 50% (confort)",
        logique: "Un bilan financé majoritairement par les fonds propres offre une flexibilité stratégique et une résilience face aux chocs. Un excès de dette contraint les choix managériaux et amplifie le risque.",
      },
    ],
    conclusion_analytique: "Commencer par vérifier la structure du bilan. Si la dette est excessive ou les intérêts non couverts, la valorisation doit intégrer une prime de risque significative — voire exclure l'investissement.",
  },

  liquidity: {
    ordre: 2,
    titre: "Peut-elle honorer ses engagements à court terme ?",
    intro: "La liquidité est distincte de la solvabilité : une entreprise peut être solvable (actifs > passifs totaux) mais illiquide (incapable de payer ses fournisseurs dans les 30 prochains jours). Reilly & Brown : une crise de liquidité peut précipiter une faillite même pour une entreprise fondamentalement saine.",
    questions: [
      {
        q: "L'actif courant couvre-t-il le passif courant ?",
        ratios: ["Current Ratio"],
        seuil: "> 2× (solide) · 1.5–2× (correct) · 1–1.5× (fragile) · < 1× (insuffisant)",
        logique: "Le ratio de liquidité courante compare actifs CT et passifs CT. Un ratio > 1.5× est généralement satisfaisant, mais dépend du secteur : une enseigne de distribution (rotation rapide) peut opérer avec un ratio < 1×.",
      },
      {
        q: "La liquidité tient-elle sans les stocks ?",
        ratios: ["Quick Ratio"],
        seuil: "> 1× (suffisant) · < 1× (dépendance aux stocks)",
        logique: "Le Quick Ratio exclut les stocks (moins liquides). Toujours comparer au Current Ratio : un écart important signale une exposition aux risques d'invendu ou d'obsolescence.",
      },
      {
        q: "La trésorerie pure est-elle suffisante ?",
        ratios: ["Cash Ratio"],
        seuil: "> 0.5× (correct) · < 0.2× (risque de choc)",
        logique: "Le Cash Ratio mesure la couverture des dettes CT par la seule trésorerie disponible. Utile en situation de stress ou pour évaluer la dépendance aux lignes de crédit bancaires.",
      },
    ],
    conclusion_analytique: "Une liquidité confortable réduit le risque opérationnel à court terme. Mais une liquidité excessive (Current > 5×) peut aussi signaler une mauvaise allocation du cash — à recouper avec le pilier Allocation du Capital.",
  },

  profitability: {
    ordre: 3,
    titre: "Génère-t-elle des marges suffisantes ?",
    intro: "La profitabilité analyse le compte de résultat de haut en bas : de la marge brute (pricing power) à la marge nette (rentabilité finale). Damodaran : 'Les marges reflètent la position concurrentielle de l'entreprise et sa capacité à défendre son avantage compétitif.'",
    questions: [
      {
        q: "Quel est le pricing power de l'entreprise ?",
        ratios: ["Marge brute"],
        seuil: "> 40% (fort) · 20–40% (correct) · < 20% (faible)",
        logique: "La marge brute mesure la part du CA conservée après les coûts directs. Une marge brute élevée indique un avantage compétitif ou un modèle à faible intensité de coûts variables (logiciel, luxe). Une marge brute faible est typique des secteurs à fort coût des ventes (distribution, matériaux).",
      },
      {
        q: "L'efficacité opérationnelle est-elle satisfaisante ?",
        ratios: ["Marge EBITDA", "Marge EBIT (opérationnelle)"],
        seuil: "EBITDA > 15% (correct) · EBIT > 10% (acceptable)",
        logique: "La marge EBITDA mesure la profitabilité avant amortissements et charges financières — l'indicateur de comparaison inter-sectoriel le plus utilisé. La marge EBIT intègre l'amortissement du capital et révèle si les investissements passés sont 'digérés'.",
      },
      {
        q: "La conversion finale en bénéfice est-elle satisfaisante ?",
        ratios: ["Marge avant impôts", "Marge nette"],
        seuil: "Marge nette > 8% (bon) · 5–8% (acceptable) · < 5% (fragile)",
        logique: "La marge nette est la synthèse finale. Un écart important entre marge EBIT et marge nette peut signaler des charges financières élevées (dette excessive) ou une fiscalité anormale (optimisation ou redressement).",
      },
    ],
    conclusion_analytique: "Analyser la trajectoire des marges sur 5 ans, pas seulement le niveau absolu. Une entreprise avec des marges faibles mais en amélioration peut avoir plus de valeur qu'une entreprise à fortes marges qui se dégradent. Croiser systématiquement avec la Rentabilité (ROIC).",
  },

  rentability: {
    ordre: 4,
    titre: "Les ressources engagées sont-elles bien rémunérées ?",
    intro: "La rentabilité mesure le rendement des ressources engagées — fonds propres (ROE), actifs totaux (ROA), capital investi (ROIC). C'est le pilier central de l'analyse fondamentale car il conditionne la capacité à créer de la valeur actionnariale sur le long terme.",
    questions: [
      {
        q: "Le rendement pour l'actionnaire est-il satisfaisant ?",
        ratios: ["ROE"],
        seuil: "> 15% (excellent) · 10–15% (bon) · 5–10% (médiocre) · < 5% (insuffisant)",
        logique: "Le ROE = Résultat net / FP. Décomposable via la formule DuPont : ROE = Marge nette × Rotation des actifs × Levier financier. Attention : un ROE élevé peut résulter d'un levier élevé (dette) plutôt que d'une vraie efficacité — toujours croiser avec le ROA.",
      },
      {
        q: "L'ensemble des actifs travaille-t-il correctement ?",
        ratios: ["ROA"],
        seuil: "> 8% (excellent) · 5–8% (correct) · < 3% (faible)",
        logique: "Le ROA mesure le rendement indépendamment du financement. Un ROA durablement supérieur au coût de la dette indique que l'effet de levier est économiquement justifié. Un ROA < coût de la dette = levier qui détruit de la valeur.",
      },
      {
        q: "Le capital investi est-il rentabilisé au-delà du coût du capital ?",
        ratios: ["ROIC", "ROTA"],
        seuil: "ROIC > WACC (création de valeur) · ROIC > 10% (généralement solide)",
        logique: "Le ROIC est la mesure la plus rigoureuse de la rentabilité économique : NOPAT / Capital Investi. C'est l'indicateur clé de l'analyse DCF. Seul un ROIC > WACC justifie la croissance. Un ROIC < WACC = chaque euro investi détruit de la valeur.",
      },
    ],
    conclusion_analytique: "La hiérarchie analytique est : ROIC > ROA > ROE. Le ROIC comparé au WACC est la question centrale : une entreprise qui gagne 15% sur son capital investi et dont le coût du capital est de 8% crée 7 points de valeur économique par an. C'est la rente économique.",
  },

  cashflow: {
    ordre: 5,
    titre: "Le bénéfice se convertit-il en cash réel ?",
    intro: "Le cash flow est 'la vérité comptable ultime' (Fridson & Alvarez). Les bénéfices comptables peuvent être influencés par des choix d'amortissement, des provisions ou des accruals. Le cash, lui, ne ment pas — il est dans (ou hors de) la trésorerie.",
    questions: [
      {
        q: "Le FCF est-il positif et croissant ?",
        ratios: ["FCF par action", "FCF / CA"],
        seuil: "FCF/sh > 0 (indispensable) · FCF/CA > 8% (solide)",
        logique: "Un FCF positif signifie que l'entreprise génère du cash après capex. Un FCF négatif chronique implique un recours récurrent aux marchés (émission d'actions, dette) pour financer les opérations — risque de dilution ou de crise de liquidité.",
      },
      {
        q: "Les bénéfices comptables sont-ils de qualité ?",
        ratios: ["Income Quality (IQ)"],
        seuil: "> 1× (excellent) · 0.8–1× (correct) · < 0.5× (accruals élevés)",
        logique: "L'IQ = CFO / Résultat net. Un IQ < 1 signifie que les bénéfices comptables surpassent le cash réel généré — signe d'accruals positifs (créances, stocks) qui gonflent artificiellement les résultats. Fridson : IQ < 0.5× est un signal d'alerte comptable sérieux.",
      },
      {
        q: "Le cash opérationnel représente-t-il une part significative du CA ?",
        ratios: ["OCF / CA"],
        seuil: "> 10% (solide) · 5–10% (correct) · < 5% (fragile)",
        logique: "L'OCF/CA mesure la part directe du chiffre d'affaires convertie en cash opérationnel. C'est un indicateur de la qualité du modèle économique, indépendamment des choix de capex.",
      },
    ],
    conclusion_analytique: "Une entreprise avec des bénéfices comptables mais un FCF chroniquement négatif mérite une décote de valorisation significative. La valeur DCF repose sur les flux de cash réels, pas sur l'EPS comptable. Systématiquement croiser IQ et FCF/CA.",
  },

  efficiency: {
    ordre: 6,
    titre: "L'entreprise utilise-t-elle bien ses actifs ?",
    intro: "L'efficacité opérationnelle mesure la vitesse à laquelle l'entreprise transforme ses ressources (actifs, créances, stocks) en revenus. Le Cycle de Conversion du Cash (CCC) est le baromètre synthétique : plus il est court, moins l'entreprise immobilise de capital dans son exploitation.",
    questions: [
      {
        q: "Les actifs génèrent-ils suffisamment de CA ?",
        ratios: ["Rotation actifs fixes"],
        seuil: "> 3× (industriel performant) · sectoriel (très variable)",
        logique: "La rotation des actifs immobilisés mesure le CA généré par euro d'actifs fixes. Elle varie fortement selon le modèle : un éditeur de logiciels a une rotation très élevée (actifs légers), un aciériste très faible (actifs lourds). Comparer au secteur est indispensable.",
      },
      {
        q: "Les clients paient-ils rapidement ?",
        ratios: ["DSO (Délai clients)", "Rotation clients"],
        seuil: "DSO < 45j (idéal B2B) · < 30j (retail) · > 90j (signal négatif)",
        logique: "Le DSO mesure le nombre de jours de CA immobilisés en créances clients. Un DSO long signifie que l'entreprise finance ses clients — risque de BFR élevé. Un DSO qui s'allonge peut signaler des difficultés de recouvrement ou une politique commerciale de facilité.",
      },
      {
        q: "Le cycle d'exploitation est-il maîtrisé ?",
        ratios: ["CCC (Cycle de conversion)"],
        seuil: "< 30j (excellent) · 30–60j (correct) · > 90j (BFR élevé)",
        logique: "CCC = DSO + DIO − DPO. Il mesure le délai entre le décaissement pour produire et l'encaissement de la vente. Un CCC négatif (cas Amazon, grandes distributions) signifie que les clients paient avant que l'entreprise règle ses fournisseurs — avantage compétitif majeur.",
      },
    ],
    conclusion_analytique: "L'efficacité opérationnelle est souvent sous-estimée. Une amélioration du CCC de 30 jours libère typiquement 8–10% du CA en cash — sans augmenter le bénéfice comptable. C'est pour cela que l'efficacité crée de la valeur réelle même quand les marges stagnent.",
  },

  growth: {
    ordre: 7,
    titre: "L'entreprise se développe-t-elle de façon rentable ?",
    intro: "La croissance n'a de valeur que si elle est rentable. Damodaran : 'La croissance crée de la valeur uniquement si le ROIC sur les nouveaux investissements excède le coût du capital.' Une croissance du CA sans ROI > WACC détruit de la valeur économique.",
    questions: [
      {
        q: "Le chiffre d'affaires progresse-t-il de façon soutenue ?",
        ratios: ["Croissance CA"],
        seuil: "> 10% (forte) · 5–10% (modérée) · 0–5% (faible) · < 0% (déclin)",
        logique: "La croissance du CA est le premier indicateur de l'expansion commerciale. Mais attention : une croissance obtenue par acquisition ou par réduction des marges n'a pas la même qualité qu'une croissance organique. Croiser avec la marge brute pour détecter une dégradation du mix.",
      },
      {
        q: "La croissance du bénéfice dépasse-t-elle celle du CA ?",
        ratios: ["Croissance résultat net", "Croissance EPS"],
        seuil: "Croissance RN > Croissance CA = levier opérationnel positif",
        logique: "Si le résultat net croît plus vite que le CA, les coûts fixes sont absorbés (levier opérationnel). L'EPS intègre en plus l'effet des rachats d'actions — un EPS en croissance avec un résultat net stable peut signaler une réduction du flottant.",
      },
      {
        q: "La croissance du FCF confirme-t-elle la croissance comptable ?",
        ratios: ["Croissance FCF", "Croissance BVPS"],
        seuil: "Croissance FCF en ligne avec croissance RN = qualité de la croissance",
        logique: "Une divergence entre croissance du RN et croissance du FCF est un signal d'alerte : les bénéfices comptables peuvent croître via des accruals sans amélioration réelle des flux de trésorerie. Le BVPS mesure l'accumulation de patrimoine par action.",
      },
    ],
    conclusion_analytique: "La croissance soutenable (g = ROIC × taux de réinvestissement) est la borne supérieure naturelle. Une entreprise qui croît plus vite que sa croissance soutenable doit se financer par de la dette ou des émissions d'actions — ce qui peut diluer ou fragiliser.",
  },

  capalloc: {
    ordre: 8,
    titre: "Comment l'entreprise déploie-t-elle son cash ?",
    intro: "L'allocation du capital est le test final de la qualité du management. Graham (Security Analysis) : 'La rétention des bénéfices ne crée de valeur que si le rendement marginal excède le coût du capital.' Sinon, distribuer vaut mieux qu'investir.",
    questions: [
      {
        q: "Le management réinvestit-il dans des projets créateurs de valeur ?",
        ratios: ["Spread ROIC−WACC", "Taux de rétention"],
        seuil: "ROIC > WACC = rétention justifiée · ROIC < WACC = distribution préférable",
        logique: "Si ROIC > WACC, retenir les bénéfices et réinvestir crée de la valeur. Si ROIC < WACC, chaque euro réinvesti détruit de la valeur — le management devrait distribuer ou se désendetter plutôt que croître.",
      },
      {
        q: "Les capex renouvellent-ils le capital productif ?",
        ratios: ["Capex / D&A"],
        seuil: "< 1× = sous-investissement · 1× = renouvellement · > 1.5× = expansion",
        logique: "Un Capex/D&A < 1 signifie que l'entreprise investit moins qu'elle n'amortit — possible indice d'un modèle en déclin ou d'une entreprise qui préserve son cash. Un ratio > 2× indique une phase d'expansion active.",
      },
      {
        q: "La distribution aux actionnaires est-elle soutenable ?",
        ratios: ["Buyback yield", "Payout ratio"],
        seuil: "Buyback yield > 2% = retour significatif · Payout < FCF = soutenable",
        logique: "Les rachats d'actions et dividendes doivent être financés par le FCF réel, pas par de la dette. Un payout supérieur au FCF crée une 'fausse' générosité qui finira par contraindre l'entreprise.",
      },
    ],
    conclusion_analytique: "La hiérarchie d'allocation optimale (Damodaran) : (1) réinvestir si ROIC > WACC, (2) se désendetter si gearing élevé, (3) racheter des actions si prix < valeur intrinsèque, (4) verser des dividendes en dernier recours. Une entreprise qui fait l'inverse détruit de la valeur.",
  },

  value_creation: {
    ordre: 9,
    titre: "Crée-t-elle de la valeur économique réelle ?",
    intro: "La création de valeur économique est la synthèse finale : ROIC > WACC = l'entreprise génère plus que ce que les apporteurs de capital exigent. L'EVA (Economic Value Added) quantifie cette sur-rémunération en valeur absolue. Koller (McKinsey) : 'Les entreprises qui créent de la valeur à long terme surperforment systématiquement l'indice.'",
    questions: [
      {
        q: "Le ROIC dépasse-t-il le coût du capital ?",
        ratios: ["Spread ROIC−WACC", "ROIC"],
        seuil: "Spread > 5% (fort) · 0–5% (fragile) · < 0% (destruction de valeur)",
        logique: "Le spread ROIC−WACC est l'indicateur ultime de la qualité économique. Un spread positif et croissant est le fondement d'une valorisation premium. Un spread négatif signifie que chaque euro de croissance supplémentaire détruit de la valeur — la croissance devient un fardeau.",
      },
      {
        q: "L'EVA est-elle positive et en amélioration ?",
        ratios: ["EVA absolue (NOPAT − coût capital × IC)"],
        seuil: "EVA > 0 = création · EVA < 0 = destruction",
        logique: "L'EVA = NOPAT − (WACC × Capital Investi). Une EVA positive signifie que l'entreprise génère plus que son coût du capital en valeur absolue. La somme des EVA futures actualisées est la valeur ajoutée par la croissance dans un modèle DCF.",
      },
      {
        q: "La croissance soutenable est-elle cohérente avec l'ambition stratégique ?",
        ratios: ["Croissance soutenable (g)", "ROCE"],
        seuil: "g soutenable > ambition affichée = financement organique suffisant",
        logique: "g = ROIC × Taux de réinvestissement. Si l'entreprise vise 10% de croissance mais que g soutenable est 4%, elle devra lever des capitaux ou de la dette. La cohérence entre ambition et capacité de financement organique est un test de crédibilité du management.",
      },
    ],
    conclusion_analytique: "La création de valeur est la convergence de tous les piliers. Une entreprise peut être liquide, solvable et profitable, mais si ROIC < WACC de façon persistante, elle ne crée pas de valeur pour ses actionnaires. C'est le test fondamental avant toute décision d'investissement.",
  },
};

// Ordre d'analyse logique pour les tabs
const ANALYSIS_ORDER = ['solvency','liquidity','profitability','rentability','cashflow','efficiency','growth','capalloc','value_creation'];

// Piliers — définition complète avec ratios FR, descriptions, métadonnées
const PILLARS = [
  {
    key:"liquidity", label:"Liquidité", abbr:"LIQ", icon:"◉", weight:4.5, color:C.edu,
    scoreKey:"liquidity_score",
    categoryDesc:"Mesure la capacité à honorer les obligations à court terme sans difficultés. Un coussin de liquidité solide protège l'entreprise des chocs de trésorerie imprévus.",
    weightDesc:"Pondération 4.5% — pilier de sécurité. Indique la capacité à absorber les chocs de trésorerie à court terme sans recourir aux marchés.",
    ratios:[
      { n:"Ratio de liquidité courante",  short:"Current",  col:"current_ratio",      fmt:"x",  hi:true,  th:">1.5×", thN:1.5,
        desc:"Actifs CT / Passifs CT. Mesure la couverture globale des dettes court terme.",
        howto:"" },
      { n:"Ratio de liquidité restreinte", short:"Quick",    col:"quick_ratio",        fmt:"x",  hi:true,  th:">1×",   thN:1.0,
        desc:"(Actifs CT − Stocks) / Passifs CT. Exclut les stocks pour une mesure plus prudente.",
        howto:"" },
      { n:"Ratio de trésorerie immédiate", short:"Cash",     col:"cash_ratio",         fmt:"x",  hi:true,  th:">0.5×", thN:0.5,
        desc:"Trésorerie & équivalents / Passifs CT. Mesure la liquidité pure.",
        howto:"" },
    ]
  },
  {
    key:"profitability", label:"Profitabilité", abbr:"PROF", icon:"◆", weight:13.5, color:C.am,
    scoreKey:"profitability_score",
    categoryDesc:"Analyse les marges à chaque niveau du compte de résultat — de la marge brute à la marge nette. Révèle la structure de coûts et le pricing power de l'entreprise.",
    weightDesc:"Pondération 13.5% — pilier important. La profitabilité des marges conditionne la capacité à générer des bénéfices récurrents. Fortement pondéré pour refléter l'importance de la qualité des résultats.",
    ratios:[
      { n:"Marge brute",        short:"Gross Mg",   col:"gross_margin",       fmt:"%",  hi:true,  th:">40%",  thN:0.40,
        desc:"(CA − Coût des ventes) / CA. Mesure le pricing power après coûts directs.",
        howto:"" },
      { n:"Marge EBITDA",       short:"EBITDA Mg",  col:"ebitda_margin",      fmt:"%",  hi:true,  th:">15%",  thN:0.15,
        desc:"EBITDA / CA. Mesure la rentabilité opérationnelle avant D&A et charges financières.",
        howto:"" },
      { n:"Marge opérationnelle",short:"EBIT Mg",   col:"operating_margin",   fmt:"%",  hi:true,  th:">10%",  thN:0.10,
        desc:"EBIT / CA. Résultat opérationnel après D&A mais avant charges financières.",
        howto:"" },
      { n:"Marge nette",         short:"Net Mg",    col:"net_margin",         fmt:"%",  hi:true,  th:">8%",   thN:0.08,
        desc:"Résultat net / CA. Mesure la rentabilité finale après tous les postes.",
        howto:"" },
      { n:"Marge avant impôts",  short:"Pretax Mg", col:"pretax_margin",      fmt:"%",  hi:true,  th:">10%",  thN:0.10,
        desc:"Résultat avant impôts / CA. Signal de la qualité des résultats avant fiscalité.",
        howto:"" },
    ]
  },
  {
    key:"growth", label:"Croissance", abbr:"GRTH", icon:"▲", weight:18.0, color:C.g,
    scoreKey:"growth_score",
    categoryDesc:"Évalue la dynamique de développement de l'entreprise sur 5 ans — revenus, bénéfices, EPS et valeur patrimoniale par action. Croisé avec le ROIC pour valider que la croissance crée de la valeur.",
    weightDesc:"Pondération 18% — pilier majeur. La croissance soutenable est le moteur de la valeur actionnariale à long terme. Graham : la tendance bénéficiaire sur cycle prime sur le niveau absolu.",
    ratios:[
      { n:"Croissance du chiffre d'affaires", short:"Rev Grwth", col:"revenue_growth",     fmt:"%", hi:true,  th:">5%",  thN:0.05,
        desc:"Variation annuelle du CA. Mesure l'expansion commerciale.",
        howto:"" },
      { n:"Croissance du résultat net",       short:"NI Grwth",  col:"net_income_growth",  fmt:"%", hi:true,  th:">5%",  thN:0.05,
        desc:"Variation annuelle du bénéfice net. Révèle la dynamique de rentabilité.",
        howto:"" },
      { n:"Croissance BPA",                   short:"EPS Grwth", col:"eps_growth",          fmt:"%", hi:true,  th:">5%",  thN:0.05,
        desc:"Variation de l'EPS. Intègre l'effet des rachats d'actions sur la valeur par action.",
        howto:"" },
      { n:"Croissance valeur comptable/action",short:"BVPS",      col:"bvps_growth",         fmt:"%", hi:true,  th:">5%",  thN:0.05,
        desc:"Variation de la valeur comptable par action. Mesure l'accumulation de patrimoine.",
        howto:"" },
      { n:"Croissance du FCF",                short:"FCF Grwth", col:"fcf_growth",           fmt:"%", hi:true,  th:">5%",  thN:0.05,
        desc:"Variation du Free Cash Flow. Mesure l'amélioration de la génération de cash réel.",
        howto:"" },
    ]
  },
  {
    key:"efficiency", label:"Efficacité", abbr:"EFFC", icon:"⊞", weight:4.5, color:C.bl,
    scoreKey:"efficiency_score",
    categoryDesc:"Mesure la vitesse à laquelle l'entreprise transforme ses ressources en revenus. Le CCC (Cash Conversion Cycle) synthétise le besoin de financement du cycle d'exploitation.",
    weightDesc:"Pondération 4.5% — pilier opérationnel secondaire. Mesure la vitesse de transformation des ressources en revenus et la maîtrise du cycle client/fournisseur.",
    ratios:[
      { n:"Rotation des actifs immobilisés",  short:"Fixed AT",   col:"fixed_asset_turnover",  fmt:"x", hi:true,  th:">3×",  thN:3,
        desc:"CA / Actifs fixes nets. Mesure l'utilisation des immobilisations.",
        howto:"" },
      { n:"Rotation des comptes clients",     short:"Recv. TO",   col:"receivables_turnover",  fmt:"x", hi:true,  th:">6×",  thN:6,
        desc:"CA / Créances clients. Mesure la vitesse d'encaissement.",
        howto:"" },
      { n:"Délai de paiement clients (DSO)",  short:"DSO",        col:"dso",                   fmt:"j", hi:false, th:"<45j", thN:45,
        desc:"Jours de CA immobilisés en créances clients. Mesure le délai moyen de recouvrement.",
        howto:"" },
      { n:"Délai de stockage (DIO)",          short:"DIO",        col:"dio",                   fmt:"j", hi:false, th:"<40j", thN:40,
        desc:"Jours de ventes immobilisés en stocks. Mesure la vitesse d'écoulement.",
        howto:"" },
      { n:"Cycle de conversion du cash (CCC)",short:"CCC",        col:"cash_conversion_cycle", fmt:"j", hi:false, th:"<45j", thN:45,
        desc:"DSO + DIO − DPO. Durée pendant laquelle le capital est immobilisé dans le cycle d'exploitation.",
        howto:"" },
    ]
  },
  {
    key:"rentability", label:"Rentabilité", abbr:"RENT", icon:"◈", weight:22.5, color:C.g2,
    scoreKey:"rentability_score",
    categoryDesc:"Mesure l'efficience avec laquelle l'entreprise génère du profit sur les ressources engagées (CP, actifs totaux, capital investi). C'est le pilier le plus pondéré car il conditionne la capacité de croissance endogène.",
    weightDesc:"Pondération 22.5% — le pilier majeur. Damodaran : 'La rentabilité du capital investi est la mesure fondamentale de la création de valeur.' ROE, ROA et ROIC mesurent respectivement la performance pour l'actionnaire, l'ensemble des ressources, et le capital productif.",
    ratios:[
      { n:"Rentabilité des capitaux propres (ROE)", short:"ROE", col:"roe", fmt:"%", hi:true, th:">12%", thN:0.12,
        desc:"Résultat net / FP moyens. Performance pour l'actionnaire. Décomposable via DuPont en marge × rotation × levier.",
        howto:"" },
      { n:"Rentabilité des actifs (ROA)",          short:"ROA", col:"roa", fmt:"%", hi:true, th:">6%",  thN:0.06,
        desc:"Résultat net / Total actifs. Performance indépendante de la structure de financement.",
        howto:"" },
      { n:"Rentabilité du capital investi (ROIC)",  short:"ROIC", col:"roic", fmt:"%", hi:true, th:">8%", thN:0.08,
        desc:"NOPAT / Capital investi. Rentabilité purement opérationnelle, indépendante du financement.",
        howto:"" },
      { n:"Rentabilité des actifs tangibles (ROTA)",short:"ROTA", col:"rota", fmt:"%", hi:true, th:">5%", thN:0.05,
        desc:"Résultat net / Actifs tangibles. Élimine l'effet goodwill des acquisitions.",
        howto:"" },
      { n:"Capacité d'autofinancement / CA",        short:"CAF/CA", col:"opcf_to_sales", fmt:"%", hi:true, th:">5%", thN:0.05,
        desc:"CFO / CA. Part du CA convertie en cash opérationnel. Mesure la qualité de génération de trésorerie.",
        howto:"" },
    ]
  },
  {
    key:"cashflow", label:"Cash Flow", abbr:"CFLW", icon:"◎", weight:18.0, color:C.ol,
    scoreKey:"cashflow_score",
    categoryDesc:"Évalue la qualité et la durabilité de la génération de trésorerie. Fridson & Alvarez : 'Le cash est la vérité comptable ultime — les bénéfices peuvent être manipulés, le cash ne ment pas.'",
    weightDesc:"Pondération 18% — pilier clé. La génération de FCF conditionne la capacité à rémunérer les actionnaires, investir et se désendetter sans recourir aux marchés. Graham : priorité au cash réel sur les bénéfices comptables.",
    ratios:[
      { n:"FCF par action",                 short:"FCF/sh",   col:"fcf_per_share",    fmt:"€",  hi:true,  th:`>0`,   thN:0,
        desc:"Free Cash Flow / Nombre d'actions. Valeur du FCF généré par action détenue.",
        howto:"" },
      { n:"FCF / Chiffre d'affaires",        short:"FCF/CA",   col:"fcf_to_sales",    fmt:"%",  hi:true,  th:">8%",   thN:0.08,
        desc:"FCF / CA. Conversion du CA en cash libre. Mesure l'efficience de conversion.",
        howto:"" },
      { n:"OCF / Chiffre d'affaires",        short:"OCF/CA",   col:"opcf_to_sales",   fmt:"%",  hi:true,  th:">10%",  thN:0.10,
        desc:"Cash Flows Opérationnels / CA. Conversion directe de l'activité en cash.",
        howto:"" },
      { n:"Qualité des bénéfices (IQ)",      short:"IQ",       col:"income_quality",  fmt:"x",  hi:true,  th:">0.8×", thN:0.8,
        desc:"CFO / Résultat net. Mesure la proportion du bénéfice convertie en cash réel.",
        howto:"" },
      { n:"FCF / Cash Flow Opérationnel",    short:"FCF/OCF",  col:"fcf_to_opcf",     fmt:"x",  hi:true,  th:">1×",   thN:1.0,
        desc:"FCF / CFO. Part du cash opérationnel disponible après investissements.",
        howto:"" },
    ]
  },
  {
    key:"solvency", label:"Solvabilité", abbr:"SOLV", icon:"▣", weight:9.0, color:C.or,
    scoreKey:"solvency_score",
    categoryDesc:"Évalue la solidité financière à long terme : structure du bilan, niveau d'endettement, capacité à couvrir les charges financières. Fabozzi : classification DN/EBITDA en 4 zones de risque.",
    weightDesc:"Pondération 9% — pilier de risque structurel. Une solvabilité dégradée peut contraindre la stratégie et déclencher des clauses de remboursement anticipé. Clé pour évaluer la résistance aux chocs financiers.",
    ratios:[
      { n:"Dette nette / EBITDA",           short:"DN/EBITDA", col:"net_debt_to_ebitda",  fmt:"x",  hi:false, th:"<3×",  thN:3,
        desc:"Endettement net / EBITDA. Mesure le nombre d'années d'EBITDA nécessaires pour rembourser la dette nette.",
        howto:"" },
      { n:"Couverture des intérêts (ICR)",  short:"ICR",       col:"interest_coverage",   fmt:"x",  hi:true,  th:">3×",  thN:3,
        desc:"EBIT / Charges d'intérêts. Mesure la capacité à honorer les intérêts de la dette.",
        howto:"" },
      { n:"Gearing (levier)",               short:"Gearing",   col:"gearing_ratio",       fmt:"x",  hi:false, th:"<1×",  thN:1,
        desc:"Dette financière nette / FP. Mesure le levier financier de l'entreprise.",
        howto:"" },
      { n:"Dette / Actifs totaux",           short:"D/A",       col:"debt_to_assets",      fmt:"%",  hi:false, th:"<50%", thN:0.5,
        desc:"Dettes financières / Total actifs. Part des actifs financée par la dette.",
        howto:"" },
      { n:"Autonomie financière",            short:"Autonomie", col:"financial_autonomy",  fmt:"%",  hi:true,  th:">50%", thN:0.5,
        desc:"FP / Total actifs. Part des actifs financée par les fonds propres.",
        howto:"" },
    ]
  },
  {
    key:"capalloc", label:"Alloc. Capital", abbr:"CAPX", icon:"⊕", weight:10.0, color:C.r,
    scoreKey:"capalloc_score",
    categoryDesc:"Évalue comment l'entreprise déploie son capital entre réinvestissement, dividendes, rachats et désendettement. Graham : 'La rétention ne crée de valeur que si le rendement marginal du capital dépasse son coût.'",
    weightDesc:"Pondération 10% — pilier stratégique. L'allocation du capital est le test final de la qualité du management. Un ROIC > WACC justifie la rétention ; un ROIC < WACC appelle à davantage de distribution.",
    ratios:[
      { n:"Taux de rétention des bénéfices",   short:"Rétention",   col:"retention_rate",        fmt:"%",  hi:true,  th:">50%", thN:0.5,
        desc:"1 − Taux de distribution. Part des bénéfices réinvestis dans l'entreprise.",
        howto:"" },
      { n:"Rendement des rachats d'actions",    short:"Buyback",     col:"buyback_yield",         fmt:"%",  hi:true,  th:">1%",  thN:0.01,
        desc:"Montant des rachats / Market Cap. Retour direct aux actionnaires via réduction du flottant.",
        howto:"" },
      { n:"Capex / Dotations aux amortissements",short:"Capex/D&A",  col:"capex_to_depreciation", fmt:"x",  hi:false, th:"<1×",  thN:1,
        desc:"Capex / D&A. Mesure si les investissements couvrent le renouvellement des actifs.",
        howto:"" },
      { n:"Spread ROIC − WACC",                 short:"ROIC-WACC",   col:"roic_vs_wacc",           fmt:"%",  hi:true,  th:">0%",  thN:0,
        desc:"ROIC − WACC. Mesure si l'entreprise crée ou détruit de la valeur économique.",
        howto:"" },
      { n:"Taux de réinvestissement",            short:"Reinvest.",   col:"reinvestment_rate",      fmt:"%",  hi:true,  th:">20%", thN:0.2,
        desc:"Part du NOPAT réinvestie dans la croissance organique.",
        howto:"" },
    ]
  },
  {
    key:"value_creation", label:"Création Valeur", abbr:"VALR", icon:"◈", weight:0, color:"#8B6FBF",
    scoreKey:"value_creation_score",
    categoryDesc:"Mesure si l'entreprise crée de la valeur économique au-delà du coût du capital. Koller (McKinsey) : seul un ROIC > WACC justifie la croissance. L'EVA quantifie la rente économique réelle.",
    weightDesc:"Pilier synthétique non pondéré dans le FScore — indicateur de second niveau qui recoupe ROIC, WACC et EVA pour évaluer la création de valeur économique nette.",
    ratios:[
      { n:"Spread ROIC − WACC",      short:"ROIC-WACC",  col:"roic_vs_wacc",           fmt:"%", hi:true,  th:">0%",  thN:0,
        desc:"ROIC − WACC. Mesure si l'entreprise crée ou détruit de la valeur économique à chaque euro déployé.",
        howto:"" },
      { n:"WACC",                     short:"WACC",       col:"wacc",                   fmt:"%", hi:false, th:"<10%", thN:0.10,
        desc:"Coût moyen pondéré du capital. Taux d'actualisation minimum exigé par les investisseurs.",
        howto:"" },
      { n:"Croissance soutenable",    short:"g Sustain.",  col:"sustainable_growth_rate", fmt:"%", hi:true,  th:">5%",  thN:0.05,
        desc:"ROIC × Taux de réinvestissement. Taux de croissance que l'entreprise peut financer sans recours aux marchés.",
        howto:"" },
      { n:"ROCE",                     short:"ROCE",       col:"roce",                   fmt:"%", hi:true,  th:">8%",  thN:0.08,
        desc:"Résultat opérationnel / Capital employé. Rentabilité du capital total (FP + dettes LT).",
        howto:"" },
    ]
  },
];

// ── ATOMS ────────────────────────────────────────────────────────────────────
function Tip({ title, body, src, children }) {
  const [vis, setVis] = useState(false);
  const [gpos, setGpos] = useState({ x:0, y:0 }); // global screen coords
  const isMob = typeof window !== "undefined" && window.innerWidth < 700;

  const track = useCallback(e => {
    setGpos({ x: e.clientX, y: e.clientY });
  }, []);

  // Mobile: bottom sheet on tap
  if (isMob) {
    return (
      <>
        <span style={{ display:"inline-flex", alignItems:"center" }}
          onClick={e => { e.stopPropagation(); setVis(v => !v); }}>
          {children}
        </span>
        {vis && (
          <div style={{ position:"fixed", inset:0, zIndex:9500 }} onClick={() => setVis(false)}>
            <div style={{ position:"fixed", bottom:0, left:0, right:0, zIndex:9600,
              background:"var(--tbg)", border:"1px solid var(--tbr)",
              borderRadius:"14px 14px 0 0", padding:"16px 18px 32px",
              boxShadow:"0 -10px 40px rgba(0,0,0,.28)" }}
              onClick={e => e.stopPropagation()}>
              <div style={{ width:36, height:3, background:"var(--sep)", borderRadius:4,
                margin:"0 auto 14px", opacity:.5 }}/>
              {title && <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, textTransform:"uppercase",
                letterSpacing:".11em", color:C.am, marginBottom:8, paddingBottom:6,
                borderBottom:"1px solid var(--sep)" }}>{title}</div>}
              <div style={{ fontSize:14, color:"var(--tx)", lineHeight:1.7 }}>{body}</div>
              {src && <div style={{ fontFamily:"ui-monospace,monospace", fontSize:11, color:"var(--mu)",
                marginTop:10, paddingTop:8, borderTop:"1px solid var(--sep)" }}>📖 {src}</div>}
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop: fixed-positioned tooltip that never overflows viewport
  const TIP_W = 280;
  const TIP_MAX_H = 220;
  const OFFSET = 14;
  const vw = typeof window !== "undefined" ? window.innerWidth  : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const left = gpos.x + OFFSET + TIP_W > vw ? gpos.x - TIP_W - OFFSET : gpos.x + OFFSET;
  const top  = gpos.y + OFFSET + TIP_MAX_H > vh ? gpos.y - TIP_MAX_H  : gpos.y + OFFSET;

  return (
    <span style={{ display:"inline-flex", alignItems:"center" }}
      onMouseEnter={e => { setVis(true); track(e); }}
      onMouseMove={track}
      onMouseLeave={() => setVis(false)}>
      {children}
      {vis && (
        <div style={{
          position:"fixed", left, top, zIndex:9999,
          width:TIP_W, background:"var(--tbg)", border:"1px solid var(--tbr)",
          borderRadius:10, padding:"10px 13px",
          boxShadow:"0 12px 36px rgba(0,0,0,.28)", backdropFilter:"blur(14px)",
          pointerEvents:"none", lineHeight:1,
        }}>
          {title && <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, textTransform:"uppercase",
            letterSpacing:".11em", color:C.am, marginBottom:5, paddingBottom:4,
            borderBottom:"1px solid var(--sep)" }}>{title}</div>}
          <div style={{ fontSize:12.5, color:"var(--tx)", lineHeight:1.62 }}>{body}</div>
          {src && <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, color:"var(--mu)",
            marginTop:6, paddingTop:5, borderTop:"1px solid var(--sep)" }}>📖 {src}</div>}
        </div>
      )}
    </span>
  );
}
function I() {
  return (
    <svg width="9" height="9" viewBox="0 0 12 12" fill="none" style={{ opacity:.35, marginLeft:3, flexShrink:0 }}>
      <circle cx="6" cy="6" r="5.4" stroke="currentColor" strokeWidth="1.1"/>
      <rect x="5.3" y="5" width="1.4" height="4.2" rx=".7" fill="currentColor"/>
      <rect x="5.3" y="2.8" width="1.4" height="1.4" rx=".7" fill="currentColor"/>
    </svg>
  );
}

// Barre animée
function AnimBar({ pct, color, delay=0, h=3 }) {
  const [w, setW] = useState(0);
  useEffect(() => { const t = setTimeout(() => setW(pct), delay+80); return () => clearTimeout(t); }, [pct, delay]);
  return (
    <div style={{ width:"100%", height:h, background:"var(--sep)", borderRadius:999, overflow:"hidden", marginTop:3 }}>
      <div style={{ height:"100%", width:`${w}%`, background:color, borderRadius:999, transition:`width .75s cubic-bezier(.4,0,.2,1) ${delay}ms` }}/>
    </div>
  );
}

// Mini sparkline cliquable
function MiniSpark({ values, years, color, hi, onClick }) {
  const v = values.map((x,i) => ({ y:YEARS[i], v:x })).filter(d => d.v !== null && !isNaN(d.v));
  if (v.length < 2) return <span style={{ fontSize:10.5, color:"var(--mu)", cursor:"pointer" }} onClick={onClick}>—</span>;
  const mn = Math.min(...v.map(d=>d.v)), mx = Math.max(...v.map(d=>d.v));
  const W=64, H=26, pad=3;
  const x = i => pad + (i*(W-pad*2))/Math.max(1,v.length-1);
  const y = val => mx===mn ? H/2 : H - pad - ((val-mn)*(H-pad*2)/(mx-mn));
  const last = v[v.length-1].v, prev = v[v.length-2].v;
  const good = hi ? last >= prev : last <= prev;
  const col = good ? C.g2 : C.r;
  const pts = v.map((d,i) => `${x(i).toFixed(1)},${y(d.v).toFixed(1)}`).join(" ");
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display:"block", cursor:"pointer" }} onClick={onClick}>
      <polyline fill="none" stroke={col} strokeWidth="1.8" points={pts} strokeLinejoin="round" strokeLinecap="round"/>
      {v.map((d,i) => <circle key={i} cx={x(i).toFixed(1)} cy={y(d.v).toFixed(1)}
        r={i===v.length-1?3:1.8} fill={i===v.length-1?col:"var(--card)"} stroke={col} strokeWidth="1.5"/>)}
    </svg>
  );
}

// Jauge circulaire principale
function BigGauge({ score, size=104 }) {
  const [anim, setAnim] = useState(0);
  const sw = size*.068, R = size*.36, circ = 2*Math.PI*R, col = heatColor(score);
  useEffect(() => { const t = setTimeout(() => setAnim(score), 300); return () => clearTimeout(t); }, [score]);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {Array.from({length:24}).map((_,i) => {
        const a = (i/24)*2*Math.PI - Math.PI/2;
        const r1 = R+size*.055, r2 = r1+(i%6===0?5:3);
        return <line key={i}
          x1={(size/2+r1*Math.cos(a)).toFixed(1)} y1={(size/2+r1*Math.sin(a)).toFixed(1)}
          x2={(size/2+r2*Math.cos(a)).toFixed(1)} y2={(size/2+r2*Math.sin(a)).toFixed(1)}
          stroke="var(--mu)" strokeWidth={i%6===0?"1.2":".5"} opacity=".3"/>;
      })}
      <circle cx={size/2} cy={size/2} r={R} fill="none" stroke="rgba(128,128,128,.16)" strokeWidth={sw}/>
      <circle cx={size/2} cy={size/2} r={R} fill="none" stroke={col} strokeWidth={sw} strokeLinecap="round"
        strokeDasharray={`${(anim/100)*circ} ${circ}`} strokeDashoffset={circ*.25}
        style={{ transition:"stroke-dasharray 1.2s cubic-bezier(.4,0,.2,1)", filter:`drop-shadow(0 0 5px ${col}55)` }}/>
      <text x={size/2} y={size/2-2} textAnchor="middle"
        style={{ fontFamily:"ui-monospace,monospace", fontSize:size*.27, fontWeight:700, fill:"var(--ts)" }}>{Math.round(anim)}</text>
      <text x={size/2} y={size/2+size*.17} textAnchor="middle"
        style={{ fontFamily:"ui-monospace,monospace", fontSize:size*.077, fill:"var(--mu)", letterSpacing:".1em" }}>FSCORE</text>
    </svg>
  );
}

function MiniArc({ score, size=36 }) {
  const [anim, setAnim] = useState(0);
  const sw=3, R=(size-sw*2)/2, circ=2*Math.PI*R, col=heatColor(score);
  useEffect(() => { const t = setTimeout(() => setAnim(score), 300); return () => clearTimeout(t); }, [score]);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink:0 }}>
      <circle cx={size/2} cy={size/2} r={R} fill="none" stroke="rgba(128,128,128,.16)" strokeWidth={sw}/>
      <circle cx={size/2} cy={size/2} r={R} fill="none" stroke={col} strokeWidth={sw} strokeLinecap="round"
        strokeDasharray={`${(anim/100)*circ} ${circ}`} strokeDashoffset={circ*.25}
        style={{ transition:"stroke-dasharray .9s cubic-bezier(.4,0,.2,1)" }}/>
      <text x={size/2} y={size/2+size*.14} textAnchor="middle"
        style={{ fontFamily:"ui-monospace,monospace", fontSize:size*.26, fontWeight:700, fill:col }}>{Math.round(anim)}</text>
    </svg>
  );
}

// ── RADAR 8 PILIERS ─────────────────────────────────────────────────────────
function RadarChart({ size=210 }) {
  const [hov, setHov] = useState(-1);
  const [tipPos, setTipPos] = useState({ x:0, y:0 });
  const cx=size/2, cy=size/2, R=size*.33, n=PILLARS.length;
  const angle = i => (i/n)*2*Math.PI - Math.PI/2;
  const pt = (i, r) => ({ x: cx+r*Math.cos(angle(i)), y: cy+r*Math.sin(angle(i)) });
  const scores = PILLARS.map(p => { const sc=RAW[p.scoreKey]; return sc?.[YN]??0; });
  const pts = scores.map((s,i) => pt(i, R*(s/100)));
  const poly = pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  return (
    <div style={{ position:"relative", display:"inline-block" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow:"visible" }}>
        {[.25,.5,.75,1].map(f=>(
          <polygon key={f}
            points={PILLARS.map((_,i)=>{const p=pt(i,R*f);return`${p.x.toFixed(1)},${p.y.toFixed(1)}`;}).join(" ")}
            fill={f===1?"rgba(111,138,91,.04)":"none"}
            stroke={f===1?"var(--bdr)":"var(--sep)"} strokeWidth={f===1?"1":".4"} strokeDasharray={f<1?"3 3":"none"}/>
        ))}
        {PILLARS.map((_,i)=>{const p=pt(i,R);return<line key={i} x1={cx} y1={cy} x2={p.x.toFixed(1)} y2={p.y.toFixed(1)} stroke="var(--sep)" strokeWidth=".6"/>;} )}
        <polygon points={poly} fill={`${C.g}22`} stroke={C.g2} strokeWidth="2"/>
        {pts.map((p,i)=>(
          <circle key={i} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r={hov===i?6:3.8}
            fill={heatColor(scores[i])} stroke="var(--card)" strokeWidth="1.8" style={{ cursor:"pointer", transition:"r .12s" }}
            onMouseEnter={e=>{setHov(i);setTipPos({x:e.clientX,y:e.clientY});}}
            onMouseMove={e=>setTipPos({x:e.clientX,y:e.clientY})} onMouseLeave={()=>setHov(-1)}/>
        ))}
        {PILLARS.map((p,i)=>{
          const lp=pt(i,R*1.32), sc=scores[i], isH=hov===i;
          return(
            <g key={i} style={{ cursor:"pointer" }}
              onMouseEnter={e=>{setHov(i);setTipPos({x:e.clientX,y:e.clientY});}}
              onMouseMove={e=>setTipPos({x:e.clientX,y:e.clientY})} onMouseLeave={()=>setHov(-1)}>
              <text x={lp.x.toFixed(1)} y={(lp.y-4).toFixed(1)} textAnchor="middle"
                style={{ fontFamily:"ui-monospace,monospace", fontSize:isH?9.5:7.5,
                  fill:isH?heatColor(sc):"var(--mu)", fontWeight:isH?800:600, transition:"all .12s" }}>{p.abbr}</text>
              <text x={lp.x.toFixed(1)} y={(lp.y+7).toFixed(1)} textAnchor="middle"
                style={{ fontFamily:"ui-monospace,monospace", fontSize:isH?9:6.5,
                  fill:heatColor(sc), fontWeight:700, opacity:isH?1:.7, transition:"all .12s" }}>{sc?Math.round(sc):"—"}</text>
            </g>
          );
        })}
      </svg>
      {hov>=0&&(
        <div style={{ position:"fixed", left:tipPos.x+14, top:tipPos.y+12, zIndex:9999,
          width:240, background:"var(--tbg)", border:"1px solid var(--tbr)", borderRadius:10,
          padding:"9px 12px", boxShadow:"0 10px 28px rgba(0,0,0,.26)", backdropFilter:"blur(14px)", pointerEvents:"none" }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:5 }}>
            <span style={{ fontSize:10.5, color:PILLARS[hov].color }}>{PILLARS[hov].icon}</span>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:700, color:"var(--ts)" }}>{PILLARS[hov].label}</span>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:15, fontWeight:800, color:heatColor(scores[hov]??0), marginLeft:"auto" }}>{scores[hov]??0}</span>
          </div>
          <p style={{ fontSize:11.5, color:"var(--tx)", lineHeight:1.65, margin:0, fontStyle:"italic" }}>
            {PILLARS[hov].categoryDesc}
          </p>
        </div>
      )}
    </div>
  );
}

// ── TREND MODAL (identique EtatsFinanciersCard) ──────────────────────────────
function TrendModal({ ratio, onClose }) {
  const vals = RAW[ratio.col] || [];
  const data = YEARS.map((y,i) => ({ year:y, v:vals[i] })).filter(d => d.v !== null && !isNaN(d.v));
  const avg = data.reduce((a,d)=>a+d.v,0)/data.length;
  const mn = Math.min(...data.map(d=>d.v)), mx = Math.max(...data.map(d=>d.v));
  const W=600, H=200, PL=70, PR=30, PT=20, PB=40;
  const cW=W-PL-PR, cH=H-PT-PB;
  const xS = i => PL + (i/(data.length-1))*cW;
  const yS = v => mx===mn ? PT+cH/2 : PT+cH - ((v-mn)/(mx-mn))*cH;
  const [hov, setHov] = useState(null);

  const fmtV = v => {
    if (v===null||v===undefined) return "—";
    if (ratio.fmt==="%") return (v*100).toFixed(1)+"%";
    if (ratio.fmt==="x") return v.toFixed(2)+"×";
    if (ratio.fmt==="j") return Math.round(v)+"j";
    if (ratio.fmt==="€") return v.toFixed(2)+"€";
    return v.toFixed(2);
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const h = e => { if(e.key==="Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => { document.body.style.overflow=""; document.removeEventListener("keydown", h); };
  }, [onClose]);

  return (
    <div style={{ position:"fixed", inset:0, zIndex:8000, background:"rgba(0,0,0,.55)", backdropFilter:"blur(4px)",
      display:"flex", alignItems:"center", justifyContent:"center" }}
      onClick={e => { if(e.target===e.currentTarget) onClose(); }}>
      <div style={{ background:"var(--card)", border:"1px solid var(--bdr)", borderRadius:16,
        width:"min(660px,96vw)", overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,.28)" }}>
        {/* Header */}
        <div style={{ padding:"14px 18px 12px", borderBottom:"1px solid var(--sep)",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          background:`linear-gradient(90deg,${ratio.color||C.am}0e 0%,transparent 60%)` }}>
          <div>
            <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, color:"var(--mu)", textTransform:"uppercase",
              letterSpacing:".1em", marginBottom:3 }}>Tendance 5 ans · {ratio.short}</div>
            <div style={{ fontSize:17, fontWeight:700, color:"var(--ts)" }}>{ratio.n}</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, color:"var(--mu)" }}>2024</div>
              <div style={{ fontFamily:"ui-monospace,monospace", fontSize:19, fontWeight:800, color:ratio.color||C.am }}>
                {fmtV(RAW[ratio.col]?.[YN])}
              </div>
            </div>
            <button onClick={onClose} style={{ width:28, height:28, display:"flex", alignItems:"center",
              justifyContent:"center", background:"var(--sur)", border:"1px solid var(--bdr)",
              borderRadius:8, cursor:"pointer", fontSize:15, color:"var(--mu)" }}>×</button>
          </div>
        </div>
        {/* Chart */}
        <div style={{ padding:"12px 18px 6px" }}>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display:"block", cursor:"crosshair" }}
            onMouseLeave={() => setHov(null)}>
            {/* Grille */}
            {[0,.5,1].map(f => {
              const v = mn+(mx-mn)*f;
              return <g key={f}>
                <line x1={PL} y1={yS(v)} x2={W-PR} y2={yS(v)} stroke="var(--gl)" strokeWidth="1"/>
                <text x={PL-6} y={yS(v)+4} textAnchor="end" fontFamily="ui-monospace,monospace" fontSize="12" fill="var(--mu)">
                  {fmtV(v)}
                </text>
              </g>;
            })}
            {/* Ligne moyenne */}
            <line x1={PL} y1={yS(avg)} x2={W-PR} y2={yS(avg)} stroke={C.am} strokeWidth=".8" strokeDasharray="4,3" opacity=".6"/>
            <text x={W-PR+4} y={yS(avg)+4} fontFamily="ui-monospace,monospace" fontSize="11" fill={C.am} opacity=".8">moy</text>
            {/* Années */}
            {data.map((d,i) => (
              <text key={d.year} x={xS(i)} y={H-PB+18} textAnchor="middle" fontFamily="ui-monospace,monospace" fontSize="12" fill="var(--mu)">{d.year}</text>
            ))}
            {/* Ligne */}
            <polyline fill="none" stroke={ratio.color||C.g2} strokeWidth="2.2" strokeLinejoin="round"
              points={data.map((d,i)=>`${xS(i).toFixed(1)},${yS(d.v).toFixed(1)}`).join(" ")}/>
            {/* Aires */}
            <polygon opacity=".06" fill={ratio.color||C.g2}
              points={[xS(0)+','+yS(mn), ...data.map((d,i)=>xS(i).toFixed(1)+','+yS(d.v).toFixed(1)), xS(data.length-1)+','+yS(mn)].join(' ')}/>
            {/* Points */}
            {data.map((d,i) => {
              const isH = hov===i;
              return (
                <g key={i} onMouseEnter={() => setHov(i)} style={{ cursor:"crosshair" }}>
                  <circle cx={xS(i)} cy={yS(d.v)} r={isH?6:3.5} fill={ratio.color||C.g2}/>
                  {isH && <>
                    <line x1={xS(i)} y1={PT} x2={xS(i)} y2={H-PB} stroke="var(--mu)" strokeWidth=".8" strokeDasharray="3,3" opacity=".4"/>
                    <rect x={xS(i)>W*.7?xS(i)-130:xS(i)+10} y={PT+4} width={118} height={38} rx="6"
                      fill="var(--tbg)" stroke="var(--tbr)" strokeWidth="1" opacity=".97"/>
                    <text x={xS(i)>W*.7?xS(i)-122:xS(i)+18} y={PT+19} fontFamily="ui-monospace,monospace" fontSize="12" fontWeight="700" fill={C.am}>{d.year}</text>
                    <text x={xS(i)>W*.7?xS(i)-122:xS(i)+18} y={PT+35} fontFamily="ui-monospace,monospace" fontSize="13" fontWeight="800" fill={ratio.color||C.g2}>{fmtV(d.v)}</text>
                  </>}
                </g>
              );
            })}
            {/* Seuil */}
            {ratio.thN !== undefined && (() => {
              const tv = ratio.fmt==="%"?ratio.thN:(ratio.fmt==="x"||ratio.fmt==="j")?ratio.thN:ratio.thN;
              const tv2 = ratio.fmt==="%"?tv:tv;
              if(tv2>=mn&&tv2<=mx){
                return <>
                  <line x1={PL} y1={yS(tv2)} x2={W-PR} y2={yS(tv2)} stroke={C.r} strokeWidth=".9" strokeDasharray="5,3" opacity=".5"/>
                  <text x={W-PR+4} y={yS(tv2)+4} fontFamily="ui-monospace,monospace" fontSize="10" fill={C.r} opacity=".7">seuil</text>
                </>;
              }
              return null;
            })()}
          </svg>
        </div>
        {/* Interprétation */}
        <div style={{ padding:"0 18px 14px" }}>
          <div style={{ background:"var(--sur)", border:"1px solid var(--sep)", borderRadius:8, padding:"9px 12px" }}>
            <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, color:"var(--mu)", textTransform:"uppercase",
              letterSpacing:".1em", marginBottom:4 }}>Comment lire cette série</div>
            <p style={{ fontSize:12.5, color:"var(--tx)", lineHeight:1.7, margin:0 }}>{ratio.howto}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── COMPASS MODAL — Guide d'analyse fondamentale ─────────────────────────────
function CompassModal({ onClose, dark, currentPillarKey }) {
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [highlightedPillar] = useState(currentPillarKey || null);

  const guide  = selectedPillar ? PILLAR_GUIDE[selectedPillar] : null;
  const pillar = selectedPillar ? PILLARS.find(p => p.key === selectedPillar) : null;
  const orderedPillars = ANALYSIS_ORDER.map(k => PILLARS.find(p => p.key === k)).filter(Boolean);

  const selectPillar = key => setSelectedPillar(key);
  const goHome = () => setSelectedPillar(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const h = e => { if(e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", h); };
  }, [onClose]);

  const idx       = selectedPillar ? ANALYSIS_ORDER.indexOf(selectedPillar) : -1;
  const prevKey   = idx > 0 ? ANALYSIS_ORDER[idx - 1] : null;
  const nextKey   = idx < ANALYSIS_ORDER.length - 1 ? ANALYSIS_ORDER[idx + 1] : null;
  const prevP     = prevKey ? PILLARS.find(p => p.key === prevKey) : null;
  const nextP     = nextKey ? PILLARS.find(p => p.key === nextKey) : null;

  // ── Grille 3×3 des piliers ────────────────────────────────────────────────
  const PillarGrid = () => (
    <div style={{
      display:"grid", gridTemplateColumns:"repeat(3,minmax(0,1fr))", gap:8,
      padding:"12px 14px",
    }}>
      {orderedPillars.map((p, i) => {
        const isActive     = selectedPillar === p.key;
        const isCurrent    = highlightedPillar === p.key;
        const ps = RAW[p.scoreKey]?.[YN] || 0;
        const hc = heatColor(ps);
        const ac = hc;   // couleur active = heatColor de la note, pas p.color
        return (
          <button key={p.key} onClick={() => selectPillar(p.key)} style={{
            display:"flex", flexDirection:"column", alignItems:"flex-start",
            padding:"8px 10px", gap:3,
            border:`1.5px solid ${isActive ? ac : isCurrent ? ac+'80' : hc+'40'}`,
            background: isActive ? `${ac}18` : isCurrent ? `${ac}09` : `${hc}08`,
            borderRadius:10, cursor:"pointer", transition:"all .15s",
            position:"relative",
            outline: isCurrent && !isActive ? `2px solid ${ac}40` : 'none',
            outlineOffset:1,
          }}>
            <div style={{
              position:"absolute", top:5, right:7,
              fontFamily:"ui-monospace,monospace", fontSize:9.5, fontWeight:800,
              color: isActive ? ac : "var(--mu)", opacity:.75,
            }}>{i + 1}</div>
            {isCurrent && !isActive && (
              <div style={{
                position:"absolute", top:5, left:8,
                fontFamily:"ui-monospace,monospace", fontSize:8.5, fontWeight:700,
                color:"var(--card)", background:ac,
                padding:"0px 4px", borderRadius:4,
              }}>actif</div>
            )}
            <span style={{ fontSize:15, color: isActive ? ac : hc, marginTop: isCurrent ? 10 : 0 }}>{p.icon}</span>
            <div style={{
              fontFamily:"ui-monospace,monospace", fontSize:"clamp(9.5px,1.8vw,10.5px)",
              fontWeight: isActive ? 700 : 600,
              color: isActive ? ac : "var(--tx)",
              lineHeight:1.2, marginTop:1,
            }}>{p.label}</div>
            <div style={{
              fontFamily:"ui-monospace,monospace", fontSize:9.5, fontWeight:700, color:hc,
              display:"flex", alignItems:"center", gap:3,
            }}>
              <span>{Math.round(ps)}</span>
              <span style={{ opacity:.7 }}>· {scoreLabel(ps)}</span>
            </div>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="compass-overlay" style={{
      position:"fixed", inset:0, zIndex:9000, background:"rgba(0,0,0,.55)",
      backdropFilter:"blur(5px)", display:"flex", alignItems:"center", justifyContent:"center",
      padding:"clamp(9px,3vw,20px)",
    }} onClick={e => { if(e.target === e.currentTarget) onClose(); }}>
      <div className="compass-modal" style={{
        background:"var(--card)", border:"1px solid var(--bdr)",
        borderRadius:"clamp(11px,1.5vw,19px)",
        width:"min(960px,98vw)", maxHeight:"92dvh",
        display:"flex", flexDirection:"column",
        boxShadow:"0 24px 80px rgba(0,0,0,.3)",
        overflow:"hidden",
      }}>

        {/* ── HEADER ── */}
        <div style={{
          padding:"11px 16px", borderBottom:"1px solid var(--sep)",
          display:"flex", alignItems:"center", gap:10, flexShrink:0,
          background:`linear-gradient(90deg,${C.g}10 0%,transparent 60%)`,
        }}>
          {/* Bouton retour (visible seulement quand un pilier est sélectionné) */}
          {selectedPillar ? (
            <button onClick={goHome} style={{
              display:"flex", alignItems:"center", gap:5, padding:"5px 10px",
              background:"var(--sur)", border:"1px solid var(--bdr)",
              borderRadius:8, cursor:"pointer", flexShrink:0,
            }}>
              <span style={{ fontSize:13, color:"var(--mu)" }}>←</span>
              <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, color:"var(--mu)" }}>Piliers</span>
            </button>
          ) : (
            <div style={{
              width:32, height:32, borderRadius:8,
              background:`${C.g}18`, border:`1px solid ${C.g}28`,
              display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
            }}>
              🧭
            </div>
          )}

          <div style={{ flex:1, minWidth:0 }}>
            {selectedPillar && pillar ? (
              /* Header pilier actif */
              (() => {
                const ps = RAW[pillar.scoreKey]?.[YN] || 0;
                const hc = heatColor(ps);
                return (
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:800,
                      color:"var(--card)", background:hc,
                      padding:"2px 7px", borderRadius:4 }}>
                      {idx + 1} / 9
                    </span>
                    <span style={{ fontSize:12, color:hc }}>{pillar.icon}</span>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:"clamp(10.5px,1.5vw,13px)",
                      fontWeight:700, color:hc }}>{pillar.label}</span>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, color:"var(--mu)" }}>
                      · {pillar.weight}% du FScore · {Math.round(ps)} {scoreLabel(ps)}
                    </span>
                  </div>
                );
              })()
            ) : (
              /* Header accueil */
              <div>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:"clamp(10.5px,1.5vw,13px)",
                  fontWeight:700, color:"var(--ts)" }}>Guide d'analyse fondamentale</div>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10, color:"var(--mu)", marginTop:1 }}>
                  9 piliers · Ordre top-down · Filtre progressif
                </div>
              </div>
            )}
          </div>

          {/* Navigation pilier (prev/next) */}
          {selectedPillar && (
            <div style={{ display:"flex", gap:5, flexShrink:0 }}>
              <button onClick={() => prevP && selectPillar(prevKey)} disabled={!prevP} style={{
                width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center",
                background: prevP ? "var(--sur)" : "transparent",
                border:`1px solid ${prevP ? "var(--bdr)" : "transparent"}`,
                borderRadius:8, cursor: prevP ? "pointer" : "default",
                fontSize:13, color: prevP ? heatColor(RAW[prevP?.scoreKey]?.[YN]||0) : "var(--mu)",
                opacity: prevP ? 1 : .3,
              }}>←</button>
              <button onClick={() => nextP && selectPillar(nextKey)} disabled={!nextP} style={{
                width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center",
                background: nextP ? `${heatColor(RAW[nextP?.scoreKey]?.[YN]||0)}15` : "transparent",
                border:`1px solid ${nextP ? heatColor(RAW[nextP?.scoreKey]?.[YN]||0)+"40" : "transparent"}`,
                borderRadius:8, cursor: nextP ? "pointer" : "default",
                fontSize:13, color: nextP ? heatColor(RAW[nextP?.scoreKey]?.[YN]||0) : "var(--mu)",
                opacity: nextP ? 1 : .3,
              }}>→</button>
            </div>
          )}

          <button onClick={onClose} style={{
            width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center",
            background:"var(--sur)", border:"1px solid var(--bdr)",
            borderRadius:8, cursor:"pointer", fontSize:15, color:"var(--mu)", flexShrink:0,
          }}>×</button>
        </div>

          {/* Barre de séquence desktop */}
          <div className="compass-seqbar" style={{
            display:"flex", alignItems:"center", gap:0,
            borderBottom:"1px solid var(--sep)", flexShrink:0,
            overflowX:"auto", padding:"0 4px",
            background:"var(--sur)",
          }}>
            {orderedPillars.map((p, i) => {
              const isActive = selectedPillar === p.key;
              const ps = RAW[p.scoreKey]?.[YN] || 0;
              const hc = heatColor(ps);
              return (
                <button key={p.key} onClick={() => selectPillar(p.key)} style={{
                  display:"flex", flexDirection:"column", alignItems:"center",
                  padding:"5px 8px", gap:2, border:"none", cursor:"pointer",
                  borderBottom: isActive ? `2px solid ${hc}` : "2px solid transparent",
                  background: isActive ? `${hc}12` : "transparent",
                  transition:"all .12s", flexShrink:0,
                }}>
                  <div style={{ display:"flex", alignItems:"center", gap:3 }}>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, color: isActive ? hc : "var(--mu)", fontWeight:700 }}>{i+1}</span>
                    <span style={{ fontSize:10.5 }}>{p.icon}</span>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:"clamp(9.5px,1vw,10.5px)",
                      color: isActive ? hc : "var(--tx)", fontWeight: isActive ? 700 : 500, whiteSpace:"nowrap",
                    }}>{p.label}</span>
                  </div>
                  <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9, color:hc, fontWeight:700 }}>{Math.round(ps)}</div>
                </button>
              );
            })}
          </div>

        {/* ── CORPS ── */}
        <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column", minHeight:0 }}>

          {/* Vue accueil — visible quand aucun pilier sélectionné */}
          {!selectedPillar && (
            <div style={{ flex:1, overflowY:"auto" }}>
              {/* Intro pédagogique */}
              <div style={{ padding:"12px 16px 8px", borderBottom:"1px solid var(--sep)",
                background:`${C.g}06` }}>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10, color:C.g, textTransform:"uppercase",
                  letterSpacing:".12em", marginBottom:6, fontWeight:700 }}>
                  Comment utiliser ce guide
                </div>
                <p style={{ fontSize:"clamp(11px,1.3vw,12.5px)", color:"var(--tx)", lineHeight:1.7, margin:0 }}>
                  L'analyse fondamentale suit une logique de <strong style={{color:"var(--ts)"}}>filtre progressif</strong> :
                  commencer par les questions de survie (solvabilité, liquidité), puis remonter vers la qualité
                  (profitabilité, rentabilité, cash), puis l'efficacité et la dynamique (croissance, allocation),
                  et conclure par la synthèse économique (création de valeur).
                </p>
              </div>
              {/* Grille 3×3 */}
              <PillarGrid/>
            </div>
          )}

          {/* Vue pilier — visible quand un pilier est sélectionné */}
          {selectedPillar && guide && pillar && (
            <div style={{ flex:1, overflowY:"auto", padding:"14px 16px" }}>

              {/* Question principale */}
              <div style={{ marginBottom:14, paddingBottom:12, borderBottom:"1px solid var(--sep)" }}>
                <div style={{ fontSize:"clamp(15px,2.5vw,19px)", fontWeight:700,
                  color:"var(--ts)", lineHeight:1.25, marginBottom:8 }}>
                  {guide.titre}
                </div>
                <p style={{ fontSize:"clamp(11px,1.3vw,12.5px)", color:"var(--tx)", lineHeight:1.7, margin:0 }}>
                  {guide.intro}
                </p>
              </div>

              {/* Questions analytiques */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700, color:C.am,
                  textTransform:"uppercase", letterSpacing:".12em", marginBottom:8 }}>
                  Questions à se poser
                </div>

                {/* Mapping ratio label → {col, fmt, thN, hi} pour colorer le chip */}
                {(() => {
                  const ps = RAW[pillar.scoreKey]?.[YN] || 0;
                  const hc = heatColor(ps);

                  const RATIO_MAP = {
                    'DN/EBITDA':         {col:'net_debt_to_ebitda',  thN:3,    hi:false},
                    'Gearing':           {col:'gearing_ratio',        thN:1,    hi:false},
                    'ICR (Couverture des intérêts)':{col:'interest_coverage',thN:3,hi:true},
                    'ICR':               {col:'interest_coverage',    thN:3,    hi:true},
                    'Autonomie financière':{col:'financial_autonomy', thN:0.5,  hi:true},
                    'Dette / Actifs (D/A)':{col:'debt_to_assets',     thN:0.5,  hi:false},
                    'Current Ratio':     {col:'current_ratio',        thN:1.5,  hi:true},
                    'Quick Ratio':       {col:'quick_ratio',          thN:1.0,  hi:true},
                    'Cash Ratio':        {col:'cash_ratio',           thN:0.5,  hi:true},
                    'Marge brute':       {col:'gross_margin',         thN:0.40, hi:true},
                    'EBITDA':            {col:'ebitda_margin',        thN:0.15, hi:true},
                    'EBIT':              {col:'operating_margin',     thN:0.10, hi:true},
                    'Marge nette':       {col:'net_margin',           thN:0.08, hi:true},
                    'ROE':               {col:'roe',                  thN:0.12, hi:true},
                    'ROA':               {col:'roa',                  thN:0.06, hi:true},
                    'ROIC':              {col:'roic',                 thN:0.08, hi:true},
                    'ROCE':              {col:'roce',                 thN:0.08, hi:true},
                    'ROTA':              {col:'rota',                 thN:0.05, hi:true},
                    'FCF/CA':            {col:'fcf_to_sales',         thN:0.05, hi:true},
                    'FCF/sh':            {col:'fcf_per_share',        thN:0,    hi:true},
                    'IQ':                {col:'income_quality',       thN:0.8,  hi:true},
                    'DSO':               {col:'dso',                  thN:45,   hi:false},
                    'CCC':               {col:'cash_conversion_cycle',thN:30,   hi:false},
                    'Spread ROIC-WACC':  {col:'roic_vs_wacc',         thN:0,    hi:true},
                    'EVA':               {col:'eva_absolute',         thN:0,    hi:true},
                    'WACC':              {col:'wacc',                 thN:0.10, hi:false},
                    'g soutenable':      {col:'sustainable_growth_rate',thN:0.05,hi:true},
                    'DSCR':              {col:'debt_service_coverage', thN:1.5, hi:true},
                  };

                  const chipColor = (label) => {
                    const m = RATIO_MAP[label];
                    if (!m) return hc;
                    const v = RAW[m.col]?.[YN];
                    if (v == null || isNaN(v)) return C.am;
                    return m.hi ? (v >= m.thN ? C.gb : C.rb) : (v <= m.thN ? C.gb : C.rb);
                  };

                  return (
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {guide.questions.map((item, qi) => {
                        return (
                          <div key={qi} style={{
                            background:"var(--sur)", border:`1px solid ${hc}30`,
                            borderLeft:`3px solid ${hc}`,
                            borderRadius:"0 9px 9px 0", padding:"9px 12px",
                          }}>
                            <div style={{ display:"flex", alignItems:"flex-start", gap:7, marginBottom:7 }}>
                              <span style={{
                                fontFamily:"ui-monospace,monospace", fontSize:9.5, fontWeight:700,
                                color:"var(--card)", background:hc,
                                padding:"1px 5px", borderRadius:4, flexShrink:0, marginTop:1,
                              }}>Q{qi + 1}</span>
                              <span style={{ fontSize:"clamp(11px,1.4vw,13px)", fontWeight:700, color:"var(--ts)" }}>
                                {item.q}
                              </span>
                            </div>
                            <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap", marginBottom:6 }}>
                              <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, color:"var(--mu)",
                                textTransform:"uppercase" }}>Ratios :</span>
                              {item.ratios.map((r, ri) => {
                                const cc = chipColor(r);
                                return (
                                  <span key={ri} style={{
                                    fontFamily:"ui-monospace,monospace", fontSize:10.5, fontWeight:700,
                                    color:cc, background:`${cc}12`,
                                    border:`1px solid ${cc}35`, padding:"1px 7px", borderRadius:4,
                                  }}>{r}</span>
                                );
                              })}
                            </div>
                            <div style={{ background:`${C.am}09`, border:`1px solid ${C.am}20`,
                              borderRadius:4, padding:"4px 8px", marginBottom:6 }}>
                              <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, color:C.am,
                                textTransform:"uppercase", marginRight:5 }}>Seuils ·</span>
                              <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, color:"var(--tx)" }}>
                                {item.seuil}
                              </span>
                            </div>
                            <p style={{ fontSize:"clamp(10.5px,1.2vw,11.5px)", color:"var(--mu)",
                              lineHeight:1.65, margin:0, fontStyle:"italic" }}>
                              {item.logique}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Conclusion */}
              <div style={{ background:`${C.g}09`, border:`1px solid ${C.g}25`,
                borderRadius:8, padding:"10px 13px", marginBottom:12 }}>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700, color:C.g2,
                  textTransform:"uppercase", letterSpacing:".12em", marginBottom:5 }}>
                  ◈ Conclusion analytique
                </div>
                <p style={{ fontSize:"clamp(11px,1.3vw,12.5px)", color:"var(--tx)",
                  lineHeight:1.7, margin:0 }}>
                  {guide.conclusion_analytique}
                </p>
              </div>

              {/* Navigation bas de page */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                paddingTop:10, borderTop:"1px solid var(--sep)" }}>
                {prevP ? (
                  <button onClick={() => selectPillar(prevKey)} style={{
                    display:"flex", alignItems:"center", gap:6, padding:"7px 12px",
                    background:"var(--sur)", border:"1px solid var(--bdr)",
                    borderRadius:8, cursor:"pointer",
                  }}>
                    <span style={{ fontSize:12, color:"var(--mu)" }}>←</span>
                    <div style={{ textAlign:"left" }}>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, color:"var(--mu)" }}>
                        {ANALYSIS_ORDER.indexOf(prevKey) + 1} / 9
                      </div>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, color:heatColor(RAW[prevP.scoreKey]?.[YN]||0),
                        fontWeight:700 }}>{prevP.label}</div>
                    </div>
                  </button>
                ) : <div/>}
                {nextP && (
                  <button onClick={() => selectPillar(nextKey)} style={{
                    display:"flex", alignItems:"center", gap:6, padding:"7px 12px",
                    background:`${heatColor(RAW[nextP.scoreKey]?.[YN]||0)}10`,
                    border:`1px solid ${heatColor(RAW[nextP.scoreKey]?.[YN]||0)}30`,
                    borderRadius:8, cursor:"pointer",
                  }}>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, color:"var(--mu)" }}>
                        {ANALYSIS_ORDER.indexOf(nextKey) + 1} / 9
                      </div>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5,
                        color:heatColor(RAW[nextP.scoreKey]?.[YN]||0), fontWeight:700 }}>{nextP.label}</div>
                    </div>
                    <span style={{ fontSize:12, color:heatColor(RAW[nextP.scoreKey]?.[YN]||0) }}>→</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// ── FORMATEUR ────────────────────────────────────────────────────────────────
function fmtVal(v, fmt, dec) {
  if (v===null||v===undefined||isNaN(v)) return "—";
  const d = dec!==undefined ? dec : (fmt==="%"?1:fmt==="x"?2:fmt==="j"?0:fmt==="€"?2:2);
  if (fmt==="%") return (v*100).toFixed(d)+"%";
  if (fmt==="x") return v.toFixed(d)+"×";
  if (fmt==="j") return Math.round(v)+"j";
  if (fmt==="€") return v.toFixed(d)+getCurSym();
  return v.toFixed(d);
}

// ── SYNTHÈSE ──────────────────────────────────────────────────────────
function OverviewPanel({ onSelect, bp }) {
  const isSm = bp==="xs"||bp==="sm";
  const sc = RAW.fscore[YN]||0;
  const sorted = [...PILLARS].sort((a,b) => (RAW[b.scoreKey]?.[YN]||0)-(RAW[a.scoreKey]?.[YN]||0));
  const forces = sorted.slice(0,3);
  const vigilances = sorted.filter(p => (RAW[p.scoreKey]?.[YN]||0) < 65);
  const scColor = heatColor(sc);

  return (
    <div style={{ padding:"clamp(9px,1.5vw,15px)", display:"flex", flexDirection:"column", gap:0 }}>
      {(()=>{
        const yn          = YEARS[YN];
        const signal      = FUND_META.signal?.[yn]         || "HOLD";
        const conviction  = FUND_META.conviction?.[yn]     || "LOW";
        const bias        = FUND_META.bias?.[yn]           || "";
        const trend       = FUND_META.trend?.[yn]          || "";
        const narrative   = FUND_META.narrative?.[yn]      || "";
        const outlook     = FUND_META.outlook?.[yn]        || "";
        const riskFlag    = FUND_META.risk_flag?.[yn]      || "NONE";
        const keyDriver   = FUND_META.key_driver?.[yn]     || "";

        const SIGNAL_MAP = {
          BUY:  { label:"Accumuler",         color:C.g2, icon:"◉", action:"Le profil fondamental supporte une thèse d'accumulation." },
          HOLD: { label:"Surveiller",         color:C.am, icon:"◎", action:"Maintenir la position, surveiller les indicateurs clés." },
          WEAK: { label:"Éviter",             color:C.r,  icon:"○", action:"Le profil présente des risques structurels qui ne justifient pas l'entrée." },
          SELL: { label:"Alléger",            color:C.r,  icon:"○", action:"Les fondamentaux se dégradent, réduire l'exposition." },
        };
        const TREND_MAP  = { STABLE:"→ Stable", DETERIORATING:"↘ Détérioration", IMPROVING:"↗ Amélioration" };
        const CONV_MAP   = { HIGH:"Conviction forte", MEDIUM:"Conviction moyenne", LOW:"Conviction faible" };
        const BIAS_MAP   = { BALANCED:"Équilibré", CASHFLOW_RISK:"Risque cash-flow", VALUE_TRAP:"Value trap potentiel", GROWTH_AT_RISK:"Croissance à risque" };
        const RISK_MAP   = { NONE:null, CASHFLOW_RISK:"⚠ Risque cash-flow", INSOLVENCY:"⚠ Risque insolvabilité", VALUE_TRAP:"⚠ Value trap" };

        const sig      = SIGNAL_MAP[signal]  || SIGNAL_MAP.HOLD;
        const trdTxt   = TREND_MAP[trend]    || trend;
        const convTxt  = CONV_MAP[conviction]|| conviction;
        const biasTxt  = BIAS_MAP[bias]      || bias;
        const riskTxt  = RISK_MAP[riskFlag]  || null;
        const keyP     = PILLARS.find(p=>p.key===keyDriver);
        const spread   = RAW.eva_spread?.[YN];

        // Diagnostic par pilier — questions + réponses dynamiques
        const DIAG = {
          solvency: {
            q: "Peut-elle survivre à long terme ?",
            answer: () => {
              const icr = RAW.interest_coverage?.[YN];
              const aut = RAW.financial_autonomy?.[YN];
              if (icr != null && icr >= 5 && aut != null && aut >= 0.5)
                return { ok:true,  txt:"Oui — bilan solide, dette maîtrisée et intérêts largement couverts." };
              if (icr != null && icr < 1.5)
                return { ok:false, txt:"Non — les intérêts de la dette ne sont plus couverts par l'exploitation. Risque de défaut." };
              if (icr != null && icr < 3)
                return { ok:"mid", txt:"Fragile — la couverture des intérêts est insuffisante pour absorber un choc." };
              return { ok:"mid", txt:"Partiel — levier financier à surveiller, marge de sécurité limitée." };
            },
          },
          liquidity: {
            q: "Peut-elle payer ses dettes à court terme ?",
            answer: () => {
              const cr = RAW.current_ratio?.[YN];
              const cash = RAW.cash_ratio?.[YN];
              if (cr != null && cr >= 2)
                return { ok:true,  txt:"Oui — l'actif court terme couvre largement les dettes immédiates." };
              if (cr != null && cr < 1)
                return { ok:false, txt:"Non — l'actif disponible est insuffisant pour honorer les échéances court terme." };
              if (cash != null && cash < 0.05)
                return { ok:"mid", txt:"Avec tension — la trésorerie immédiate est quasi nulle, dépendance aux lignes de crédit." };
              return { ok:"mid", txt:"Correcte mais limitée — pas de marge de sécurité significative en cas de choc." };
            },
          },
          profitability: {
            q: "Génère-t-elle des marges suffisantes ?",
            answer: () => {
              const nm = RAW.net_margin?.[YN];
              const em = RAW.ebitda_margin?.[YN];
              if (nm != null && nm < 0)
                return { ok:false, txt:"Non — l'activité est déficitaire. Chaque euro de CA génère une perte nette." };
              if (nm != null && nm < 0.03)
                return { ok:false, txt:"À peine — les marges sont quasi nulles, aucun coussin face aux aléas." };
              if (em != null && em >= 0.15 && nm != null && nm >= 0.05)
                return { ok:true,  txt:"Oui — marges opérationnelles et nettes suffisantes pour autofinancer la croissance." };
              return { ok:"mid", txt:"Partiellement — la rentabilité opérationnelle existe mais reste sous pression." };
            },
          },
          rentability: {
            q: "Les ressources engagées sont-elles bien rémunérées ?",
            answer: () => {
              const roic = RAW.roic?.[YN];
              const wacc = RAW.wacc?.[YN];
              const sp   = roic != null && wacc != null ? roic - wacc : null;
              if (roic != null && roic < 0)
                return { ok:false, txt:"Non — le capital investi ne génère aucun retour. Chaque euro engagé détruit de la valeur." };
              if (sp != null && sp < 0)
                return { ok:false, txt:"Non — le rendement du capital est inférieur à son coût. L'investissement détruit de la valeur." };
              if (sp != null && sp > 0.03)
                return { ok:true,  txt:"Oui — le capital rapporte plus qu'il ne coûte. Création de valeur économique réelle." };
              return { ok:"mid", txt:"À peine — le rendement du capital couvre tout juste son coût, sans création de valeur nette." };
            },
          },
          cashflow: {
            q: "Le bénéfice se convertit-il en cash réel ?",
            answer: () => {
              const iq  = RAW.income_quality?.[YN];
              const fcf = RAW.fcf_to_sales?.[YN];
              if (fcf != null && fcf < 0)
                return { ok:false, txt:"Non — l'entreprise consomme du cash malgré un bénéfice apparent. Le profit comptable ne reflète pas la réalité." };
              if (iq != null && iq < 0.5)
                return { ok:false, txt:"Non — une grande partie du bénéfice est comptable, sans contrepartie en trésorerie réelle." };
              if (iq != null && iq >= 0.8 && fcf != null && fcf > 0)
                return { ok:true,  txt:"Oui — le bénéfice se traduit en cash disponible. La qualité des résultats est solide." };
              return { ok:"mid", txt:"Partiellement — conversion cash correcte mais incomplète, à surveiller sur la durée." };
            },
          },
          efficiency: {
            q: "Utilise-t-elle bien ses actifs opérationnels ?",
            answer: () => {
              const ccc = RAW.cash_conversion_cycle?.[YN];
              const dso = RAW.dso?.[YN];
              if (ccc != null && ccc < 30)
                return { ok:true,  txt:"Oui — le cycle d'exploitation est rapide, le cash rentre vite après la vente." };
              if (ccc != null && ccc > 200)
                return { ok:false, txt:"Non — le cycle opérationnel est très long. Le cash est immobilisé plusieurs mois avant de rentrer." };
              if (dso != null && dso > 150)
                return { ok:"mid", txt:"Lentement — les clients paient tardivement, ce qui pèse sur la trésorerie." };
              return { ok:"mid", txt:"Correcte — efficacité opérationnelle moyenne, marge de progression possible." };
            },
          },
          growth: {
            q: "Se développe-t-elle de façon rentable ?",
            answer: () => {
              const rg = RAW.revenue_growth?.[YN];
              const ng = RAW.net_income_growth?.[YN];
              const roic = RAW.roic?.[YN];
              const wacc = RAW.wacc?.[YN];
              if (rg != null && rg < 0)
                return { ok:false, txt:"Non — l'activité se contracte. La croissance est négative cette année." };
              if (rg != null && rg > 0.10 && roic != null && wacc != null && roic > wacc)
                return { ok:true,  txt:"Oui — la croissance est soutenue et rentable, elle crée de la valeur pour l'actionnaire." };
              if (ng != null && ng < 0 && rg != null && rg > 0)
                return { ok:"mid", txt:"Croissance du CA mais résultat en baisse — la rentabilité ne suit pas le volume." };
              return { ok:"mid", txt:"Modérée — développement positif mais insuffisant pour dégager un réel effet d'échelle." };
            },
          },
          capalloc: {
            q: "Déploie-t-elle bien son cash ?",
            answer: () => {
              const roic = RAW.roic?.[YN];
              const wacc = RAW.wacc?.[YN];
              const sp   = roic != null && wacc != null ? roic - wacc : null;
              const cd   = RAW.capex_to_depreciation?.[YN];
              if (sp != null && sp < 0)
                return { ok:false, txt:"Non — chaque euro réinvesti détruit de la valeur. Mieux vaudrait redistribuer aux actionnaires." };
              if (sp != null && sp > 0 && cd != null && cd >= 1)
                return { ok:true,  txt:"Oui — le capital est réinvesti dans des projets rentables qui créent de la valeur." };
              if (cd != null && cd < 0.3)
                return { ok:"mid", txt:"Sous-investissement — les actifs s'usent sans renouvellement suffisant." };
              return { ok:"mid", txt:"Partiellement — l'allocation est correcte mais sans création de valeur nette évidente." };
            },
          },
          value_creation: {
            q: "Crée-t-elle de la valeur économique réelle ?",
            answer: () => {
              const roic = RAW.roic?.[YN];
              const wacc = RAW.wacc?.[YN];
              const sp   = roic != null && wacc != null ? roic - wacc : null;
              const eva  = RAW.eva_absolute?.[YN];
              if (sp != null && sp > 0.05)
                return { ok:true,  txt:"Oui — l'entreprise génère plus que ce que le capital lui coûte. Valeur créée pour l'actionnaire." };
              if (sp != null && sp < 0)
                return { ok:false, txt:"Non — l'entreprise détruit de la valeur économique. Le rendement ne couvre pas le risque pris." };
              if (eva != null && eva > 0)
                return { ok:"mid", txt:"Marginalement — la création de valeur est positive mais insuffisante pour justifier le risque." };
              return { ok:"mid", txt:"Neutre — aucune création nette de valeur économique sur l'exercice." };
            },
          },
        };

        // ── Mapping clé pilier → colonnes diag_* dans RAW (Fundamental_Scores) ──
        const DIAG_DB_KEY = {
          liquidity:      "diag_liquidity",
          profitability:  "diag_profitability",
          growth:         "diag_growth",
          efficiency:     "diag_efficiency",
          rentability:    "diag_rentability",
          cashflow:       "diag_cashflow",
          solvency:       "diag_solvency",
          capalloc:       "diag_capalloc",
          value_creation: "diag_valuecreation",
        };

        // Piliers où answer() JS est plus fiable que le score agrégé DB
        // car ils ont un ratio éliminatoire que la moyenne masque :
        //   rentability   → ROIC < 0 est éliminatoire, ROE/ROA peuvent compenser
        //   value_creation→ EVA négative nécessite lecture directe du spread ROIC-WACC
        // NOTE solvency : retiré de cette liste car le SP calcule sur Calc_Avg_Ratios_5Y
        // (ICR moyen 5 ans). La DB est plus fiable que l'ICR spot qui peut être volatile
        // sur 1 an (ex. 2025 ICR = -13.3 mais moyenne 5 ans = positive → DB dit "Oui").
        const ALWAYS_USE_JS = new Set(["rentability", "value_creation"]);

        // Résolution : JS pour les piliers à ratio éliminatoire,
        //              DB pour les autres (avec fallback JS si DB vide)
        function resolveRes(k) {
          // Piliers éliminatoires → toujours JS sur ratios bruts
          if (ALWAYS_USE_JS.has(k)) {
            const diag = DIAG[k];
            return diag ? { ...diag.answer(), src:"js" } : null;
          }
          // Autres piliers → DB en priorité
          const dbKey = DIAG_DB_KEY[k];
          if (dbKey) {
            const ok  = RAW[`${dbKey}_ok`]?.[YN];
            const txt = RAW[`${dbKey}_txt`]?.[YN];
            if (ok !== undefined && txt) {
              return {
                ok:  ok === 1 ? true : ok === 0 ? false : "mid",
                txt: txt,
                src: "db",
              };
            }
          }
          // Fallback JS
          const diag = DIAG[k];
          return diag ? { ...diag.answer(), src:"js" } : null;
        }

        const allResults = ANALYSIS_ORDER.map(k => {
          const p = PILLARS.find(x => x.key === k);
          if (!p) return null;
          const diag = DIAG[k];
          const res  = resolveRes(k);
          return { p, diag, res, ps: RAW[p.scoreKey]?.[YN] || 0 };
        }).filter(Boolean);

        const nOk    = allResults.filter(r=>r.res?.ok===true).length;
        const nFail  = allResults.filter(r=>r.res?.ok===false).length;
        const nMid   = allResults.filter(r=>r.res?.ok==="mid").length;

        return (
          <div style={{ display:"flex", flexDirection:"column", gap:0 }}>

            {/* ══ BLOC DÉCISION ════════════════════════════════════════════════ */}
            <div style={{
              borderRadius:"12px 12px 0 0",
              background: sig.color+"0d",
              border:"1px solid "+sig.color+"28",
              borderBottom:"none",
              padding:"10px 14px",
            }}>
              {/* Ligne 1 : verdict + badges */}
              <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:8 }}>
                <span style={{ fontSize:20, color:sig.color }}>{sig.icon}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, color:"var(--mu)",
                    textTransform:"uppercase", letterSpacing:".12em", marginBottom:2 }}>
                    Décision · FY {yn}
                  </div>
                  <div style={{ fontSize:"clamp(16px,3vw,20px)", fontWeight:800,
                    color:sig.color, lineHeight:1.1 }}>{sig.label}</div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:4, alignItems:"flex-end" }}>
                  {/* Ligne unique : FScore + ROIC-WACC + chips contexte */}
                  <div style={{ display:"flex", gap:5, flexWrap:"wrap", justifyContent:"flex-end", alignItems:"center" }}>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
                      color:heatColor(sc), background:heatColor(sc)+"14",
                      border:"1px solid "+heatColor(sc)+"30",
                      padding:"2px 8px", borderRadius:4 }}>
                      FScore {sc.toFixed(1)}/100
                    </span>
                    {spread!=null && (
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
                        color:spread>=0?C.g2:C.r,
                        background:(spread>=0?C.g2:C.r)+"14",
                        border:"1px solid "+(spread>=0?C.g2:C.r)+"30",
                        padding:"2px 8px", borderRadius:4 }}>
                        ROIC−WACC {fmtVal(spread,"%")}
                      </span>
                    )}
                    {[trdTxt, convTxt, biasTxt].filter(Boolean).map((b,i)=>(
                      <span key={i} style={{ fontFamily:"ui-monospace,monospace", fontSize:10,
                        color:"var(--mu)", background:"var(--sur)",
                        border:"1px solid var(--sep)",
                        padding:"2px 7px", borderRadius:4, whiteSpace:"nowrap" }}>{b}</span>
                    ))}
                    {riskTxt && (
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
                        color:C.or, background:C.or+"10", border:"1px solid "+C.or+"28",
                        padding:"2px 7px", borderRadius:4 }}>{riskTxt}</span>
                    )}
                  </div>
                  {/* Compteurs verdict */}
                  <div style={{ display:"flex", gap:4 }}>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10,
                      color:C.r, background:C.r+"10", border:"1px solid "+C.r+"25",
                      padding:"1px 6px", borderRadius:4 }}>✗ {nFail}</span>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10,
                      color:C.am, background:C.am+"10", border:"1px solid "+C.am+"25",
                      padding:"1px 6px", borderRadius:4 }}>~ {nMid}</span>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10,
                      color:C.g2, background:C.g2+"10", border:"1px solid "+C.g2+"25",
                      padding:"1px 6px", borderRadius:4 }}>✓ {nOk}</span>
                  </div>
                </div>
              </div>

              {/* Ligne 2 : action */}
              <div style={{ display:"flex", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
                <p style={{ fontSize:"clamp(11px,1.4vw,13px)", color:"var(--tx)",
                  lineHeight:1.65, margin:0, flex:1, minWidth:160 }}>
                  {sig.action} {narrative}
                </p>
              </div>

              {/* Ligne 3 : pilier dominant + outlook */}
              {(keyP||outlook) && (
                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8,
                  paddingTop:8, borderTop:"1px solid var(--sep)", flexWrap:"wrap" }}>
                  {keyP && (
                    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, color:"var(--mu)",
                        textTransform:"uppercase", letterSpacing:".08em" }}>Pilier dominant ·</span>
                      <span style={{ fontSize:11, color:keyP.color }}>{keyP.icon}</span>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, fontWeight:700,
                        color:keyP.color }}>{keyP.label}</span>
                    </div>
                  )}
                  {outlook && (
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, color:C.am,
                      background:C.am+"0e", border:"1px solid "+C.am+"22",
                      padding:"2px 8px", borderRadius:4 }}>{outlook}</span>
                  )}
                </div>
              )}
            </div>

            {/* ══ DIAGNOSTIC PAR PILIER ════════════════════════════════════════ */}
            <div style={{
              border:"1px solid var(--sep)",
              borderRadius:"0 0 12px 12px",
              overflow:"hidden",
            }}>
              {/* Header */}
              <div style={{ padding:"7px 16px",
                background:"var(--sur)", borderBottom:"1px solid var(--sep)",
                display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, fontWeight:700,
                  color:"var(--mu)", textTransform:"uppercase", letterSpacing:".12em" }}>
                  Justification · 9 piliers · cliquer pour le détail
                </span>
              </div>

              {/* Grille 3×3 */}
              <div style={{
                display:"grid",
                gridTemplateColumns:"repeat(3,1fr)",
              }}>
                {allResults.map(({ p, diag, res, ps }, idx) => {
                  const okCol  = res?.ok===true ? C.g2 : res?.ok===false ? C.r : C.am;
                  const col    = idx % 3;
                  const row    = Math.floor(idx/3);
                  const totalRows = Math.ceil(allResults.length / 3);
                  const isLastRow = row === totalRows - 1;
                  const isLastCol = col === 2;
                  return (
                    <div key={p.key} onClick={()=>onSelect(p.key)}
                      style={{
                        display:"flex", alignItems:"stretch",
                        borderBottom: isLastRow ? "none" : "1px solid var(--sep)",
                        borderRight:  isLastCol ? "none" : "1px solid var(--sep)",
                        cursor:"pointer", transition:"background .12s",
                      }}
                      onMouseEnter={e=>{ e.currentTarget.style.background=okCol+"07"; }}
                      onMouseLeave={e=>{ e.currentTarget.style.background="transparent"; }}>

                      {/* Bande couleur verdict */}
                      <div style={{ width:3, flexShrink:0, background:okCol }}/>

                      {/* Contenu */}
                      <div style={{ flex:1, padding:"10px 12px", minWidth:0 }}>

                        {/* Ligne 1 : icône + label + score badge */}
                        <div style={{ display:"flex", alignItems:"center",
                          justifyContent:"space-between", gap:4, marginBottom:4 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:5, minWidth:0 }}>
                            <span style={{ fontSize:10.5, color:heatColor(ps), flexShrink:0 }}>{p.icon}</span>
                            <span style={{ fontSize:"clamp(11px,1.3vw,12px)", fontWeight:700,
                              color:"var(--ts)", lineHeight:1.2 }}>{p.label}</span>
                          </div>
                          {/* Score petit badge discret */}
                          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, fontWeight:800,
                            color:heatColor(ps), background:heatColor(ps)+"18",
                            border:`1px solid ${heatColor(ps)}30`,
                            padding:"1.5px 5.5px", borderRadius:4, flexShrink:0 }}>
                            {Math.round(ps)}
                          </span>
                        </div>

                        {/* Question — la vraie question analytique */}
                        <div style={{ fontFamily:"ui-monospace,monospace", fontSize:"clamp(9.5px,1vw,10px)",
                          color:"var(--mu)", fontStyle:"italic",
                          lineHeight:1.42, marginBottom:4 }}>
                          {diag?.q}
                        </div>

                        {/* Réponse — élément principal, colorée selon verdict */}
                        {res && (
                          <div style={{
                            fontSize:"clamp(11.5px,1.3vw,12.5px)", fontWeight:600,
                            color: res.ok===true ? C.g2 : res.ok===false ? C.r : C.am,
                            lineHeight:1.48,
                          }}>
                            {res.txt}
                          </div>
                        )}

                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        );
      })()}
    </div>
  );
}


// ── VUE SYNTHÈSE GLOBALE ─────────────────────────────────────────────────────
// ── VUE DÉTAIL PILIER ────────────────────────────────────────────────────────
function ScoreHistoryChart({ scoreKey, color, size=60 }) {
  const scores = (RAW[scoreKey] || []).map((v,i) => ({ y:YEARS[i], v }));
  const valid  = scores.filter(d => d.v !== null && !isNaN(d.v));
  if (valid.length < 2) return null;
  const W=size*1.8, H=size*.9, pad=4;
  const mn=Math.min(...valid.map(d=>d.v)), mx=Math.max(...valid.map(d=>d.v));
  const xp = i => pad + (i/(scores.length-1))*(W-pad*2);
  const yp = v => mx===mn ? H/2 : H - pad - ((v-mn)/(mx-mn))*(H-pad*2);
  const pts = scores.map((d,i) =>
    d.v !== null && !isNaN(d.v) ? `${xp(i).toFixed(1)},${yp(d.v).toFixed(1)}` : null
  ).filter(Boolean).join(' ');
  const last = valid[valid.length-1].v;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{display:'block',flexShrink:0}}>
      <polyline fill="none" stroke={color} strokeWidth="1.6" points={pts}
        strokeLinejoin="round" strokeLinecap="round" opacity=".8"/>
      {scores.map((d,i) => d.v !== null && !isNaN(d.v) ? (
        <circle key={i} cx={xp(i).toFixed(1)} cy={yp(d.v).toFixed(1)}
          r={i===scores.length-1?3:1.8}
          fill={i===scores.length-1?color:'var(--card)'} stroke={color} strokeWidth="1.5"/>
      ) : null)}
    </svg>
  );
}

function PillarView({ pillar, bp }) {
  const isMob = bp==='xs';
  const isSm  = bp==='xs'||bp==='sm';
  const ps    = RAW[pillar.scoreKey]?.[YN] || 0;
  const pc    = heatColor(ps);   // couleur alignée sur la note, pas sur le pilier
  const col   = pc;              // alias — utilisé dans scoreLabel et badges
  const qual  = scoreLabel(ps);
  const qualColor = s => s>=90?C.g2:s>=75?C.g:s>=55?C.am:s>=35?C.or:C.r;
  const [trendModal, setTrendModal] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const avg5 = key => {
    const vals = (RAW[key]||[]).filter(x=>x!==null&&!isNaN(x));
    return vals.length ? vals.reduce((a,b)=>a+b,0)/vals.length : null;
  };

  // Is ratio meeting threshold?
  const ratioOk = (r, val) => {
    if (val === null || val === undefined || isNaN(val)) return null;
    return r.hi ? val >= r.thN : val <= r.thN;
  };

  const ratioColor = (r, val) => {
    const ok = ratioOk(r, val);
    if (ok === null) return 'var(--mu)';
    return ok ? C.g2 : C.r;
  };


  const histScores  = RAW[pillar.scoreKey] || [];

  // Tendance & label pilier (depuis FUND_META)
  const pillarTrend24 = FUND_META.pillar_trends?.[pillar.key]?.[YEARS[YN]] || null;
  const pillarLabel24 = FUND_META.pillar_labels?.[pillar.key]?.[YEARS[YN]] || null;
  const trendColor    = pillarTrend24==='↑' ? C.g2 : pillarTrend24==='↓' ? C.r : C.am;

  // Score evolution line for header
  const scoreHistory = YEARS.map((y,i) => ({ y, v: histScores[i] }));

  return (
    <div>
      {/* ══ HEADER PILIER ══════════════════════════════════════════════════ */}
      <div style={{
        padding: isMob ? '12px 4px 10px' : '14px 6px 12px',
        borderBottom: '1px solid var(--sep)',
        background: `linear-gradient(160deg,${pc}0d 0%,transparent 70%)`,
      }}>
        {/* Row 1 : titre | diagnostic | scores */}
        <div style={{ display:'flex', alignItems:'stretch', gap:10, flexWrap:'wrap' }}>

          {/* Icône + nom */}
          <div style={{ display:'flex', alignItems:'center', gap:8, flex:'0 0 auto', minWidth:160 }}>
            <Tip title={pillar.label} body={pillar.categoryDesc} src="Analysis_Fundamentals · {COMPANY_META.ticker} · Invelps">
              <div style={{
                width:32, height:32, borderRadius:8, background:`${pc}18`, color:pc,
                fontSize:15, border:`1px solid ${pc}25`,
                display:'flex', alignItems:'center', justifyContent:'center', cursor:'help', flexShrink:0
              }}>{pillar.icon}</div>
            </Tip>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:'clamp(15px,3vw,19px)', fontWeight:700, color:'var(--ts)',
                letterSpacing:'-.02em', lineHeight:1.1 }}>{pillar.label}</div>
              <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, color:'var(--mu)',
                letterSpacing:'.09em', marginTop:2 }}>
                {pillar.abbr} · {pillar.weight}% du FScore
                <Tip title="Poids dans le FScore" body={pillar.weightDesc}>
                  <span style={{ display:'inline-flex', cursor:'help', marginLeft:4 }}><I/></span>
                </Tip>
              </div>
            </div>
          </div>

          {/* ── DIAGNOSTIC — occupe l'espace central ── */}
          {(() => {
            const diagKey = {
              liquidity:'diag_liquidity', profitability:'diag_profitability',
              growth:'diag_growth', efficiency:'diag_efficiency',
              rentability:'diag_rentability', cashflow:'diag_cashflow',
              solvency:'diag_solvency', capalloc:'diag_capalloc',
              value_creation:'diag_valuecreation',
            }[pillar.key];
            const diagOk  = diagKey ? RAW[`${diagKey}_ok`]?.[YN] : undefined;
            const diagTxt = diagKey ? RAW[`${diagKey}_txt`]?.[YN] : null;
            const diagColor = diagOk === 1 ? C.g2 : diagOk === 0 ? C.r : C.am;
            const QUESTIONS = {
              solvency:      "Peut-elle survivre à long terme ?",
              liquidity:     "Peut-elle payer ses dettes à court terme ?",
              profitability: "Génère-t-elle des marges suffisantes ?",
              rentability:   "Les ressources engagées sont-elles bien rémunérées ?",
              cashflow:      "Le bénéfice se convertit-il en cash réel ?",
              efficiency:    "Utilise-t-elle bien ses actifs opérationnels ?",
              growth:        "Se développe-t-elle de façon rentable ?",
              capalloc:      "Déploie-t-elle bien son cash ?",
              value_creation:"Crée-t-elle de la valeur économique réelle ?",
            };
            return (
              <div style={{
                flex:'1 1 200px', minWidth:0,
                padding:'10px 14px',
                background:`${diagColor}08`,
                border:`1.5px solid ${diagColor}28`,
                borderRadius:10,
                display:'flex', flexDirection:'column', justifyContent:'center', gap:5,
              }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
                    color:diagColor, textTransform:'uppercase', letterSpacing:'.09em' }}>
                    Diagnostic
                  </span>
                  {QUESTIONS[pillar.key] && (
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, color:'var(--mu)',
                      fontStyle:'italic' }}>— {QUESTIONS[pillar.key]}</span>
                  )}
                </div>
                {diagTxt ? (
                  <p style={{ margin:0, fontSize:'clamp(11px,1.3vw,13px)', fontWeight:600,
                    color:diagColor, lineHeight:1.5 }}>{diagTxt}</p>
                ) : (
                  <p style={{ margin:0, fontSize:'clamp(11px,1.3vw,13px)', fontWeight:600,
                    color:pc, lineHeight:1.5, fontStyle:'italic' }}>
                    {pillarLabel24 || '—'}
                  </p>
                )}
              </div>
            );
          })()}

          {/* Scores N-1 / N + variation + sparkline */}
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', flexShrink:0 }}>
            {/* Score N-1 */}
            {(() => {
              const ps23 = histScores[YN-1];
              if (!ps23) return null;
              const c23 = qualColor(ps23);
              return (
                <div style={{ padding:'8px 14px', borderRadius:10,
                  background:`${c23}0e`, border:`1px solid ${c23}28` }}>
                  <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10, color:'var(--mu)', marginBottom:3 }}>Score {YEARS[YN-1]}</div>
                  <div style={{ display:'flex', alignItems:'baseline', gap:5 }}>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:20, fontWeight:800, color:c23, lineHeight:1 }}>{Math.round(ps23)}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:c23 }}>{scoreLabel(ps23)}</span>
                  </div>
                </div>
              );
            })()}

            {/* Score N */}
            <div style={{ padding:'8px 14px', borderRadius:10,
              background:`${qualColor(ps)}0e`, border:`1px solid ${qualColor(ps)}28` }}>
              <div style={{ fontFamily:"ui-monospace,monospace", fontSize:10, color:'var(--mu)', marginBottom:3 }}>Score {YEARS[YN]}</div>
              <div style={{ display:'flex', alignItems:'baseline', gap:5 }}>
                <span style={{ fontFamily:"ui-monospace,monospace", fontSize:20, fontWeight:800, color:col, lineHeight:1 }}>{Math.round(ps)}</span>
                <span style={{ fontSize:13, fontWeight:700, color:qualColor(ps) }}>{qual}</span>
              </div>
              {pillarLabel24 && (
                <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:5 }}>
                  <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
                    color:qualColor(ps), background:`${qualColor(ps)}14`,
                    padding:'1px 6px', borderRadius:4, border:`1px solid ${qualColor(ps)}28` }}>
                    {pillarLabel24}
                  </span>
                  {pillarTrend24 && (
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:12, fontWeight:800, color:trendColor }}>
                      {pillarTrend24}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Variation + sparkline */}
            {(() => {
              const ps23 = histScores[YN-1];
              if (!ps23 || !ps) return null;
              const diff = ps - ps23;
              const up   = diff >= 0;
              const vc   = up ? C.g2 : C.r;
              return (
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, color:'var(--mu)', letterSpacing:'.06em' }}>Var</span>
                    <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        {up
                          ? <path d="M7 17L17 7M17 7H7M17 7v10" stroke={vc} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                          : <path d="M7 7l10 10M17 17H7m10 0V7"  stroke={vc} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>}
                      </svg>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:13, fontWeight:700, color:vc }}>
                        {up?'+':''}{diff.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  {!isMob && (
                    <div>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, color:'var(--mu)', display:'block', marginBottom:2 }}>Historique</span>
                      <ScoreHistoryChart scoreKey={pillar.scoreKey} color={col} size={45}/>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Row 2 : Lecture économique (tooltip pilier) */}
        {(() => {
          const TOOLTIP_KEYS = {
            liquidity:'fund_liquidity_tooltip', profitability:'fund_profitability_tooltip',
            growth:'fund_growth_tooltip', efficiency:'fund_efficiency_tooltip',
            rentability:'fund_rentability_tooltip', cashflow:'fund_cashflow_tooltip',
            solvency:'fund_solvency_tooltip', capalloc:'fund_capalloc_tooltip',
            value_creation:'fund_value_creation_tooltip',
          };
          const tooltipTxt = FUND_META[TOOLTIP_KEYS[pillar.key]]?.[YEARS[YN]];
          if (!tooltipTxt) return null;
          return (
            <div style={{
              marginTop:10,
              padding:'8px 12px',
              background:`${pc}07`,
              border:`1px solid ${pc}1a`,
              borderRadius:8,
              fontSize:'clamp(11px,1.2vw,12px)',
              color:'var(--tx)', lineHeight:1.65,
            }}>
              <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
                color:pc, textTransform:'uppercase', letterSpacing:'.09em', marginRight:8 }}>
                Lecture économique ·
              </span>
              {tooltipTxt}
            </div>
          );
        })()}
      </div>

      {/* ── TABLEAU RATIOS ══════════════════════════════════════════════════ */}
      <div className="af-table-scroll" style={{ width:'100%', overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
        <table style={{
          width:'100%', borderCollapse:'collapse',
          tableLayout: isMob ? 'fixed' : 'auto',
          minWidth: isMob ? 'unset' : 0,
        }}>
          <colgroup>
            {/* Ratio : fixe */}
            <col style={{width: isMob ? 90 : 160}}/>
            {/* Trend : fixe */}
            <col style={{width: isMob ? 46 : 66}}/>
            {/* Années historiques : masquées sur desktop via CSS */}
            {YEARS.map((y,yi)=>(
              <col key={y}
                className={yi<YN ? 'col-year-hist' : 'col-year-2024'}
                style={{width: isMob ? 0 : 62}}/>
            ))}
            {/* Moy. + Seuil : fixe */}
            <col style={{width: isMob ? 56 : 68}}/>
            <col style={{width: isMob ? 42 : 58}}/>
            {/* Mobile : dot · Desktop : colonne Analyse flexible — pilotage JS (isMob), pas CSS media query */}
            {isMob ? <col style={{width:18}}/> : <col/>}
          </colgroup>
          <thead>
            <tr style={{ background:'rgba(0,0,0,.025)' }}>
              <th style={TH()}>Ratio</th>
              <th style={TH('center')}>Trend</th>
              {YEARS.map((y,yi)=>(
                <th key={y}
                  className={yi<YN ? 'col-year-hist' : 'col-year-2024'}
                  style={TH('right', y===2024?pc:null)}>{y}</th>
              ))}
              <th style={TH('right',C.am)}>Moy.</th>
              <th style={TH('right')}>Seuil</th>
              {isMob
                ? <th style={{...TH('center'), padding:'4px 2px'}}></th>
                : <th style={TH('left')}>Analyse</th>
              }
            </tr>
          </thead>
          <tbody>
            {pillar.ratios.map((r, ri) => {
              const vals    = RAW[r.col] || [null,null,null,null,null];
              const lastVal = vals[YN];
              const av      = avg5(r.col);

              // Compare MOYENNE au seuil (diagnostic structurel sur 5 ans)
              const okAvg   = ratioOk(r, av);

              // Compare 2024 au seuil (diagnostic ponctuel)
              const ok2024  = ratioOk(r, lastVal);

              // Tendance : compare dernière valeur disponible vs avant-dernière
              const validVals = vals.filter(v => v !== null && !isNaN(v));
              const trendUp   = validVals.length >= 2
                ? (r.hi
                    ? validVals[validVals.length-1] >= validVals[validVals.length-2]
                    : validVals[validVals.length-1] <= validVals[validVals.length-2])
                : null;

              // Signal composite : moyenne vs seuil + tendance
              // ● vert plein   = moy. OK + tendance favorable
              // ● vert pâle    = moy. OK + tendance défavorable (vigilance)
              // ● orange       = moy. sous seuil + tendance favorable (amélioration)
              // ● rouge        = moy. sous seuil + tendance défavorable
              const dotColor = okAvg===null ? 'var(--mu)'
                : okAvg===true  && trendUp===true  ? C.g2
                : okAvg===true  && trendUp===false ? C.ol
                : okAvg===false && trendUp===true  ? C.or
                : okAvg===false && trendUp===false ? C.r
                : okAvg===true ? C.g2 : C.r;

              const dotGlow = okAvg===true && trendUp===true ? ('0 0 5px ' + C.g2 + '70')
                : okAvg===false && trendUp===false ? `0 0 5px ${C.r}70` : 'none';

              const dotTip = okAvg===null ? 'Données insuffisantes.'
                : okAvg===true && trendUp===true   ? ('Moyenne ' + fmtVal(av,r.fmt) + ' ≥ seuil ' + r.th + ' · Tendance favorable ↗')
                : okAvg===true && trendUp===false  ? ('Moyenne ' + fmtVal(av,r.fmt) + ' ≥ seuil ' + r.th + ' · Tendance dégradante ↘ — surveiller')
                : okAvg===false && trendUp===true  ? ('Moyenne ' + fmtVal(av,r.fmt) + ' sous seuil ' + r.th + ' · Tendance favorable ↗ — en amélioration')
                :                                    ('Moyenne ' + fmtVal(av,r.fmt) + ' sous seuil ' + r.th + ' · Tendance dégradante ↘ — signal négatif');


              // Fond de ligne basé sur la moyenne (diagnostic structurel)
              const rowBg = okAvg===true ? `${C.g}05` : okAvg===false ? `${C.r}05` : 'transparent';
              const rowBgHov = okAvg===true ? `${C.g}0c` : okAvg===false ? `${C.r}0c` : (pc + '07');

              return (
                <tr key={r.n}
                  style={{ cursor:'default', background:rowBg }}
                  onMouseEnter={e => e.currentTarget.style.background = rowBgHov}
                  onMouseLeave={e => e.currentTarget.style.background = rowBg}>

                  {/* ── Ratio name ── */}
                  <td style={TD()}>
                    <Tip title={r.n} body={r.desc} src="Analysis_Fundamentals">
                      <div style={{ cursor:'help' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                          <span style={{
                            width:7, height:7, borderRadius:'50%', flexShrink:0,
                            background: dotColor
                          }}/>
                          {/* Short name only on mobile (via CSS), full on desktop */}
                          <span className="ratio-name-short" style={{ fontFamily:"ui-monospace,monospace",
                            fontSize:11, fontWeight:700, color:'var(--ts)', whiteSpace:'nowrap',
                            overflow:'hidden', textOverflow:'ellipsis', display:'none' }}>
                            {r.short}<I/>
                          </span>
                          <span className="ratio-name-full" style={{ fontFamily:"ui-monospace,monospace",
                            fontSize:'clamp(10.5px,1.3vw,12px)', fontWeight:700, color:'var(--ts)',
                            whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                            {r.short}
                          </span>
                          <I/>
                        </div>
                        <div className="ratio-name-full" style={{ fontFamily:"ui-monospace,monospace", fontSize:10,
                          color:'var(--mu)', whiteSpace:'nowrap', overflow:'hidden',
                          textOverflow:'ellipsis', paddingLeft:12 }}>{r.n}</div>
                      </div>
                    </Tip>
                  </td>

                  {/* ── Trend (mini spark cliquable) ── */}
                  <td style={TD('center')}>
                    <Tip title={`Tendance ${r.short}`} body="Cliquer pour le graphique détaillé.">
                      <MiniSpark values={vals} color={pc} hi={r.hi}
                        onClick={()=>setTrendModal({...r, color:pc})}/>
                    </Tip>
                  </td>

                  {/* ── Années ── */}
                  {vals.map((v, vi) => {
                    const isLast = vi===YN;
                    const ok = ratioOk(r, v);
                    const fcol = isLast
                      ? (ok===true ? C.g2 : ok===false ? C.r : 'var(--mu)')
                      : 'var(--tx)';
                    return (
                      <td key={vi}
                        className={vi<YN?'col-year-hist': vi===YN?'col-year-2024':''}
                        style={{ ...TD('right'),
                          fontFamily:'ui-monospace,monospace',
                          fontSize: isLast ? 'clamp(12px,1.5vw,14px)' : 'clamp(10px,1.2vw,12px)',
                          fontWeight: isLast ? 800 : 400,
                          color: fcol,
                          background: isLast && ok!==null ? (ok?`${C.g}0a`:`${C.r}0a`) : 'transparent',
                        }}>
                        {fmtVal(v, r.fmt)}
                      </td>
                    );
                  })}

                  {/* ── Moyenne — vs seuil + indicateur de tendance ── */}
                  <td style={{ ...TD('right'),
                    fontFamily:'ui-monospace,monospace', fontSize:'clamp(11px,1.3vw,13px)',
                    fontWeight:700,
                    color: av===null ? 'var(--mu)' : (r.hi ? (av>=r.thN?C.g2:C.r) : (av<=r.thN?C.g2:C.r)),
                    background: av===null ? 'transparent' : (r.hi ? (av>=r.thN?`${C.g}07`:`${C.r}07`) : (av<=r.thN?`${C.g}07`:`${C.r}07`)),
                  }}>
                    <span style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:2 }}>
                      <span>{fmtVal(av, r.fmt)}</span>
                      {trendUp !== null && (
                        <span style={{
                          fontSize:10, fontWeight:700, lineHeight:1,
                          color: trendUp ? C.g2 : C.r,
                        }}>{trendUp ? '↗' : '↘'}</span>
                      )}
                    </span>
                  </td>

                  {/* ── Seuil ── */}
                  <td style={{ ...TD('right'),
                    fontFamily:'ui-monospace,monospace', fontSize:'clamp(10.5px,1.1vw,11px)',
                    color:'var(--mu)',
                  }}>
                    {r.th}
                  </td>

                  {/* ── Dot (mobile) ou Analyse (desktop) — pilotage JS (isMob) ── */}
                  {isMob ? (
                    <td style={{ ...TD('center'), padding:'4px 2px' }}>
                      <Tip title={r.n} body={dotTip} src="Reilly · Fridson · Fabozzi">
                        <span style={{
                          display:'inline-block', width:10, height:10, borderRadius:'50%',
                          cursor:'help', background:dotColor, boxShadow:dotGlow,
                        }}/>
                      </Tip>
                    </td>
                  ) : (
                    <td style={{ ...TD('left'), padding:'5px 12px' }}>
                      {(() => {
                        // Priorité 1 : commentaire SQL depuis SP (DB_ANALYSE)
                        const dbText = DB_ANALYSE[pillar.key]?.[r.col];
                        if (dbText) return (
                          <span style={{
                            fontSize:'clamp(10.5px,1vw,11px)',
                            color: dotColor,
                            lineHeight:1.55,
                          }}>{dbText}</span>
                        );
                        // Priorité 2 : calcul frontend buildEconomicImpact
                        const eco = buildEconomicImpact(r);
                        return eco ? (
                          <span style={{
                            fontSize:'clamp(10.5px,1vw,11px)',
                            color: dotColor,
                            lineHeight:1.55,
                          }}>{eco}</span>
                        ) : (
                          <span style={{
                            fontFamily:"ui-monospace,monospace", fontSize:'clamp(10px,0.9vw,11px)',
                            color:'var(--mu)', lineHeight:1.5,
                          }}>{dotTip}</span>
                        );
                      })()}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Légende */}
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', padding:'5px 14px 7px',
        fontSize:10.5, color:'var(--mu)', fontWeight:600, borderBottom:'1px solid var(--sep)',
        background:'rgba(0,0,0,.01)', alignItems:'center' }}>
        {[
          [C.g2, '● Moy. OK + ↗'],
          [C.ol, '● Moy. OK + ↘'],
          [C.or, '● Moy. < seuil + ↗'],
          [C.r,  '● Moy. < seuil + ↘'],
        ].map(([col,lbl]) => (
          <span key={lbl} style={{ display:'flex', alignItems:'center', gap:3, color:col }}>{lbl}</span>
        ))}
        <span style={{ fontStyle:'italic', fontSize:10, color:'var(--mu)', marginLeft:4 }}>
          Moy. 5 ans vs seuil · ↗↘ tendance · cliquer dot → analyse
        </span>
      </div>

      {/* ══ SYNTHÈSE ══════════════════════════════════════════════════════ */}
      <div style={{ padding: isMob?'10px 4px':'12px 6px', display:'flex', flexDirection:'column', gap:10 }}>

        {/* ══ ANALYSE · INTERPRÉTATION ÉCONOMIQUE & FINANCIÈRE ═══════════════
             Source : *_insight SQL (constat factuel) enrichi d'une lecture
             économique dynamique construite sur les valeurs RAW réelles.
             Structure : header → ratios avec constat+impact → recommandations → conclusion ══ */}
        {(() => {
          const yn  = YEARS[YN];
          const ins = PILLAR_INSIGHTS[pillar.key]?.[yn];
          const lbl = FUND_META.pillar_labels?.[pillar.key]?.[yn];
          const trd = FUND_META.pillar_trends?.[pillar.key]?.[yn];
          const tc  = trd==='↑'?C.g2:trd==='↓'?C.r:C.am;

          // ── 1. Parser le texte SQL ─────────────────────────────────────────
          const secForces   = ins ? ins.indexOf('Forces:') : -1;
          const secVigilRaw = ins ? ins.search(/Points de vigilance:/i) : -1;
          const secReco     = ins ? ins.search(/Recommandations?:/i) : -1;
          const secConc     = ins ? ins.search(/Conclusion:/i) : -1;

          const forcesRaw = secForces>=0
            ? ins.slice(secForces+7, secVigilRaw>=0?secVigilRaw:(secReco>=0?secReco:ins.length)).trim() : '';
          const vigilsRaw = secVigilRaw>=0
            ? ins.slice(ins.indexOf(':',secVigilRaw)+1, secReco>=0?secReco:(secConc>=0?secConc:ins.length)).trim() : '';
          const recoRaw   = secReco>=0
            ? ins.slice(ins.indexOf(':',secReco)+1, secConc>=0?secConc:ins.length).trim() : '';
          const conclusionSql = secConc>=0
            ? ins.slice(ins.indexOf(':',secConc)+1).replace(/\.\s*$/,'').trim() : '';

          const splitItems = s => {
            if (!s || /^aucune/i.test(s.trim())) return [];
            return s.split(/\.,\s*/)
              .map(x=>x.replace(/^\s*,\s*/,'').replace(/\.\s*$/,'').trim())
              .filter(x=>x.length>8);
          };
          const splitRecos = s => {
            if (!s || /^aucune/i.test(s.trim())) return [];
            return s.split(/\.\s+(?=[A-ZÀÂÉÈÊÎÔÙÛÉÈÊÀÂÙÇ])/)
              .map(x=>x.replace(/\.\s*$/,'').trim()).filter(x=>x.length>8);
          };

          const sqlForces = splitItems(forcesRaw);
          const sqlVigilances = splitItems(vigilsRaw);
          const sqlRecos  = splitRecos(recoRaw);
          const scoreMatch = ins ? ins.match(/Score global:\s*([0-9.]+)/) : null;
          const scoreDisp  = scoreMatch ? scoreMatch[1] : null;

          // buildEconomicImpact, avg5, trendDir → définis au niveau top-level

          // ── Construire les items enrichis depuis les items SQL ────────────
          // Chaque item SQL est associé à un ratio du pilier par correspondance
          // de texte, puis enrichi de son impact économique via buildEconomicImpact
          const enrichItem = (text, isForce) => {
            const matched = pillar.ratios.find(r =>
              text.toLowerCase().includes(r.short.toLowerCase()) ||
              text.toLowerCase().includes(r.n.toLowerCase().split('(')[0].trim())
            );
            const val    = matched ? RAW[matched.col]?.[YN] : null;
            const impact = matched ? buildEconomicImpact(matched) : null;
            const ok     = matched && val!=null ? (matched.hi ? val>=matched.thN : val<=matched.thN) : null;
            return { text, matched, val, impact, ok, isForce };
          };

          const enrichedForces     = sqlForces.map(t => enrichItem(t, true));
          const enrichedVigilances = sqlVigilances.map(t => enrichItem(t, false));

          // ── 3. Données de contexte pour le header ────────────────────────
          const scoreN1   = RAW[pillar.scoreKey]?.[YN-1] || 0;
          const scoreDiff = ps - scoreN1;
          const situationColor = ps>=75?C.g2:ps>=55?C.am:ps>=35?C.or:C.r;
          const situationLabel = ps>=75?'Situation saine':ps>=55?'Situation fragile':ps>=35?'Situation dégradée':'Situation critique';
          const nOk = pillar.ratios.filter(r=>{const v=RAW[r.col]?.[YN];return v!=null&&(r.hi?v>=r.thN:v<=r.thN);}).length;
          const nTotal = pillar.ratios.filter(r=>RAW[r.col]?.[YN]!=null).length;

          return (
            <div style={{ borderRadius:10, overflow:'hidden',
              border:`1px solid ${situationColor}22`,
              background:`${situationColor}04` }}>

              {/* ── HEADER ── */}
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap',
                padding:'7px 12px', borderBottom:`1px solid ${situationColor}18`,
                background:`${situationColor}09` }}>
                <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
                  color:situationColor, textTransform:'uppercase', letterSpacing:'.1em' }}>
                  Analyse {yn}
                </span>
                {scoreDisp && (
                  <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, fontWeight:800,
                    color:heatColor(parseFloat(scoreDisp)),
                    background:`${heatColor(parseFloat(scoreDisp))}14`,
                    padding:'1px 7px', borderRadius:4,
                    border:`1px solid ${heatColor(parseFloat(scoreDisp))}28` }}>
                    Score {scoreDisp}
                  </span>
                )}
                {lbl && (
                  <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
                    color:qualColor(ps), background:`${qualColor(ps)}12`,
                    padding:'1px 6px', borderRadius:4,
                    border:`1px solid ${qualColor(ps)}25` }}>{lbl}</span>
                )}
                {trd && (
                  <span style={{ fontFamily:"ui-monospace,monospace", fontSize:12, fontWeight:800, color:tc }}>{trd}</span>
                )}
                <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
                  {nTotal>0 && (
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, color:'var(--mu)' }}>
                      {nOk}/{nTotal} ratios ≥ seuil
                    </span>
                  )}
                  {scoreDiff > 1 && (
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700, color:C.g2,
                      background:`${C.g2}12`, border:`1px solid ${C.g2}28`, padding:'1px 6px', borderRadius:4 }}>
                      ↗ +{scoreDiff.toFixed(1)} vs {yn-1}
                    </span>
                  )}
                  {scoreDiff < -1 && (
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700, color:C.r,
                      background:`${C.r}12`, border:`1px solid ${C.r}28`, padding:'1px 6px', borderRadius:4 }}>
                      ↘ {scoreDiff.toFixed(1)} vs {yn-1}
                    </span>
                  )}
                </div>
              </div>

              {/* ── FORCES + VIGILANCES en 2 colonnes ── */}
              {(enrichedForces.length > 0 || enrichedVigilances.length > 0) && (
                <div style={{
                  display:'grid',
                  gridTemplateColumns: enrichedForces.length > 0 && enrichedVigilances.length > 0
                    ? '1fr 1fr' : '1fr',
                  borderBottom: (sqlRecos.length>0||conclusionSql||PILLAR_GUIDE[pillar.key]?.conclusion_analytique)
                    ? `1px solid ${situationColor}12` : 'none',
                }}>
                  {/* Forces */}
                  {enrichedForces.length > 0 && (
                    <div style={{
                      padding:'8px 12px',
                      borderRight: enrichedVigilances.length > 0 ? `1px solid ${situationColor}12` : 'none',
                    }}>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, fontWeight:700, color:C.g2,
                        textTransform:'uppercase', letterSpacing:'.1em', marginBottom:5 }}>✓ Forces</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        {enrichedForces.map((item, i) => (
                          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:5 }}>
                            <span style={{ color:C.g2, fontSize:10.5, fontWeight:700, flexShrink:0, marginTop:1 }}>✓</span>
                            <span style={{ fontSize:'clamp(10.5px,1.1vw,11px)', color:'var(--ts)',
                              lineHeight:1.45 }}>{item.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Vigilances */}
                  {enrichedVigilances.length > 0 && (
                    <div style={{ padding:'8px 12px' }}>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, fontWeight:700, color:C.or,
                        textTransform:'uppercase', letterSpacing:'.1em', marginBottom:5 }}>⚠ Vigilances</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        {enrichedVigilances.map((item, i) => (
                          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:5 }}>
                            <span style={{ color:C.or, fontSize:10.5, fontWeight:700, flexShrink:0, marginTop:1 }}>⚠</span>
                            <span style={{ fontSize:'clamp(10.5px,1.1vw,11px)', color:'var(--ts)',
                              lineHeight:1.45 }}>{item.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── RECOMMANDATIONS ── */}
              {sqlRecos.length > 0 && (
                <div style={{
                  padding:'7px 12px',
                  borderBottom:`1px solid ${situationColor}12`,
                  background:`${C.edu}06`,
                  display:'flex', flexWrap:'wrap', gap:'4px 20px',
                }}>
                  <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, fontWeight:700, color:C.edu,
                    textTransform:'uppercase', letterSpacing:'.1em', flexShrink:0,
                    alignSelf:'center', marginRight:4 }}>→ Reco.</span>
                  {sqlRecos.map((rec,i) => (
                    <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:4 }}>
                      <span style={{ color:C.edu, fontSize:10.5, flexShrink:0, marginTop:1 }}>→</span>
                      <span style={{ fontSize:'clamp(10.5px,1.1vw,11px)', color:'var(--tx)',
                        lineHeight:1.45 }}>{rec}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── CONCLUSION SQL + LECTURE ÉCONOMIQUE sur une ligne ── */}
              {(conclusionSql || PILLAR_GUIDE[pillar.key]?.conclusion_analytique) && (
                <div style={{ padding:'7px 12px', background:'var(--sur)',
                  display:'flex', flexDirection:'column', gap:5 }}>
                  {conclusionSql && (
                    <p style={{ fontSize:'clamp(10.5px,1.1vw,11px)', color:'var(--mu)',
                      fontStyle:'italic', lineHeight:1.55, margin:0 }}>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, fontWeight:700,
                        color:'var(--mu)', textTransform:'uppercase', letterSpacing:'.1em',
                        marginRight:6 }}>SQL ·</span>
                      {conclusionSql}
                    </p>
                  )}
                  {PILLAR_GUIDE[pillar.key]?.conclusion_analytique && (
                    <p style={{ fontSize:'clamp(10.5px,1.2vw,11.5px)', color:'var(--tx)',
                      lineHeight:1.6, margin:0 }}>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, fontWeight:700,
                        color:situationColor, textTransform:'uppercase', letterSpacing:'.1em',
                        marginRight:6 }}>Lecture ·</span>
                      {PILLAR_GUIDE[pillar.key].conclusion_analytique}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Source ── */}
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ flex:1, height:1, background:'var(--sep)', borderRadius:999 }}/>
          <div style={{ display:'flex', alignItems:'center', gap:5, borderRadius:8,
            padding:'3px 10px', background:`${C.or}10`, border:`1px solid ${C.or}28` }}>
            <span style={{ fontSize:10.5, color:'var(--mu)', fontWeight:600 }}>Source ·</span>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, color:C.or, fontWeight:700 }}>
              {COMPANY_META.ticker} {COMPANY_META.fullName} · Analysis_Fundamentals · IFRS
            </span>
          </div>
          <div style={{ flex:1, height:1, background:'var(--sep)', borderRadius:999 }}/>
        </div>
      </div>

      {trendModal && <TrendModal ratio={trendModal} onClose={()=>setTrendModal(null)}/>}
    </div>
  );
}

// Helpers pour styles de cellules uniformes entre onglets
function TH(align='left', color=null) {
  return {
    padding:'8px 6px',
    textAlign:align,
    fontFamily:"ui-monospace,monospace",
    fontSize:'clamp(10px,1.1vw,11px)',
    fontWeight:700,
    color: color || 'var(--mu)',
    background:'rgba(0,0,0,.025)',
    borderBottom:'1px solid var(--sep)',
    whiteSpace:'nowrap',
    letterSpacing:'.05em',
    textTransform:'uppercase',
    verticalAlign:'bottom',
  };
}
function TD(align='left') {
  return {
    padding:'8px 6px',
    textAlign:align,
    borderBottom:'1px solid var(--sep)',
    verticalAlign:'middle',
  };
}


// ── COURS MODAL — Analyse Fondamentale par Ratios ────────────────────────────
// Bibliographie : Damodaran (Investment Valuation) · Fridson & Alvarez
// (Financial Statement Analysis) · Reilly & Brown (Investment Analysis)
// Graham & Dodd (Security Analysis) · Fabozzi & Peterson (Financial Mgmt)

const CourseCtx = React.createContext(null);
const useCourse = () => React.useContext(CourseCtx);

function CourseProvider({ children, dark }) {
  const D = dark ? {
    bg:'#09090b', su:'#111114', s2:'#16161b', bo:'#1e1e26', b2:'#2a2a36',
    tx:'#dcd8ce', di:'#8a8680', mu:'#3e3c38',
    go:'#c4a35a', gd:'#7a5e28', gp:'#e8d5a0',
    gr:'#3a7a50', gb:'#6dbd8a', gbg:'#0a130c',
    re:'#8a3a3a', rb:'#c47070', rbg:'#130a0a',
    bl:'#3a5a8a', bb:'#6a9aca', blbg:'#0a0d16', am:'#c4803a',
  } : {
    bg:'#f8f7f3', su:'#f0ede6', s2:'#e8e4da', bo:'#d4cfc4', b2:'#c4bfb4',
    tx:'#2d2a24', di:'#5a5650', mu:'#8a8680',
    go:'#8a6a2a', gd:'#6a5020', gp:'#3a2a10',
    gr:'#2a6040', gb:'#1a7a50', gbg:'#e8f4ee',
    re:'#7a2a2a', rb:'#9a4040', rbg:'#f8eaea',
    bl:'#2a4a8a', bb:'#3a6aaa', blbg:'#eaeef8', am:'#8a5820',
  };
  const [scores, setScores] = useState({});
  const register = useCallback((id, correct) =>
    setScores(p => p[id] !== undefined ? p : { ...p, [id]: correct }), []);
  return (
    <CourseCtx.Provider value={{ D, scores, register }}>
      {children}
    </CourseCtx.Provider>
  );
}

/* ── Atoms ── */
function CP({ children }) {
  const { D } = useCourse();
  return <p style={{ fontSize:'.83rem', color:D.di, lineHeight:1.8, marginBottom:'.8rem' }}>{children}</p>;
}
function CH({ children }) { const { D } = useCourse(); return <strong style={{ color:D.gp, fontWeight:500 }}>{children}</strong>; }
function CRef({ src }) { const { D } = useCourse(); return <span style={{ fontFamily:"ui-monospace,monospace", fontSize:'.65rem', color:D.go, opacity:.8, marginLeft:6 }}>[ {src} ]</span>; }
function CSrc({ children }) { const { D } = useCourse(); return <div style={{ fontFamily:"ui-monospace,monospace", fontSize:'.68rem', color:D.mu, marginTop:2, marginBottom:8, display:'flex', alignItems:'center', gap:4 }}><span style={{ opacity:.5 }}>📖</span>{children}</div>; }
function CForm({ label, f }) {
  const { D } = useCourse();
  return (
    <div style={{ margin:'.7rem 0', padding:'.65rem 1rem', background:D.s2, border:`1px solid ${D.b2}`, borderRadius:4, display:'flex', alignItems:'flex-start', gap:10 }}>
      {label && <span style={{ fontFamily:"ui-monospace,monospace", fontSize:'.54rem', color:D.mu, textTransform:'uppercase', letterSpacing:'.08em', flexShrink:0, paddingTop:3 }}>{label}</span>}
      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:'.8rem', color:D.gp, lineHeight:1.5 }}>{f}</span>
    </div>
  );
}
function CBox({ children, color, label }) {
  const { D } = useCourse();
  const co = color || D.bl;
  return (
    <div style={{ padding:'.75rem 1rem', margin:'.7rem 0', borderLeft:`3px solid ${co}`, background:`${co}16`, fontSize:'.8rem', lineHeight:1.68, color:D.di, borderRadius:'0 5px 5px 0' }}>
      {label && <span style={{ fontFamily:"ui-monospace,monospace", fontSize:'.54rem', letterSpacing:'.18em', textTransform:'uppercase', opacity:.7, display:'block', marginBottom:4 }}>{label}</span>}
      {children}
    </div>
  );
}
function CSec({ num, tag, title }) {
  const { D } = useCourse();
  return (
    <div style={{ marginTop:'2.5rem', marginBottom:'1.2rem' }}>
      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:'.54rem', letterSpacing:'.26em', textTransform:'uppercase', color:D.go, marginBottom:5 }}>{num} — {tag}</div>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(1.1rem,3vw,1.55rem)', color:D.tx, lineHeight:1.2 }}>{title}</div>
      <div style={{ width:36, height:2, background:D.gd, marginTop:9, opacity:.5 }}/>
    </div>
  );
}
function QCM({ id, q, choices }) {
  const { D, scores, register } = useCourse();
  const done = scores[id] !== undefined;
  const ok   = scores[id] === true;
  const [sel, setSel] = useState(null);
  const [fb,  setFb]  = useState(null);
  const BC = done ? (ok ? D.gr : D.re) : D.b2;
  const BG = done ? (ok ? D.gbg : D.rbg) : D.s2;
  return (
    <div style={{ border:`1px solid ${BC}`, background:BG, marginBottom:'1rem', borderRadius:4, overflow:'hidden', transition:'border-color .3s,background .3s', position:'relative' }}>
      <div style={{ position:'absolute', left:0, top:0, bottom:0, width:3, background:done?(ok?D.gb:D.rb):D.gd }}/>
      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'.5rem 1rem', borderBottom:`1px solid ${D.bo}` }}>
        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:'.54rem', letterSpacing:'.14em', textTransform:'uppercase', padding:'.1rem .4rem', border:`1px solid ${D.bb}`, color:D.bb, background:D.blbg }}>QCM</span>
        <span style={{ fontSize:'.7rem', color:D.di, flex:1 }}>{id}</span>
        {done && <span style={{ color:ok?D.gb:D.rb, fontSize:'.85rem' }}>{ok?'✓':'✗'}</span>}
      </div>
      <div style={{ padding:'.75rem 1rem .5rem', fontSize:'.8rem', color:D.tx, lineHeight:1.65 }}>{q}</div>
      <div style={{ display:'flex', flexDirection:'column', gap:4, padding:'0 1rem .7rem' }}>
        {choices.map((ch, i) => {
          let bc=D.bo, bg=D.bg, co=D.di;
          if (done) { if(ch.c){bc=D.gr;bg=D.gbg;co=D.gb;} else if(sel===i){bc=D.re;bg=D.rbg;co=D.rb;} }
          return (
            <div key={i} onClick={()=>{ if(!done){ setSel(i); register(id,ch.c); setFb(ch.f); } }}
              style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'.38rem .65rem',
                border:`1px solid ${bc}`, background:bg, color:co, cursor:done?'default':'pointer',
                fontSize:'.76rem', lineHeight:1.5, userSelect:'none', borderRadius:4 }}>
              <span style={{ fontFamily:"ui-monospace,monospace", fontSize:'.54rem', border:`1px solid ${bc}`,
                padding:'.03rem .27rem', flexShrink:0, marginTop:2 }}>{['A','B','C','D'][i]}</span>
              {ch.l}
            </div>
          );
        })}
      </div>
      {fb && done && (
        <div style={{ padding:'.5rem 1rem', borderTop:`1px solid ${ok?D.gr:D.re}`,
          background:ok?D.gbg:D.rbg, color:ok?D.gb:D.rb, fontSize:'.74rem', lineHeight:1.6 }}>
          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:'.54rem', display:'block', marginBottom:2, opacity:.7, textTransform:'uppercase' }}>{ok?'Correct':'Incorrect'}</span>
          {fb}
        </div>
      )}
    </div>
  );
}

function CDataRow({ label, values, fmt, thN, hi, col }) {
  const { D } = useCourse();
  const fv = v => v===null||v===undefined||isNaN(v) ? '—' :
    fmt==='%' ? (v*100).toFixed(1)+'%' :
    fmt==='x' ? v.toFixed(2)+'×' :
    fmt==='j' ? Math.round(v)+'j' :
    fmt==='€' ? v.toFixed(2)+'€' : v.toFixed(2);
  const ok = v => v===null||v===undefined||isNaN(v) ? null : hi ? v>=thN : v<=thN;
  return (
    <tr>
      <td style={{ padding:'6px 10px', fontFamily:"ui-monospace,monospace", fontSize:'.72rem', color:D.go, borderBottom:`1px solid ${D.bo}`, whiteSpace:'nowrap' }}>{label}</td>
      {YEARS.map((y,i) => {
        const v = (RAW[col]||[])[i];
        const isOk = ok(v);
        return (
          <td key={y} style={{ padding:'6px 8px', textAlign:'right', fontFamily:"ui-monospace,monospace",
            fontSize: i===YN?'.82rem':'.73rem', fontWeight:i===YN?700:400,
            color: i===YN ? (isOk===true?D.gb:isOk===false?D.rb:D.go) : D.di,
            borderBottom:`1px solid ${D.bo}`, whiteSpace:'nowrap' }}>
            {fv(v)}
          </td>
        );
      })}
    </tr>
  );
}

function CPRXTable({ title, rows }) {
  const { D } = useCourse();
  return (
    <div style={{ margin:'.8rem 0', border:`1px solid ${D.bo}`, borderRadius:8, overflow:'hidden' }}>
      <div style={{ padding:'.54rem .8rem', background:D.su, fontFamily:"ui-monospace,monospace", fontSize:'.5rem',
        color:D.mu, textTransform:'uppercase', letterSpacing:'.12em', borderBottom:`1px solid ${D.bo}`,
        display:'flex', alignItems:'center', gap:6 }}>
        <span style={{ width:6, height:6, borderRadius:'50%', background:D.gb, display:'inline-block' }}/>
        {COMPANY_META.ticker} · {title} · Données Analysis_Fundamentals
      </div>
      <div className="af-table-scroll" style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:380 }}>
          <thead>
            <tr style={{ background:D.s2 }}>
              <th style={{ padding:'5px 10px', textAlign:'left', fontFamily:"ui-monospace,monospace", fontSize:'.54rem',
                color:D.mu, textTransform:'uppercase', letterSpacing:'.08em', borderBottom:`1px solid ${D.bo}` }}>Ratio</th>
              {YEARS.map(y => (
                <th key={y} style={{ padding:'5px 8px', textAlign:'right', fontFamily:"ui-monospace,monospace",
                  fontSize:'.54rem', color: y===2024?D.go:D.mu, textTransform:'uppercase',
                  letterSpacing:'.06em', borderBottom:`1px solid ${D.bo}` }}>{y}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => <CDataRow key={r.label} {...r}/>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScoreBar() {
  const { D, scores } = useCourse();
  const total = 6;
  const ans = Object.keys(scores).length;
  const cor = Object.values(scores).filter(Boolean).length;
  if (ans < total) return null;
  const pct = Math.round(cor/total*100);
  return (
    <div style={{ margin:'3rem 0 2rem', padding:'1.5rem 1.8rem', border:`1px solid ${D.bo}`, background:D.su, borderRadius:4 }}>
      <div style={{ display:'flex', alignItems:'baseline', gap:18 }}>
        <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'3rem', color:D.go, lineHeight:1 }}>{cor}/{total}</span>
        <div>
          <div style={{ fontFamily:"ui-monospace,monospace", fontSize:'.54rem', color:D.mu, textTransform:'uppercase', letterSpacing:'.14em' }}>Score final · Cours 3.1</div>
          <div style={{ height:3, background:D.b2, width:200, marginTop:8, borderRadius:4 }}>
            <div style={{ height:'100%', background:D.go, width:`${pct}%`, transition:'width 1.2s ease', borderRadius:4 }}/>
          </div>
        </div>
      </div>
      <div style={{ fontSize:'.78rem', color:D.di, marginTop:12 }}>
        {pct===100?'Maîtrise complète — prêt pour la pratique.':pct>=75?'Très bonne maîtrise.':pct>=50?'Base correcte — relisez les sections insuffisantes.':'Relisez le cours avant de continuer.'}
      </div>
    </div>
  );
}

function CourseContent({ dark }) {
  return (
    <CourseProvider dark={dark}>
      <CourseContentInner/>
    </CourseProvider>
  );
}

function CourseContentInner() {
  const { D } = useCourse();
  const [activeChap, setActiveChap] = useState('intro');
  const [menuOpen, setMenuOpen] = useState(false);
  const yr = YEARS[YN];
  const fmtV = (v,f='%') => v!=null&&!isNaN(v)?fmtVal(v,f):'—';
  const fmtX = v => fmtV(v,'x');
  const fmtJ = v => v!=null&&!isNaN(v)?`${Math.round(v)}j`:'—';
  const traj = (col,f='%') => YEARS.map((_,i)=>fmtV(RAW[col]?.[i],f)).join(' → ');

  const CHAPTERS = [
    {id:'intro',  num:'0', label:'Introduction'},
    {id:'method', num:'1', label:'4 niveaux de lecture'},
    {id:'liq',    num:'2', label:'Liquidité'},
    {id:'prof',   num:'3', label:'Profitabilité'},
    {id:'grow',   num:'4', label:'Croissance'},
    {id:'eff',    num:'5', label:'Efficacité'},
    {id:'rent',   num:'6', label:'Rentabilité'},
    {id:'cf',     num:'7', label:'Cash Flow'},
    {id:'solv',   num:'8', label:'Solvabilité'},
    {id:'capalloc',num:'9',label:'Alloc. Capital'},
    {id:'valeur', num:'10',label:'Création Valeur'},
    {id:'valuation',num:'11',label:'Valorisation'},
    {id:'synth',  num:'12',label:'Synthèse FScore'},
  ];

  const scrollTo = id => {
    const container = document.getElementById('course-scroll-container');
    const el = container?.querySelector(`#course-${id}`);
    if (el && container) container.scrollTo({ top: el.offsetTop - 24, behavior:'smooth' });
    setActiveChap(id);
    setMenuOpen(false);
  };

  useEffect(() => {
    const container = document.getElementById('course-scroll-container');
    if (!container) return;

    const handleScroll = () => {
      const sections = container.querySelectorAll('[data-sec]');
      let current = 'intro';
      sections.forEach(el => {
        // offsetTop relatif au container scrollable
        const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
        if (top <= 80) current = el.dataset.sec;
      });
      setActiveChap(prev => {
        if (prev === current) return prev;
        // Scroll la sidebar pour garder l'élément actif visible
        setTimeout(() => {
          document.getElementById(`sidebar-ch-${current}`)
            ?.scrollIntoView({ behavior:'smooth', block:'nearest' });
        }, 0);
        return current;
      });
    };

    // Délai pour laisser le DOM se construire
    const timer = setTimeout(() => {
      container.addEventListener('scroll', handleScroll, { passive:true });
      handleScroll(); // init au chargement
    }, 200);

    return () => {
      clearTimeout(timer);
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // ── Micro-composants ──────────────────────────────────────────────────────
  const Sec = ({id,num,tag,title}) => (
    <div id={`course-${id}`} data-sec={id} style={{paddingTop:'2rem',scrollMarginTop:'60px',maxWidth:780}}>
      <div style={{fontFamily:"ui-monospace,monospace",fontSize:'.52rem',letterSpacing:'.2em',textTransform:'uppercase',color:D.go,marginBottom:'.25rem'}}>{num} — {tag}</div>
      <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:'clamp(1.05rem,2.4vw,1.35rem)',color:D.tx,fontWeight:400,margin:'0 0 .65rem',borderBottom:`2px solid ${D.go}28`,paddingBottom:'.3rem'}}>{title}</h2>
    </div>
  );
  const P = ({children}) => <p style={{fontSize:'.81rem',color:D.di,lineHeight:1.8,margin:'0 0 .55rem',maxWidth:720}}>{children}</p>;
  const B = ({children}) => <strong style={{color:D.tx}}>{children}</strong>;
  const Ref = ({src}) => <span style={{fontFamily:"ui-monospace,monospace",fontSize:'.52rem',color:D.go,marginLeft:4,opacity:.8}}>[{src}]</span>;
  const Bx = ({label,color,children}) => (
    <div style={{margin:'.5rem 0',padding:'.65rem .88rem',background:`${color||D.go}0c`,border:`1px solid ${color||D.go}26`,borderRadius:8,maxWidth:780}}>
      {label&&<div style={{fontFamily:"ui-monospace,monospace",fontSize:'.52rem',color:color||D.go,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:'.28rem',fontWeight:700}}>{label}</div>}
      <div style={{fontSize:'.78rem',color:D.di,lineHeight:1.72}}>{children}</div>
    </div>
  );

  // ── Fiche métrique complète ────────────────────────────────────────────────
  // desc: définition courte
  // formula: formule de calcul
  // seuils: objet {ok, warn, bad, note?}
  // val: valeur actuelle depuis RAW
  // trendCol: colonne RAW pour la trajectoire
  // n3: lecture économique
  // n4: qualité / limites du ratio
  const Metric = ({title, desc, formula, seuils, val, fmtF='%', trendCol, hi=true, thN, n3, n4}) => {
    const v = val ?? RAW[trendCol]?.[YN];
    const nums = trendCol ? YEARS.map((_,i)=>RAW[trendCol]?.[i]) : [];
    const ok = v!=null&&!isNaN(v) ? (hi ? v>=thN : v<=thN) : null;
    const dotC = ok===true ? D.gb : ok===false ? D.rb : D.am;
    const fv = v!=null&&!isNaN(v) ? fmtVal(v, fmtF) : '—';

    return (
      <div style={{margin:'.65rem 0',border:`1px solid ${D.bo}`,borderRadius:8,overflow:'hidden'}}>
        {/* En-tête */}
        <div style={{padding:'7px 12px',background:D.su,display:'flex',alignItems:'center',gap:8,borderBottom:`1px solid ${D.bo}`}}>
          <div style={{width:8,height:8,borderRadius:'50%',background:dotC,flexShrink:0}}/>
          <span style={{fontFamily:"ui-monospace,monospace",fontSize:'.6rem',fontWeight:700,color:D.tx,letterSpacing:'.04em'}}>{title}</span>
          <span style={{fontFamily:"ui-monospace,monospace",fontSize:'.75rem',fontWeight:800,color:dotC,marginLeft:'auto'}}>{fv}</span>
          {thN!=null&&<span style={{fontFamily:"ui-monospace,monospace",fontSize:'.55rem',color:D.mu}}>{hi?'>':'<'}{fmtVal(thN,fmtF)}</span>}
        </div>

        <div style={{padding:'8px 12px',display:'flex',flexDirection:'column',gap:8}}>
          {/* Description + formule */}
          <div style={{display:'grid',gridTemplateColumns:'1fr auto',gap:'0 12px',alignItems:'start'}}>
            <div>
              <div style={{fontSize:'.75rem',color:D.di,lineHeight:1.7,marginBottom:'.3rem'}}>{desc}</div>
              {formula && (
                <div style={{fontFamily:"ui-monospace,monospace",fontSize:'.60rem',color:D.go,background:D.su,
                  border:`1px solid ${D.bo}`,borderLeft:`3px solid ${D.go}`,
                  padding:'4px 8px',borderRadius:'0 4px 4px 0',display:'inline-block'}}>
                  {formula}
                </div>
              )}
            </div>
            {/* Mini seuils */}
            {seuils && (
              <div style={{flexShrink:0,fontSize:'.56rem',fontFamily:"ui-monospace,monospace",color:D.mu,
                border:`1px solid ${D.bo}`,borderRadius:4,overflow:'hidden',minWidth:70}}>
                {seuils.ok  && <div style={{padding:'2px 6px',background:D.gb+'12',borderBottom:`1px solid ${D.bo}30`,color:D.gb}}>{seuils.ok}</div>}
                {seuils.warn&& <div style={{padding:'2px 6px',background:D.am+'10',borderBottom:`1px solid ${D.bo}30`,color:D.am}}>{seuils.warn}</div>}
                {seuils.bad && <div style={{padding:'2px 6px',background:D.rb+'10',color:D.rb}}>{seuils.bad}</div>}
              </div>
            )}
          </div>

          {/* Trajectoire 5 ans */}
          {trendCol && nums.length>0 && (
            <div style={{display:'flex',gap:4,alignItems:'center',flexWrap:'wrap'}}>
              <span style={{fontFamily:"ui-monospace,monospace",fontSize:'.52rem',color:D.mu,marginRight:2}}>Traj.</span>
              {nums.map((nv,i)=>{
                const cur=i===YN;
                const nok=nv!=null&&!isNaN(nv)?(hi?nv>=thN:nv<=thN):null;
                return (
                  <span key={i} style={{
                    fontFamily:"ui-monospace,monospace",fontSize:cur?'.68rem':'.58rem',
                    fontWeight:cur?800:400,
                    color:cur?(nok===true?D.gb:nok===false?D.rb:D.di):(nv!=null?D.mu:'transparent'),
                    background:cur?(nok===true?D.gb+'12':nok===false?D.rb+'12':'transparent'):'transparent',
                    padding:cur?'1px 4px':'0',borderRadius:4,
                  }}>
                    {nv!=null&&!isNaN(nv)?fmtVal(nv,fmtF):'—'}
                    {cur&&<span style={{fontSize:'.5rem',marginLeft:2}}>{yr}</span>}
                  </span>
                );
              })}
            </div>
          )}

          {/* N3 — Lecture économique */}
          {n3 && (
            <div style={{display:'flex',gap:6}}>
              <span style={{fontFamily:"ui-monospace,monospace",fontSize:'.52rem',color:D.gb,fontWeight:700,
                flexShrink:0,paddingTop:2}}>N3</span>
              <div style={{fontSize:'.74rem',color:D.di,lineHeight:1.68}}>{n3}</div>
            </div>
          )}

          {/* N4 — Qualité */}
          {n4 && (
            <div style={{display:'flex',gap:6,borderTop:`1px dashed ${D.bo}`,paddingTop:7}}>
              <span style={{fontFamily:"ui-monospace,monospace",fontSize:'.52rem',color:D.am,fontWeight:700,
                flexShrink:0,paddingTop:2}}>N4</span>
              <div style={{fontSize:'.72rem',color:D.mu,lineHeight:1.65,fontStyle:'italic'}}>{n4}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const PScore = k => RAW[`${k}_score`]?.[YN];
  const PLbl   = k => FUND_META.pillar_labels?.[k]?.[yr]||'';
  const PTip   = k => FUND_META[`fund_${k}_tooltip`]?.[yr]||'';

  const SectionHeader = ({pillarKey, tag, id, num, title}) => (
    <>
      <Sec id={id} num={num} tag={tag} title={title}/>
      {PScore(pillarKey)!=null && (
        <Bx label={`Score ${PScore(pillarKey)?.toFixed(0)}/100 · ${PLbl(pillarKey)}`}
          color={heatColor(PScore(pillarKey)||0)}>
          {PTip(pillarKey)}
        </Bx>
      )}
    </>
  );

  // ── Contenu principal ─────────────────────────────────────────────────────
  const Content = () => (
    <div style={{paddingTop:'.5rem'}}>

      {/* 0 — INTRODUCTION */}
      <Sec id="intro" num="0" tag="Introduction" title="Analyse Fondamentale par Ratios"/>
      <P>Ce cours présente les principaux <B>ratios financiers organisés en 9 piliers analytiques</B>, chacun illustré sur les données réelles de <B>{COMPANY_META.ticker} {COMPANY_META.fullName} (FY {YEARS[0]}–{YEARS[YN]})</B>. Chaque métrique est analysée à travers 4 niveaux progressifs : valeur brute, tendance, lecture économique et qualité du signal.<Ref src="Reilly ch.10"/></P>
      <Bx label="📚 Références">
        <div style={{fontFamily:"ui-monospace,monospace",fontSize:'.58rem',display:'grid',gridTemplateColumns:'72px 1fr',gap:'3px 8px'}}>
          <b style={{color:D.go}}>[Damodaran]</b><span>Investment Valuation — ROIC, EVA, valorisation</span>
          <b style={{color:D.go}}>[Fridson]</b><span>Financial Statement Analysis — Income Quality</span>
          <b style={{color:D.go}}>[Reilly]</b><span>Investment Analysis — DuPont, ratios, croissance</span>
          <b style={{color:D.go}}>[Graham]</b><span>Security Analysis — valeur intrinsèque, earnings quality</span>
          <b style={{color:D.go}}>[Fabozzi]</b><span>Financial Management — solvabilité, ICR</span>
        </div>
      </Bx>
      <Bx label={`${COMPANY_META.ticker} ${COMPANY_META.fullName} · FY ${yr}`} color={D.gb}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'3px 16px',fontFamily:"ui-monospace,monospace",fontSize:'.62rem'}}>
          <div>FScore <B>{RAW.fscore?.[YN]?.toFixed(1)}/100</B></div>
          <div>Signal <B>{FUND_META.signal?.[yr]}</B></div>
          <div>ROIC <B>{fmtV(RAW.roic?.[YN])}</B></div>
          <div>WACC <B>{fmtV(RAW.wacc?.[YN])}</B></div>
          <div>ICR <B>{fmtX(RAW.interest_coverage?.[YN])}</B></div>
          <div>FCF/CA <B>{fmtV(RAW.fcf_to_sales?.[YN])}</B></div>
        </div>
      </Bx>

      {/* 1 — MÉTHODE */}
      <Sec id="method" num="1" tag="Méthode" title="Les 4 niveaux de lecture d'un ratio"/>
      <P>Tout ratio peut être lu selon quatre angles complémentaires. Cumuler ces lectures est ce qui distingue l'analyse rigoureuse de la lecture superficielle.<Ref src="Reilly §10.1"/></P>
      <div style={{border:`1px solid ${D.bo}`,borderRadius:8,overflow:'hidden',margin:'.5rem 0'}}>
        {[
          {num:'N1',lbl:'Brut',color:'#6b8fd4',text:"La valeur du ratio comparée au seuil de référence. Lecture binaire : OK ou NOK. Point de départ obligatoire mais insuffisant seul."},
          {num:'N2',lbl:'Tendance',color:D.go,text:"L'évolution sur 5 ans. Un ratio en dégradation constante est plus préoccupant qu'un ratio bas mais en amélioration. Permet de distinguer conjoncturel et structurel."},
          {num:'N3',lbl:'Économique',color:D.gb,text:"Pourquoi ce niveau ? Quels mécanismes opérationnels, financiers ou sectoriels l'expliquent ? La lecture économique donne le sens — c'est le niveau analytique le plus riche."},
          {num:'N4',lbl:'Qualité',color:D.am,text:"Le ratio mesure-t-il correctement ce qu'il prétend mesurer ? Distorsions IFRS, comparabilité sectorielle, conventions comptables, manipulation potentielle."},
        ].map(({num,lbl,color,text})=>(
          <div key={num} style={{padding:'8px 12px',borderBottom:`1px solid ${D.bo}30`,display:'flex',gap:10}}>
            <div style={{fontFamily:"ui-monospace,monospace",fontSize:'.56rem',color,fontWeight:700,flexShrink:0,width:52,paddingTop:2}}>
              {num}<br/><span style={{fontSize:'.5rem',color:D.mu,fontWeight:400}}>{lbl}</span>
            </div>
            <div style={{fontSize:'.76rem',color:D.di,lineHeight:1.7}}>{text}</div>
          </div>
        ))}
      </div>

      {/* 2 — LIQUIDITÉ */}
      <SectionHeader pillarKey="liquidity" id="liq" num="2" tag="Pilier 1" title="Liquidité"/>
      <P>Les ratios de liquidité mesurent la capacité à honorer les engagements des 12 prochains mois sans recourir aux marchés. Ils répondent à la question de <B>survie immédiate</B>, distincte de la solvabilité à long terme.<Ref src="Fabozzi ch.4"/></P>

      <Metric title="Current Ratio — Ratio de liquidité courante"
        desc="Mesure si les actifs à court terme couvrent les passifs à court terme. Inclut trésorerie, créances et stocks."
        formula="Actifs CT / Passifs CT"
        seuils={{ok:'>1.5× solide',warn:'1–1.5× correct',bad:'<1× tension'}}
        trendCol="current_ratio" fmtF="x" hi={true} thN={1.5}
        n3={`Pour cette entreprise, le CR élevé (${fmtX(RAW.current_ratio?.[YN])}) reflète la trésorerie abondante et un BFR structurellement négatif : les clients paient via l'app avant la livraison. Ce n'est pas une force opérationnelle, c'est un avantage structurel des plateformes B2C qui encaissent avant de décaisser.`}
        n4="Le CR inclut les stocks — peu pertinents pour les plateformes à modèle asset-light. Les avances clients et créances inter-segments peuvent gonfler artificiellement le numérateur. Vérifier la composition de l'actif CT dans les annexes."
      />
      <Metric title="Quick Ratio — Liquidité immédiate (hors stocks)"
        desc="Variante du CR excluant les stocks. Mesure la liquidité immédiate avec les actifs rapidement mobilisables."
        formula="(Actifs CT − Stocks) / Passifs CT"
        seuils={{ok:'>1× suffisant',warn:'0.7–1× correct',bad:'<0.7× risque'}}
        trendCol="quick_ratio" fmtF="x" hi={true} thN={1.0}
        n3={`QR ${fmtX(RAW.quick_ratio?.[YN])} ≈ CR car cette entreprise détient quasi pas de stocks (modèle marketplace). La quasi-égalité CR/QR confirme la nature asset-light : aucun stock ne gonfle artificiellement la liquidité. Ratio plus pertinent pour les industriels et distributeurs.`}
        n4="La définition des quasi-espèces varie selon les normes : certains analystes incluent les placements CT < 90 jours, d'autres non. S'assurer de l'homogénéité pour la comparaison sectorielle."
      />
      <Metric title="Cash Ratio — Liquidité pure"
        desc="Test de liquidité le plus strict : seule la trésorerie disponible face aux dettes immédiates. Mesure la résistance à un choc soudain."
        formula="(Trésorerie + Équivalents) / Passifs CT"
        seuils={{ok:'>0.5× confortable',warn:'0.2–0.5× correct',bad:'<0.2× risque choc'}}
        trendCol="cash_ratio" fmtF="x" hi={true} thN={0.5}
        n3={`Cash Ratio ${fmtX(RAW.cash_ratio?.[YN])} — la trésorerie seule couvre ${((RAW.cash_ratio?.[YN]||0)*100).toFixed(0)}% des passifs CT. Pour cette mega cap, ce niveau de trésorerie disponible garantit une flexibilité stratégique totale et une résistance maximale aux chocs de marché.`}
        n4="En HK/Chine, les placements financiers CT à grande liquidité peuvent être inclus ou exclus selon la politique comptable. Graham insistait sur la conversion réelle des actifs liquides — certains placements CT peuvent avoir des délais de sortie contractuels."
      />

      {/* 3 — PROFITABILITÉ */}
      <SectionHeader pillarKey="profitability" id="prof" num="3" tag="Pilier 2" title="Profitabilité"/>
      <P>Les marges mesurent la capacité à transformer le chiffre d'affaires en profit à chaque niveau de la <B>cascade des résultats</B> : du brut commercial jusqu'au net après impôts. La lecture de cascade (Gross → EBITDA → EBIT → Net) permet d'isoler le niveau où la valeur se perd.<Ref src="Graham ch.12 · Fridson ch.5"/></P>

      <Metric title="Marge brute (Gross Margin)"
        desc="Part du CA restant après le coût direct des ventes. Mesure le pricing power et la compétitivité sur les coûts directs."
        formula="(CA − Coût des ventes) / CA"
        seuils={{ok:'>40% fort',warn:'20–40% correct',bad:'<20% faible'}}
        trendCol="gross_margin" fmtF="%" hi={true} thN={0.40}
        n3={`Gross Mg ${fmtV(RAW.gross_margin?.[YN])} — sous le seuil de 40% mais en progression (${fmtV(RAW.gross_margin?.[0])} en 2021). Pour ce type de plateforme, le coût des ventes inclut les incitatifs livreurs, les subventions marchands et l'infrastructure réseau. La marge de 30% reflète le coût de la couche physique de livraison — prix d'entrée pour bâtir le fossé concurrentiel.`}
        n4="En IFRS vs US GAAP, la classification entre coût des ventes et charges opérationnelles peut varier. Certaines plateformes HK capitalisent de la R&D en COGS — limitant la comparabilité avec les pairs occidentaux."
      />
      <Metric title="Marge EBITDA"
        desc="Rentabilité opérationnelle avant amortissements et charges financières. Proxy de la génération de cash opérationnel brut."
        formula="EBITDA / CA"
        seuils={{ok:'>15% correct',warn:'5–15% fragile',bad:'<5% ou nég.'}}
        trendCol="ebitda_margin" fmtF="%" hi={true} thN={0.15}
        n3={`EBITDA Mg ${fmtV(RAW.ebitda_margin?.[YN])} — négatif en 2021-2022 puis positif 2023-2024, rechute 2025. Le pivot illustre l'évolution du modèle : les pertes initiales liées aux investissements (Instashopping, vélos) ont cédé la place à une profitabilité opérationnelle intermédiaire, fragilisée en 2025 par la récession des marges.`}
        n4="Graham critiquait les valorisations EV/EBITDA : 'EBITDA is not cash flow.' L'EBITDA ignore les capex de renouvellement et les variations de BFR — pour les plateformes avec D&A significatifs sur actifs physiques, l'EBITDA peut surestimer la profitabilité réelle."
      />
      <Metric title="Marge EBIT (opérationnelle)"
        desc="Résultat opérationnel après amortissements mais avant intérêts et impôts. Mesure la rentabilité pure de l'exploitation."
        formula="EBIT / CA"
        seuils={{ok:'>10% correct',warn:'0–10% fragile',bad:'<0% déficit opérationnel'}}
        trendCol="operating_margin" fmtF="%" hi={true} thN={0.10}
        n3={`EBIT Mg ${fmtV(RAW.operating_margin?.[YN])} — la phase 2025 avec EBIT négatif signifie que même sans dette, l'activité perd de l'argent sur le plan opérationnel. À distinguer d'une phase d'investissement volontaire (EBITDA positif mais EBIT négatif par D&A élevés) d'une vraie dégradation commerciale.`}
        n4="En IFRS 16, les amortissements des droits d'utilisation s'ajoutent aux D&A et compriment l'EBIT. Pour une plateforme avec peu de locaux propres, l'impact IFRS 16 reste modéré comparé aux chaînes de distribution ou aux hôteliers."
      />
      <Metric title="Marge nette (Net Margin)"
        desc="Part du CA transformée en bénéfice net final. Intègre l'ensemble des éléments : opérationnel, financier, fiscal, exceptionnel."
        formula="Résultat net / CA"
        seuils={{ok:'>8% bon',warn:'2–8% acceptable',bad:'<0% perte'}}
        trendCol="net_margin" fmtF="%" hi={true} thN={0.08}
        n3={`Net Mg ${fmtV(RAW.net_margin?.[YN])} — la trajectoire 2021→2024 montrait une nette amélioration (de −13.1% à +10.6%) avant la rechute 2025 (−6.4%). Cette volatilité reflète la sensibilité du résultat net aux gains/pertes sur investissements financiers (participations) et aux variations de la charge fiscale différée.`}
        n4="Le résultat net IFRS inclut des éléments non-cash importants (réévaluation d'investissements, stock-options). Fridson recommande de retraiter les non-récurrents pour obtenir le 'core earnings'. Pour les conglomérats et plateformes avec des participations financières importantes, ces éléments créent une volatilité significative."
      />
      <Metric title="Marge avant impôts (Pretax Margin)"
        desc="Résultat avant impôts rapporté au CA. L'écart avec la marge nette révèle le poids fiscal effectif."
        formula="Résultat avant impôts / CA"
        seuils={{ok:'>10% correct',warn:'2–10% fragile',bad:'<0% déficit'}}
        trendCol="pretax_margin" fmtF="%" hi={true} thN={0.10}
        n3={`Pretax Mg ${fmtV(RAW.pretax_margin?.[YN])}. L'écart Pretax − Net Mg révèle le taux effectif : en 2024, pretax ${fmtV(RAW.pretax_margin?.[3])} vs net ${fmtV(RAW.net_margin?.[3])} → taux effectif d'environ 5%. Les avantages fiscaux spécifiques et les crédits R&D permettent à l'entreprise de bénéficier d'un taux effectif bien inférieur au taux nominal (25% en Chine).`}
        n4="Le taux d'imposition effectif IFRS peut diverger du taux nominal via les impôts différés actifs/passifs. Fabozzi recommande d'analyser le taux effectif sur 5 ans pour identifier les tendances. Une hausse du taux effectif peut anticiper la perte d'avantages fiscaux."
      />

      {/* 4 — CROISSANCE */}
      <SectionHeader pillarKey="growth" id="grow" num="4" tag="Pilier 3" title="Croissance"/>
      <P>Les ratios de croissance mesurent la <B>dynamique d'expansion</B> de l'entreprise sur ses principales métriques financières. La croissance n'a de valeur que si elle est rentable : Damodaran démontre que croître avec ROIC &lt; WACC détruit de la valeur pour l'actionnaire.<Ref src="Damodaran ch.11"/></P>

      <Metric title="Croissance du CA (Revenue Growth)"
        desc="Taux d'expansion du chiffre d'affaires sur un an. Indicateur premier de l'attractivité commerciale et de la dynamique de marché."
        formula="(CA(n) − CA(n-1)) / CA(n-1)"
        seuils={{ok:'>10% fort',warn:'5–10% acceptable',bad:'<5% ou nég.'}}
        trendCol="revenue_growth" fmtF="%" hi={true} thN={0.05}
        n3={`CA growth ${fmtV(RAW.revenue_growth?.[YN])} — forte décélération depuis ${fmtV(RAW.revenue_growth?.[0])} en 2021. La maturité du marché de la livraison de repas en Chine et la concurrence accrue de Douyin (TikTok Food) pèsent. La croissance reste positive mais converge vers le taux de croissance du marché — signe de maturité, pas de déclin.`}
        n4="La croissance reportée peut être distordue par les acquisitions (inorganique), les effets de périmètre, ou les changements de politique de reconnaissance du CA (IFRS 15). Reilly recommande d'analyser croissance organique et inorganique séparément."
      />
      <Metric title="Croissance du bénéfice net (Net Income Growth)"
        desc="Taux de progression du résultat net. Mesure si la croissance du CA se traduit en enrichissement des actionnaires."
        formula="(RN(n) − RN(n-1)) / |RN(n-1)|"
        seuils={{ok:'>10% bon',warn:'0–10% médiocre',bad:'<0% en recul'}}
        trendCol="net_income_growth" fmtF="%" hi={true} thN={0.05}
        n3={`Net growth ${fmtV(RAW.net_income_growth?.[YN])} — très volatile sur 5 ans (de −600% à +308%). Cette instabilité reflète la sensibilité du résultat net aux éléments non-récurrents (gains/pertes sur investissements financiers) plutôt qu'une vraie volatilité opérationnelle. La lecture en tendance sur 5 ans est plus fiable que le chiffre annuel.`}
        n4="La croissance du RN en base négative peut créer des chiffres aberrants arithmétiquement. Mieux vaut analyser le niveau absolu (RN positif vs négatif) et la tendance de la marge nette que le taux de croissance quand la base est négative."
      />
      <Metric title="Croissance de l'EPS (Earnings Per Share)"
        desc="Taux de progression du bénéfice par action. Intègre les effets dilution (stock-options) et rachats d'actions."
        formula="(EPS(n) − EPS(n-1)) / |EPS(n-1)|"
        seuils={{ok:'>10% bon',warn:'0–10% neutre',bad:'<0% dilution/pertes'}}
        trendCol="eps_growth" fmtF="%" hi={true} thN={0.05}
        n3={`EPS growth ${fmtV(RAW.eps_growth?.[YN])}. Un EPS en progression plus rapide que le RN indique des rachats d'actions (l'entreprise dispose d'un programme de rachat actif). Un EPS en retard sur le RN signale une dilution via stock-options ou convertibles. Pour les plateformes avec des stock-options massifs, surveiller l'EPS dilué vs de base.`}
        n4="Graham recommandait l'EPS normalisé sur 7-10 ans pour lisser les cycles (ce que Shiller appelle le CAPE pour les indices). Pour les plateformes en croissance avec des cycles courts, 5 ans est souvent le maximum disponible."
      />
      <Metric title="Croissance du BVPS (Book Value Per Share)"
        desc="Taux d'accumulation de la valeur comptable par action. Mesure l'enrichissement patrimonial réel de l'actionnaire dans le temps."
        formula="(BVPS(n) − BVPS(n-1)) / BVPS(n-1)"
        seuils={{ok:'>8% bon',warn:'0–8% neutre',bad:'<0% érosion'}}
        trendCol="bvps_growth" fmtF="%" hi={true} thN={0.05}
        n3={`BVPS growth ${fmtV(RAW.bvps_growth?.[YN])} — plus stable que l'EPS car indépendant des non-récurrents. Pour cette entreprise (rétention 100%), le BVPS devrait croître au rythme du ROE. La baisse observée en 2021-2022 reflète les pertes accumulées qui érodent les fonds propres — signal que la création de valeur n'était pas encore au rendez-vous.`}
        n4="Graham utilisait le BVPS comme mesure de la valeur tangible accumulée. Le BVPS IFRS inclut goodwill et incorporels significatifs — il recommandait le Tangible BVPS (= BVPS − goodwill − incorporels) comme mesure conservative de la valeur liquidative réelle."
      />
      <Metric title="Croissance du FCF (Free Cash Flow)"
        desc="Taux de progression du flux de trésorerie libre après investissements. Indicateur-clé pour l'évaluation DCF."
        formula="(FCF(n) − FCF(n-1)) / |FCF(n-1)|"
        seuils={{ok:'>10% bon',warn:'0–10% neutre',bad:'FCF négatif critique'}}
        trendCol="fcf_growth" fmtF="%" hi={true} thN={0.05}
        n3={`FCF growth ${fmtV(RAW.fcf_growth?.[YN])}. La forte volatilité (de −77% à +492% sur 5 ans) s'explique par les cycles capex : les investissements lourds d'Instashopping ont généré des FCF fortement négatifs en 2021-2022, puis leur normalisation a déclenché un rebond massif en 2023-2024. La tendance haussière du FCF est la donnée structurellement importante.`}
        n4="Le FCF peut être manipulé via le timing des paiements fournisseurs (allongement temporaire du DPO améliore le FCF sans créer de valeur réelle). Fridson recommande de reconstruire le FCF depuis le CFO ajusté des variations de BFR ponctuelles."
      />

      {/* 5 — EFFICACITÉ */}
      <SectionHeader pillarKey="efficiency" id="eff" num="5" tag="Pilier 4" title="Efficacité Opérationnelle"/>
      <P>Les ratios d'efficacité mesurent la <B>qualité de la gestion des actifs opérationnels</B> — vitesse d'encaissement, rotation des stocks, délais de paiement. Ils impactent directement le Besoin en Fonds de Roulement (BFR) et donc le FCF réel.<Ref src="Reilly §10.4.2"/></P>

      <Metric title="Fixed Asset Turnover — Rotation des immobilisations"
        desc="Chiffre d'affaires généré par euro d'actifs immobilisés. Mesure l'intensité d'utilisation des investissements physiques."
        formula="CA / Actifs immobilisés nets"
        seuils={{ok:'>5× asset-light',warn:'2–5× standard',bad:'<2× intensif'}}
        trendCol="fixed_asset_turnover" fmtF="x" hi={true} thN={3.0}
        n3={`AT Immo ${fmtX(RAW.fixed_asset_turnover?.[YN])} — très élevé, caractéristique des modèles plateforme (asset-light). Chaque euro d'actif physique génère ~${fmtX(RAW.fixed_asset_turnover?.[YN])} de CA — avantage structurel massif vs un industriel (<3×). La légère baisse en 2025 reflète les investissements Instashopping (entrepôts, véhicules) qui alourdissent mécaniquement la base d'actifs.`}
        n4="Ne pas comparer une plateforme asset-light à un industriel ou un distributeur à logistique lourde — le ratio peut différer de 5-10×. Reilly recommande d'utiliser les actifs nets d'amortissement pour la cohérence inter-entreprises."
      />
      <Metric title="DSO — Days Sales Outstanding (Délai d'encaissement)"
        desc="Nombre moyen de jours entre la vente et l'encaissement. Plus c'est court, moins de capital est immobilisé en créances clients."
        formula="(Créances clients / CA) × 365"
        seuils={{ok:'<30j rapide',warn:'30–60j normal',bad:'>90j long'}}
        trendCol="dso" fmtF="x" hi={false} thN={45}
        n3={`DSO ${fmtJ(RAW.dso?.[YN])} — exceptionnel. Les consommateurs paient quasi instantanément (paiement app) avant livraison. La 'créance' n'existe quasi plus : l'entreprise encaisse instantanément. Ce DSO de ${fmtJ(RAW.dso?.[YN])} est un des principaux générateurs du BFR négatif structurel et améliore directement le FCF.`}
        n4="Le DSO peut être distordu par la saisonnalité du CA (Q4 fort réduit mécaniquement le DSO annuel). Pour les plateformes chinoises, les systèmes de paiement immédiat rendent le DSO quasi incomparable avec les entreprises occidentales qui accordent des délais de 30-60 jours."
      />
      <Metric title="DIO — Days Inventory Outstanding (Délai de stockage)"
        desc="Nombre moyen de jours de stockage des marchandises. Plus court = moins de capital immobilisé en inventaire."
        formula="(Stocks / Coût des ventes) × 365"
        seuils={{ok:'<15j rapide',warn:'15–45j normal',bad:'>90j long'}}
        trendCol="dio" fmtF="x" hi={false} thN={30}
        n3={`DIO ${fmtJ(RAW.dio?.[YN])} — quasi nul car cette entreprise n'est pas un distributeur classique : les stocks (nourriture, épicerie) appartiennent aux marchands partenaires, pas à l'opérateur de la plateforme. Le développement d'Instashopping (épicerie express sur stocks propres) pourrait allonger ce ratio dans les prochaines années — à surveiller.`}
        n4="Le DIO peut être réduit artificiellement si l'entreprise externalise le stock (consignation, drop-shipping). En IFRS, les stocks de consignation ne figurent pas au bilan — ce qui peut sous-estimer le risque réel de rupture ou d'invendus."
      />
      <Metric title="DPO — Days Payables Outstanding (Délai de paiement fournisseurs)"
        desc="Nombre moyen de jours avant paiement des fournisseurs. Un DPO long améliore le BFR — mais reflète aussi le rapport de force."
        formula="(Dettes fournisseurs / Coût des ventes) × 365"
        seuils={{ok:'40–70j optimal',warn:'20–40j ou >80j',bad:'<20j ou >120j'}}
        trendCol="days_payables_outstanding" fmtF="x" hi={false} thN={30}
        n3={`DPO ${fmtJ(RAW.days_payables_outstanding?.[YN])} — dans la norme mais relativement court pour une grande capitalisation. Un allongement à 60-80j améliorerait le BFR et le FCF sans coût direct. Le rapport de force avec les livreurs partenaires (peu substituables à court terme) limite la capacité à imposer des délais plus longs.`}
        n4="Un DPO excessivement long peut signaler des difficultés de trésorerie : l'entreprise retarde les paiements faute de cash. Graham distinguait les entreprises qui allongent le DPO par force (signal de stress) vs par stratégie (signal de pouvoir de marché)."
      />
      <Metric title="CCC — Cash Conversion Cycle (Cycle de conversion cash)"
        desc="Nombre de jours entre le décaissement pour l'achat et l'encaissement de la vente. Négatif = l'entreprise encaisse avant de décaisser."
        formula="DSO + DIO − DPO"
        seuils={{ok:'<0j excellent',warn:'0–30j acceptable',bad:'>60j problématique'}}
        trendCol="cash_conversion_cycle" fmtF="x" hi={false} thN={30}
        n3={`CCC ${fmtJ(RAW.cash_conversion_cycle?.[YN])} — négatif et en amélioration. Un CCC négatif signifie que l'entreprise est financée par ses fournisseurs pendant 27 jours. Sur un CA de ~340 Mds HKD, cela représente ~25 Mds HKD de financement gratuit. C'est l'apanage des grandes plateformes et distributeurs (Amazon, Carrefour ont aussi des CCC négatifs).`}
        n4="Le CCC négatif peut donner une fausse impression : si le CA ralentit brutalement, les dettes fournisseurs exigibles restent mais les encaissements clients diminuent. Fabozzi recommande d'analyser la sensibilité du FCF à une variation de +10j de CCC pour quantifier ce risque."
      />

      {/* 6 — RENTABILITÉ */}
      <SectionHeader pillarKey="rentability" id="rent" num="6" tag="Pilier 5" title="Rentabilité"/>
      <P>Les ratios de rentabilité mesurent le rendement du capital déployé. Ils répondent à la question fondamentale : <B>l'entreprise crée-t-elle plus de valeur qu'elle n'en consomme</B> pour les apporteurs de capitaux ?<Ref src="Damodaran ch.3 · Reilly §10.5"/></P>

      <Metric title="ROE — Return on Equity (+ décomposition DuPont)"
        desc="Rendement des fonds propres. Se décompose en 3 facteurs (DuPont) : marge nette × rotation des actifs × effet de levier."
        formula="Résultat net / Fonds propres  =  Net Mg × Asset TO × Levier FP"
        seuils={{ok:'>12% bon',warn:'6–12% médiocre',bad:'<6% ou nég.'}}
        trendCol="roe" fmtF="%" hi={true} thN={0.12}
        n3={`ROE ${fmtV(RAW.roe?.[YN])}. Décomposition DuPont ${yr} : marge nette ${fmtV(RAW.net_margin?.[YN])} × rotation actifs × levier ${fmtX(RAW.debt_to_equity?.[YN])}. La marge nette négative est le facteur dominant — l'entreprise ne booste pas son ROE via l'endettement (gearing négatif). Le levier est structurellement bas, donc le ROE reflète directement la profitabilité réelle.`}
        n4="Le ROE peut être trompeur en cas de fonds propres négatifs (pertes accumulées dépassant le capital) : il peut alors être artificiellement positif. Graham exigeait ROE > 15% sur 10 ans comme critère de qualité durable."
      />
      <Metric title="ROA — Return on Assets"
        desc="Rendement de l'ensemble des actifs, indépendamment de la structure de financement. Mesure la productivité économique brute."
        formula="Résultat net / Actifs totaux"
        seuils={{ok:'>6% bon',warn:'3–6% médiocre',bad:'<3% ou nég.'}}
        trendCol="roa" fmtF="%" hi={true} thN={0.06}
        n3={`ROA ${fmtV(RAW.roa?.[YN])}. Positif seulement en 2023-2024. Un ROA négatif signifie que même sans dette, l'entreprise serait déficitaire — signal plus fondamental que le ROE car indépendant du levier. Sur ${(((RAW.roa?.[YN]||0) * (RAW.invested_capital?.[YN]||0))/1e9).toFixed(0)} Mds HKD d'actifs, cela représente une perte opérationnelle significative.`}
        n4="Le ROA est sensible à la méthode d'amortissement : accélérer les D&A réduit les actifs nets et améliore mécaniquement le ratio. En IFRS, goodwill et incorporels gonflent le dénominateur — Fabozzi recommande le ROTA pour les comparaisons inter-sectorielles."
      />
      <Metric title="ROIC — Return on Invested Capital"
        desc="Rendement du capital investi dans les opérations. Ratio clé de création de valeur — doit dépasser le WACC pour que la croissance crée de la valeur."
        formula="NOPAT / Capital Investi"
        seuils={{ok:'>WACC (>5%) création',warn:'0–5% neutre',bad:'<0% destruction'}}
        trendCol="roic" fmtF="%" hi={true} thN={0.08}
        n3={`ROIC ${fmtV(RAW.roic?.[YN])} vs WACC ${fmtV(RAW.wacc?.[YN])} → spread ${fmtV(RAW.eva_spread?.[YN])}. Seul 2024 positif au-dessus du WACC. Damodaran : une entreprise avec ROIC < WACC devrait ralentir sa croissance plutôt que l'accélérer — chaque euro de croissance investi détruit de la valeur pour l'actionnaire.`}
        n4="Le Capital Investi est défini différemment selon les analystes (inclure/exclure goodwill, participations, cash excédentaire). Damodaran recommande d'exclure le cash opérationnel. Pour les entreprises avec une trésorerie structurellement excédentaire, cette hypothèse modifie significativement le ROIC calculé."
      />
      <Metric title="ROCE — Return on Capital Employed"
        desc="Rendement du capital employé (fonds propres + dettes LT). Mesure la rentabilité opérationnelle indépendamment de la fiscalité."
        formula="EBIT / (Fonds propres + Dettes LT)"
        seuils={{ok:'>8% solide',warn:'4–8% correct',bad:'<4% ou nég.'}}
        trendCol="roce" fmtF="%" hi={true} thN={0.08}
        n3={`ROCE ${fmtV(RAW.roce?.[YN])} — pour cette entreprise, ROCE ≈ ROIC car la structure de financement est simple (dettes LT quasi nulles). L'EBIT négatif en 2025 rend le ROCE négatif : le capital employé ne génère pas de résultat opérationnel positif après amortissements.`}
        n4="La définition du Capital Employé est hétérogène : certains incluent les dettes CT financières, d'autres non. L'hétérogénéité limite la comparabilité. Reilly recommande de spécifier la définition dans toute analyse formelle."
      />
      <Metric title="ROTA — Return on Tangible Assets"
        desc="Rendement des actifs tangibles, excluant le goodwill et les incorporels. Mesure la rentabilité sur les actifs physiquement réels."
        formula="Résultat net / (Actifs totaux − Goodwill − Incorporels)"
        seuils={{ok:'>5% correct',warn:'2–5% fragile',bad:'<2% ou nég.'}}
        trendCol="rota" fmtF="%" hi={true} thN={0.05}
        n3={`ROTA ${fmtV(RAW.rota?.[YN])} — légèrement supérieur au ROA car le goodwill (acquisition Dianping) réduit la base d'actifs tangibles. Pour les entreprises issues de fusions avec goodwill important, l'écart ROA/ROTA peut être très significatif et révéler si la rentabilité dépend d'actifs immatériels non amortissables.`}
        n4="En IFRS, les incorporels identifiables acquis (relations clients, marques, licences) sont comptabilisés séparément du goodwill. Graham incluait tous les incorporels dans sa 'valeur tangible' — approche la plus conservative pour l'analyse bilancielle."
      />
      <Metric title="CAF/CA — Capacité d'Autofinancement / CA"
        desc="Part du CA convertie en trésorerie opérationnelle avant investissements. Mesure l'autofinancement réel de l'activité."
        formula="Cash Flow Opérationnel / CA"
        seuils={{ok:'>8% solide',warn:'3–8% correct',bad:'<3% ou nég.'}}
        trendCol="opcf_to_sales" fmtF="%" hi={true} thN={0.05}
        n3={`CAF/CA ${fmtV(RAW.opcf_to_sales?.[YN])}. La chute en 2025 (à ${fmtV(RAW.opcf_to_sales?.[YN])}) reflète la détérioration du résultat d'exploitation et les variations négatives du BFR. Un CAF/CA > 5% est le minimum pour financer les investissements courants sans recourir à la dette ou à des cessions d'actifs.`}
        n4="La CAF IFRS inclut les amortissements et variations BFR qui peuvent la déformer conjoncturellement. Fridson recommande d'analyser l'OCF avant variation BFR pour isoler la génération de cash structurelle vs conjoncturelle."
      />

      {/* 7 — CASH FLOW */}
      <SectionHeader pillarKey="cashflow" id="cf" num="7" tag="Pilier 6" title={'Cash Flow — Qualité des flux de trésorerie'}/>
      <P><em style={{color:D.di,fontSize:'.8rem'}}>« Cash is fact, profit is opinion. »</em><Ref src="Fridson ch.8"/><br/>Les ratios de cash flow testent si le bénéfice comptable se matérialise en <B>trésorerie réelle et disponible</B>. Une entreprise profitable sur le papier mais sans cash est une entreprise fragile.</P>

      <Metric title="FCF par action (Free Cash Flow Per Share)"
        desc="Cash libre généré par l'entreprise pour chaque action en circulation. Équivalent 'cash' de l'EPS — base du DCF par action."
        formula="(CFO − Capex) / Nombre d'actions"
        seuils={{ok:'>0 positif',warn:'0 neutre',bad:'<0 consommateur'}}
        trendCol="fcf_per_share" fmtF="x" hi={true} thN={0}
        n3={`FCF/sh ${fmtX(RAW.fcf_per_share?.[YN])}. Fort positif en 2023-2024 (${fmtX(RAW.fcf_per_share?.[3])} en 2024), rechute en 2025 (${fmtX(RAW.fcf_per_share?.[YN])}). Pour la valorisation DCF, c'est le FCF/sh actualisé au WACC qui donne la valeur intrinsèque — pas l'EPS qui inclut des non-cash.`}
        n4="Le FCF/sh est volatile si les capex sont irréguliers (grands projets une année, rien l'année suivante). Reilly recommande le FCF/sh normalisé sur 3-5 ans. Les rachats d'actions réduisent mécaniquement le nombre d'actions et améliorent le FCF/sh apparent."
      />
      <Metric title="FCF Margin (FCF/CA)"
        desc="Part du CA convertie en cash libre après investissements. Mesure l'efficacité de la conversion revenus→cash disponible."
        formula="(CFO − Capex) / CA"
        seuils={{ok:'>8% fort',warn:'3–8% correct',bad:'<0% négatif'}}
        trendCol="fcf_to_sales" fmtF="%" hi={true} thN={0.05}
        n3={`FCF/CA ${fmtV(RAW.fcf_to_sales?.[YN])}. La trajectoire illustre parfaitement le cycle d'investissement : FCF très négatif 2021-2022 (lourds investissements Instashopping et vélos), rebond puissant 2023-2024 à mesure que ces investissements mûrissent, puis rechute 2025 avec la nouvelle phase d'expansion.`}
        n4="Le FCF/CA est sensible à la définition du FCF (capex maintenance vs capex total). Un FCF/CA positif grâce uniquement à une réduction des capex de croissance peut masquer un sous-investissement préoccupant à long terme."
      />
      <Metric title="IQ — Income Quality (Qualité des bénéfices)"
        desc="Rapport entre le cash opérationnel et le bénéfice net. Mesure si les profits comptables se matérialisent réellement en trésorerie."
        formula="CFO / Résultat net"
        seuils={{ok:'>1× excellent',warn:'0.5–1× acceptable',bad:'<0.5× accruals élevés'}}
        trendCol="income_quality" fmtF="x" hi={true} thN={0.8}
        n3={`IQ ${fmtX(RAW.income_quality?.[YN])}. Un IQ > 1 signifie que l'entreprise génère plus de cash opérationnel que de bénéfice net comptable — signal de qualité (peu d'accruals). La forte volatilité historique (de ${fmtX(RAW.income_quality?.[0])} à ${fmtX(RAW.income_quality?.[3])}) reflète les variations importantes du BFR et les éléments exceptionnels non-cash.`}
        n4="Fridson distingue les accruals opérationnels (variation BFR, naturelle) des accruals comptables (provisions, reconnaissance de CA). Les seconds sont le principal signal d'alerte de manipulation comptable. Un IQ chroniquement < 0.5 sur 3 ans est un signal sérieux à investiguer."
      />
      <Metric title="FCF/OCF — Intensité Capex"
        desc="Part du cash opérationnel qui reste après les investissements. Mesure quelle fraction du CFO est absorbée par les capex."
        formula="FCF / CFO = (CFO − Capex) / CFO"
        seuils={{ok:'>0.7× léger en capex',warn:'0.4–0.7× standard',bad:'<0.4× capex élevés'}}
        trendCol="fcf_to_opcf" fmtF="x" hi={true} thN={0.5}
        n3={`FCF/OCF ${fmtX(RAW.fcf_to_opcf?.[YN])}. Un ratio > 1 (comme en 2021 et 2025) peut sembler paradoxal — il résulte de variations BFR très favorables qui gonflent le FCF au-delà du CFO. En 2024, le ratio ${fmtX(RAW.fcf_to_opcf?.[3])} indique que 80% du cash opérationnel reste disponible après capex — profil très favorable.`}
        n4="Un FCF/OCF > 1 mérite investigation : soit le CFO est négatif (et le FCF encore plus négatif), soit des cessions d'actifs créent un 'FCF' positif artificiel. Fabozzi recommande d'analyser les composantes du CFO avant de conclure."
      />

      {/* 8 — SOLVABILITÉ */}
      <SectionHeader pillarKey="solvency" id="solv" num="8" tag="Pilier 7" title="Solvabilité"/>
      <P>Les ratios de solvabilité mesurent la <B>viabilité financière à long terme</B> : capacité à honorer les engagements de dette, solidité du bilan, marge de sécurité face aux chocs économiques. À distinguer de la liquidité (court terme).<Ref src="Fabozzi ch.9 · Graham ch.17"/></P>

      <Metric title="DN/EBITDA — Levier financier bancaire"
        desc="Nombre d'années d'EBITDA nécessaires pour rembourser la dette nette. Standard d'analyse bancaire et des agences de notation."
        formula="(Dettes financières − Trésorerie) / EBITDA"
        seuils={{ok:'<2× investment grade',warn:'2–4× vigilance',bad:'>4× stress'}}
        trendCol="net_debt_to_ebitda" fmtF="x" hi={false} thN={3.0}
        n3={`DN/EBITDA ${fmtX(RAW.net_debt_to_ebitda?.[YN])} — la dette nette est négative (trésorerie > dette financière), ce qui rend ce ratio négatif et non conventionnellement applicable. l'entreprise est en position créditrice nette : elle prête plus qu'elle n'emprunte. Ratio non calculable quand l'EBITDA est négatif (2021, 2022, 2025).`}
        n4="L'EBITDA utilisé dans les covenants bancaires est souvent l'EBITDA ajusté (hors one-off) — pouvant diverger de l'EBITDA reporté. Fabozzi recommande d'utiliser l'EBITDA LTM (12 derniers mois) pour les comparaisons en temps réel."
      />
      <Metric title="ICR — Interest Coverage Ratio (Couverture des intérêts)"
        desc="Capacité à payer les charges d'intérêts avec le résultat opérationnel. Test de résistance de base pour les créanciers."
        formula="EBIT / Charges d'intérêts nettes"
        seuils={{ok:'>5× très solide',warn:'3–5× correct',bad:'<1.5× fragile'}}
        trendCol="interest_coverage" fmtF="x" hi={true} thN={3.0}
        n3={`ICR ${fmtX(RAW.interest_coverage?.[YN])} — négatif en 2025 car l'EBIT est négatif (EBIT ${fmtV(RAW.operating_margin?.[YN])} × CA). Ce n'est pas une insuffisance quantitative de la dette mais une insuffisance qualitative de l'exploitation : l'EBIT ne couvre pas les intérêts. La forte trésorerie rend toutefois le risque de défaut quasi nul à court terme.`}
        n4="Graham exigeait ICR > 5× sur 7 ans pour qualifier une obligation d'investment grade. Certains analystes utilisent EBITDA / intérêts (ratio plus favorable car avant D&A). En IFRS 16, les charges d'intérêts locatifs s'ajoutent aux intérêts financiers."
      />
      <Metric title="Gearing Ratio — Levier net"
        desc="Rapport entre la dette nette et les fonds propres. Mesure le levier financier de l'entreprise."
        formula="(Dettes financières nettes) / Fonds propres"
        seuils={{ok:'<0.5× faible',warn:'0.5–1× modéré',bad:'>1× élevé'}}
        trendCol="gearing_ratio" fmtF="x" hi={false} thN={1.0}
        n3={`Gearing ${fmtX(RAW.gearing_ratio?.[YN])} — négatif car trésorerie > dette financière. Position créditrice nette qui offre une flexibilité stratégique maximale : rachats d'actions, acquisitions, investissements opportunistes peuvent être financés sans dilution ni hausse du levier. Avantage compétitif rare pour une entreprise de cette croissance.`}
        n4="Le gearing UK (dette nette / FP) et américain (D/E = dette totale / FP) se calculent différemment. L'IFRS 16 gonfle les dettes via les obligations locatives — un gearing négatif peut paraître moins favorable après retraitement complet."
      />
      <Metric title="Autonomie financière"
        desc="Part des actifs financée par les fonds propres. Mesure l'indépendance financière vis-à-vis des créanciers."
        formula="Fonds propres / Actifs totaux"
        seuils={{ok:'>50% solide',warn:'30–50% correct',bad:'<30% fragile'}}
        trendCol="financial_autonomy" fmtF="%" hi={true} thN={0.50}
        n3={`Autonomie ${fmtV(RAW.financial_autonomy?.[YN])} — en légère baisse en 2025 reflétant la compression des FP (pertes accumulées). Mais un ratio de ${fmtV(RAW.financial_autonomy?.[YN])} signifie que près de la moitié des actifs est financée par les fonds propres — solide pour une entreprise de cette taille en phase de croissance active.`}
        n4="Le D/A IFRS inclut les passifs locatifs (IFRS 16) qui ne représentent pas une dette financière au sens bancaire. Retraiter en excluant les passifs IFRS 16 pour une vision plus économique du levier réel."
      />
      <Metric title="Solvency Ratio — Couverture des engagements"
        desc="Rapport entre les fonds propres et la totalité des dettes. Mesure directe de la couverture bilancielle en cas de liquidation."
        formula="Fonds propres / Dettes totales"
        seuils={{ok:'>1.5× très solide',warn:'0.8–1.5× correct',bad:'<0.5× risque insolvabilité'}}
        trendCol="solvency_ratio" fmtF="x" hi={true} thN={1.0}
        n3={`Solvency ${fmtX(RAW.solvency_ratio?.[YN])} — les FP couvrent environ ${((RAW.solvency_ratio?.[YN]||0)*100).toFixed(0)}% des dettes totales. En cas de liquidation hypothétique, les créanciers seraient intégralement remboursés avec une marge de sécurité confortable. Baisse progressive depuis 2021 (${fmtX(RAW.solvency_ratio?.[0])}) mais niveau encore solide.`}
        n4="La valeur de liquidation réelle peut diverger de la valeur comptable. Fabozzi recommande d'analyser la nature des actifs (trésorerie vs immobilisations vs incorporels) pour estimer la valeur de réalisation réelle en cas de vente forcée."
      />

      {/* 9 — ALLOCATION CAPITAL */}
      <SectionHeader pillarKey="capalloc" id="capalloc" num="9" tag="Pilier 8" title="Allocation du Capital"/>
      <P>L'allocation du capital mesure la <B>qualité des décisions d'investissement, de financement et de distribution</B>. C'est la discipline qui transforme le cash opérationnel en valeur durable — ou la détruit si le ROIC reste sous le WACC.<Ref src="Damodaran ch.11"/></P>

      <Metric title="Spread ROIC−WACC — Moteur de création de valeur"
        desc="Différence entre le rendement du capital investi et son coût. Positif = chaque euro investi crée de la valeur. Négatif = chaque euro investi en détruit."
        formula="ROIC − WACC"
        seuils={{ok:'>2% création',warn:'0–2% neutre',bad:'<0% destruction'}}
        trendCol="roic_vs_wacc" fmtF="%" hi={true} thN={0}
        n3={`Spread ${fmtV(RAW.roic_vs_wacc?.[YN])} en ${yr}. Positif seulement en 2023 (+2.2%) et 2024 (+12.2%), négatif les autres années. Damodaran : si ROIC > WACC, maximiser la croissance (elle crée de la valeur). Si ROIC < WACC, ralentir et distribuer le cash. L'arbitrage croissance/dividendes dépend entièrement de ce spread.`}
        n4="Le WACC est une estimation (CAPM). Une variation de ±1% du WACC peut faire basculer le spread. Damodaran publie ses estimations de WACC par secteur et pays — référence standard incluant le country risk premium HK/Chine (1-2% supplémentaires)."
      />
      <Metric title="Taux de rétention (Retention Rate)"
        desc="Part des bénéfices conservés (non distribués). Complément du payout ratio — mesure le réinvestissement potentiel."
        formula="1 − Payout ratio  =  1 − (Dividendes / Résultat net)"
        seuils={{ok:'>50% réinvestissement',warn:'20–50% équilibré',bad:'Rétention forcée (pertes)'}}
        trendCol="retention_rate" fmtF="%" hi={true} thN={0.50}
        n3={`Rétention ${fmtV(RAW.retention_rate?.[YN])} — 100% sur toute la période. Pas de dividendes versés. Cohérent pour une entreprise en phase d'investissement. Mais Damodaran rappelle : si ROIC < WACC, retenir les bénéfices détruit de la valeur — il vaudrait mieux distribuer. En 2025, l'entreprise retient des pertes — rétention forcée, pas stratégique.`}
        n4="Distinction clé : rétention stratégique (ROIC > WACC, opportunités de croissance créatrices) vs rétention forcée (pertes ou manque de liquidités). Graham était sceptique sur les entreprises qui retiennent 100% sans démontrer un ROIC élevé sur la durée."
      />
      <Metric title="Capex/D&A — Politique d'investissement"
        desc="Rapport entre les investissements et les amortissements. Inférieur à 1 = sous-investissement. Supérieur à 1 = phase d'expansion."
        formula="Capex / Dotations aux amortissements"
        seuils={{ok:'1–2× expansion saine',warn:'0.5–1× renouvellement',bad:'<0.5× sous-investissement'}}
        trendCol="capex_to_depreciation" fmtF="x" hi={false} thN={1.0}
        n3={`Capex/D&A ${fmtX(RAW.capex_to_depreciation?.[YN])} en ${yr} — les capex représentent quasi 2× les D&A, confirmant une phase d'expansion active des infrastructures physiques (entrepôts Instashopping, flotte de livraison). La hausse progressive depuis 2021 (${fmtX(RAW.capex_to_depreciation?.[0])}) indique une accélération des investissements.`}
        n4="En IFRS, la R&D capitalisée peut gonfler les capex. Damodaran distingue capex maintenance (renouvellement des actifs existants) et capex croissance (expansion) — seul le second justifie une valorisation premium dans un DCF."
      />
      <Metric title="DSCR — Debt Service Coverage Ratio"
        desc="Capacité à rembourser intérêts ET principal avec le cash opérationnel. Indicateur bancaire standard pour l'accord de crédit."
        formula="CFO / (Remboursements de dette + Intérêts)"
        seuils={{ok:'>2× très solide',warn:'1.25–2× correct',bad:'<1.25× risque'}}
        trendCol="debt_service_coverage" fmtF="x" hi={true} thN={1.5}
        n3={`DSCR ${fmtX(RAW.debt_service_coverage?.[YN])} — solide malgré la dégradation en 2025. Pour cette entreprise (dettes financières quasi nulles), le DSCR est principalement lié aux obligations locatives IFRS 16 et aux éventuels emprunts CT. La forte trésorerie compense largement le CFO négatif de 2025.`}
        n4="Le DSCR est le critère standard des banques pour l'accord de crédit (covenant minimum : DSCR > 1.25×). Les variations de BFR peuvent distordre le CFO et donc le DSCR d'une année à l'autre. Fabozzi recommande d'utiliser un CFO normalisé (moyenne 3 ans) au numérateur."
      />
      <Metric title="ESR — Equity Service Ratio"
        desc="Capacité à financer la rémunération des actionnaires (dividendes + rachats) avec le FCF généré. Mesure la soutenabilité de la politique de distribution."
        formula="FCF / (Dividendes + Rachats d'actions)"
        seuils={{ok:'>1.5× soutenable',warn:'1–1.5× équilibré',bad:'<1× insoutenable'}}
        trendCol="equity_service_ratio" fmtF="x" hi={true} thN={1.0}
        n3={`ESR ${fmtX(RAW.equity_service_ratio?.[YN])} en ${yr}. Le programme de rachat d'actions (actif depuis 2023) est financé par le FCF quand il est positif (2024 : ESR ${fmtX(RAW.equity_service_ratio?.[3])}) mais dépasse le FCF disponible en 2025 (FCF négatif). La trésorerie abondante sécurise ce déséquilibre ponctuel.`}
        n4="L'ESR est moins standardisé que le DSCR et sa définition varie selon les analystes (inclure/exclure les rachats, utiliser FCF ou CFO). Reilly recommande d'inclure les rachats car ils constituent un vrai décaissement, indépendamment de leur traitement comptable."
      />

      {/* 10 — CRÉATION DE VALEUR */}
      <SectionHeader pillarKey="value_creation" id="valeur" num="10" tag="Pilier 9" title="Création de Valeur Économique"/>
      <P>La création de valeur est le <B>test ultime de l'investissement</B> : le rendement obtenu justifie-t-il le risque pris par les apporteurs de capitaux ? C'est la convergence de tous les piliers précédents en une seule mesure synthétique.<Ref src="Damodaran ch.11"/></P>

      <Metric title="WACC — Coût Moyen Pondéré du Capital"
        desc="Taux de rendement minimum que l'entreprise doit générer pour satisfaire l'ensemble de ses apporteurs de capitaux (actionnaires + créanciers)."
        formula="Kd×(1-t)×(D/V) + Ke×(E/V)"
        seuils={{ok:'<8% coût maîtrisé',warn:'8–12% élevé',bad:'>12% très élevé'}}
        trendCol="wacc" fmtF="%" hi={false} thN={0.10}
        n3={`WACC ${fmtV(RAW.wacc?.[YN])} — stable autour de 4.9%. Pour cette entreprise (quasi sans dette), WACC ≈ Ke (coût des fonds propres) = taux sans risque HK + prime de risque marché × beta. Un WACC bas permet à l'entreprise de créer de la valeur avec un ROIC relativement modeste. C'est un avantage structurel des grandes capitalisations liquides.`}
        n4="Le WACC est une estimation sujette à de nombreuses hypothèses (beta, prime de risque marché, taux sans risque). Une variation de ±1% peut faire basculer le spread ROIC-WACC. Damodaran publie ses estimations actualisées par secteur et pays — référence académique standard."
      />
      <Metric title="EVA — Economic Value Added"
        desc="Valeur économique créée ou détruite en valeur absolue. Intègre à la fois la rentabilité du capital et son coût."
        formula="EVA = (ROIC − WACC) × Capital Investi"
        seuils={{ok:'EVA > 0 création',bad:'EVA < 0 destruction'}}
        trendCol="eva_absolute" fmtF="x" hi={true} thN={0}
        n3={`EVA ${RAW.eva_absolute?.[YN]!=null?(RAW.eva_absolute[YN]/1e9).toFixed(1)+' Mds HKD':'—'} en ${yr}. Cumul sur 5 ans : ${YEARS.map((_,i)=>RAW.eva_absolute?.[i]!=null?(RAW.eva_absolute[i]/1e9).toFixed(1)+'Mds':'—').join(' + ')}. La destruction nette sur 5 ans est significative, partiellement compensée par la création de valeur en 2023-2024. Chaque euro de capital investi a globalement détruit de la valeur sur la période.`}
        n4="L'EVA dépend fortement de la définition du Capital Investi et du NOPAT. Damodaran recommande de retraiter en excluant les actifs financiers non opérationnels et les éléments non-récurrents du NOPAT. Stern Stewart (inventeurs de l'EVA) proposent plus de 160 ajustements — en pratique, 5-10 suffisent."
      />
      <Metric title="NOPAT — Net Operating Profit After Tax"
        desc="Résultat opérationnel net d'impôt, avant tout effet de levier. Mesure la performance économique pure de l'exploitation."
        formula="EBIT × (1 − taux d'imposition effectif)"
        seuils={{ok:'NOPAT > 0 rentable',bad:'NOPAT < 0 déficitaire'}}
        trendCol="nopat" fmtF="x" hi={true} thN={0}
        n3={`NOPAT ${RAW.nopat?.[YN]!=null?(RAW.nopat[YN]/1e9).toFixed(1)+' Mds HKD':'—'} en ${yr}. Positif seulement en 2024 (${RAW.nopat?.[3]!=null?(RAW.nopat[3]/1e9).toFixed(1)+' Mds HKD':'—'}). Le NOPAT positif est la condition nécessaire — mais pas suffisante — pour que le ROIC dépasse le WACC. Sans NOPAT positif, aucune création de valeur n'est possible.`}
        n4="Damodaran recommande de retraiter le NOPAT en excluant les revenus financiers et les résultats non-récurrents. Utiliser le taux d'imposition marginal (pas le taux effectif annuel trop volatile) pour le calcul du NOPAT normalisé."
      />
      <Metric title="Croissance soutenable (g = ROE × Rétention)"
        desc="Taux maximum de croissance finançable par les seuls bénéfices retenus, sans augmentation de capital ni hausse de levier."
        formula="g = ROE × (1 − Payout ratio)"
        seuils={{ok:'>7% fort',warn:'3–7% modéré',bad:'<3% ou nég.'}}
        trendCol="sustainable_growth_rate" fmtF="%" hi={true} thN={0.05}
        n3={`g sout. ${fmtV(RAW.sustainable_growth_rate?.[YN])} en ${yr}. La trésorerie abondante permet à l'entreprise de dépasser mécaniquement ce taux en mobilisant le cash existant. Damodaran : une entreprise qui croît au-delà de g sout. pendant plusieurs années doit nécessairement émettre des dettes ou des actions pour financer l'écart.`}
        n4="La formule de Gordon-Shapiro suppose un ROE stable et un payout constant — hypothèse irréaliste pour les entreprises en forte croissance ou en restructuration. Utiliser comme borne théorique, pas comme prévision."
      />

      {/* 11 — VALORISATION */}
      <Sec id="valuation" num="11" tag="Bonus" title="Valorisation — Multiples de marché"/>
      <P>Les multiples comparent le prix de marché aux fondamentaux. Leur lecture exige toujours une <B>comparaison sectorielle et temporelle</B> — un PE de 20× peut être cher pour un industriel mature et bon marché pour une plateforme en forte croissance.<Ref src="Damodaran ch.17 · Graham §VI"/></P>

      <Metric title="PE Ratio — Price to Earnings"
        desc="Prix payé pour un euro de bénéfice net. Multiple le plus suivi par le marché, mais limité par sa sensibilité aux non-récurrents."
        formula="Prix par action / BPA"
        seuils={{ok:'<20× raisonnable',warn:'20–35× élevé',bad:'>35× ou nég.'}}
        n3={`PE TTM ${fmtX(RAW.pe_ttm?.[YN] ?? null)} — négatif ou non significatif en ${yr} car le résultat net est négatif. Pour les entreprises cycliquement déficitaires, Graham recommandait d'utiliser un PE sur bénéfices normalisés (moyenne 7-10 ans) plutôt que le PE ponctuel, qui peut induire en erreur.`}
        n4="Graham préférait le Shiller PE (CAPE sur 10 ans) pour lisser les cycles. Pour les plateformes HK/Chine avec des stock-options agressifs, le BPA dilué peut diverger du BPA de base de 15-30% — toujours utiliser le BPA dilué pour la comparaison."
      />
      <Metric title="PEG Ratio — Price/Earnings to Growth"
        desc="Ajuste le PE par le taux de croissance des bénéfices. Permet de comparer des entreprises à croissances différentes."
        formula="PE / Taux de croissance EPS (%)"
        seuils={{ok:'<1 sous-valorisé',warn:'1–2 correctement valorisé',bad:'>2 cher'}}
        n3="Le PEG (popularisé par Peter Lynch) ajuste le PE par la croissance attendue : un PEG de 0.5 avec 20% de croissance est plus attractif qu'un PEG de 1 avec 10%. Non calculable si le PE est négatif (pertes) ou si la croissance EPS est négative — ce qui est souvent le cas pour les plateformes en phase d'investissement."
        n4="La croissance utilisée au dénominateur varie selon les analystes (historique vs prévision vs moyenne). Reilly recommande d'utiliser la croissance historique 5 ans comme plancher conservatif — les prévisions des analystes sont trop souvent optimistes."
      />
      <Metric title="P/B — Price to Book Value"
        desc="Prime payée sur la valeur comptable. Mesure si le marché valorise un goodwill économique (marque, réseau, avantage compétitif)."
        formula="Prix par action / BVPS"
        seuils={{ok:'<2× value',warn:'2–5× croissance',bad:'>8× spéculatif'}}
        n3={`PB ${fmtX(RAW.pb_ttm?.[YN] ?? null)}. Pour une plateforme (peu d'actifs tangibles), un PB élevé est normal — il reflète la valeur des actifs immatériels non comptabilisés (base d'utilisateurs, algorithmes, données, réseau d'effet réseau). La valeur économique réelle dépasse largement la valeur comptable.`}
        n4="Graham préférait le Tangible Book Value Par Share (= BVPS − goodwill − incorporels) comme mesure conservative. Le Book Value IFRS peut inclure des OCI (pertes latentes sur portefeuilles) et des impôts différés qui brouillent la lecture."
      />
      <Metric title="FCF Yield — Rendement du Cash Flow Libre"
        desc="Rendement en cash libre de l'investissement. Équivalent du rendement obligataire pour les actions — mesure le rendement 'réel' payé par le marché."
        formula="FCF / Capitalisation boursière"
        seuils={{ok:'>5% attractif',warn:'2–5% neutre',bad:'<2% ou nég.'}}
        n3={`FCF Yield ${fmtV(RAW.fcf_yield?.[YN] ?? null)} en ${yr}. L'inverse du Price/FCF — mesure le rendement en cash pour chaque euro de capitalisation boursière. Pour les investisseurs value, un FCF Yield > 5% est le seuil d'attractivité — comparer au taux sans risque (OAT 10 ans ~3%) pour mesurer la prime de risque implicite.`}
        n4="Sensible à la définition du FCF et à la capitalisation boursière (flottante ou totale). Un FCF Yield basé sur le FCF avant variations BFR est plus stable sur longue période. Reilly recommande d'utiliser la capitalisation totale (incluant minoritaires) pour la cohérence avec l'EV/EBITDA."
      />
      <Metric title="EV/EBITDA — Enterprise Value Multiple"
        desc="Multiple de valeur d'entreprise sur EBITDA. Neutre à la structure financière — standard de comparaison M&A et cross-sectoriel."
        formula="(Capitalisation + Dette nette) / EBITDA"
        seuils={{ok:'<15× raisonnable',warn:'15–25× élevé',bad:'>25× ou nég.'}}
        n3={`EV/EBITDA ${fmtX(RAW.ev_to_ebitda_ttm?.[YN] ?? null)} en ${yr} — non calculable sur les années à EBITDA négatif (EBITDA négatif). En 2024 (EBITDA positif à ${fmtV(RAW.ebitda_margin?.[3])}% du CA), l'EV/EBITDA reflète la prime de croissance que le marché accorde à cette entreprise par rapport à ses comparables régionaux.`}
        n4="L'EBITDA ajusté publié par les entreprises exclut souvent stock-options, restructurations et one-off — l'EV/EBITDA publié peut être plus favorable que l'EV/EBITDA calculé depuis les comptes. Graham recommandait de toujours calculer depuis les comptes bruts."
      />
      <Metric title="Graham Number — Valeur intrinsèque conservative"
        desc="Approximation conservative de la valeur intrinsèque basée sur les critères de sécurité de Graham (PE < 15 et PB < 1.5)."
        formula="√(22.5 × BPA × BVPS)"
        seuils={{ok:'Cours < Graham Nb',bad:'Cours > Graham Nb'}}
        n3={`Graham Number ${fmtV(RAW.grahamNumber?.[YN] ?? null, 'x')} pour cette entreprise — non calculable quand le BPA est négatif. Le critère PE×PB < 22.5 (= PE < 15 et PB < 1.5) est très conservatif pour une plateforme en croissance active. La méthode Graham est mieux adaptée aux entreprises industrielles matures avec des bénéfices stables.`}
        n4="La méthode Graham date de 1934 et a été conçue pour les marchés de la Grande Dépression. Son application directe aux plateformes numériques asiatiques est contestée — les structures d'actifs sont radicalement différentes. Utiliser comme borne conservative uniquement, jamais comme seul critère d'investissement."
      />

      {/* 12 — SYNTHÈSE */}
      <Sec id="synth" num="12" tag="Synthèse" title="FScore — Architecture des 9 piliers"/>
      <P>Le FScore agrège les 9 piliers selon une pondération académique reflétant leur <B>importance relative dans la création de valeur à long terme</B>. Les piliers les plus pondérés (Rentabilité, Cash Flow, Croissance) sont aussi ceux qui ont le plus de pouvoir prédictif sur la performance boursière à 5 ans.<Ref src="Damodaran · Reilly · Fabozzi"/></P>

      <div style={{border:`1px solid ${D.bo}`,borderRadius:8,overflow:'hidden',margin:'.5rem 0'}}>
        <div className="course-score-grid" style={{display:'grid',gridTemplateColumns:'1fr 32px 60px 48px',background:D.su,
          borderBottom:`1px solid ${D.bo}`,fontFamily:"ui-monospace,monospace",fontSize:'.52rem',color:D.mu,
          textTransform:'uppercase',letterSpacing:'.06em'}}>
          {['Pilier','%','Score','Bar'].map(h=>(
            <div key={h} style={{padding:'4px 8px',textAlign:h==='Pilier'?'left':'right'}}>{h}</div>
          ))}
        </div>
        {[
          ['Rentabilité','ROE, ROA, ROIC, ROCE, ROTA, CAF/CA',22.5,'rentability'],
          ['Cash Flow','FCF/sh, FCF/CA, OCF/CA, IQ, FCF/OCF',15.0,'cashflow'],
          ['Croissance','CA, Net, EPS, BVPS, FCF',15.0,'growth'],
          ['Profitabilité','Gross Mg, EBITDA, EBIT, Net Mg',13.5,'profitability'],
          ['Création Valeur','WACC, EVA, NOPAT, g sout.',12.0,'value_creation'],
          ['Alloc. Capital','Spread, Rétention, Capex/D&A, DSCR',10.0,'capalloc'],
          ['Solvabilité','DN/EBITDA, ICR, Gearing, D/A',9.0,'solvency'],
          ['Efficacité','DSO, DIO, CCC, DPO, Asset TO',4.0,'efficiency'],
          ['Liquidité','Current, Quick, Cash Ratio',4.0,'liquidity'],
        ].map(([p,m,w,k])=>{
          const s=RAW[`${k}_score`]?.[YN];
          const hc=heatColor(s||0);
          return (
            <div className="course-score-grid" key={p} style={{display:'grid',gridTemplateColumns:'1fr 32px 60px 48px',
              borderBottom:`1px solid ${D.bo}35`,alignItems:'center'}}>
              <div style={{padding:'5px 8px'}}>
                <div style={{fontFamily:"ui-monospace,monospace",fontSize:'.60rem',color:D.tx,fontWeight:700}}>{p}</div>
                <div style={{fontFamily:"ui-monospace,monospace",fontSize:'.54rem',color:D.mu,marginTop:1}}>{m}</div>
              </div>
              <div style={{padding:'5px 4px',fontFamily:"ui-monospace,monospace",fontSize:'.60rem',color:D.go,textAlign:'right',fontWeight:700}}>{w}%</div>
              <div style={{padding:'5px 6px',fontFamily:"ui-monospace,monospace",fontSize:'.65rem',color:hc,fontWeight:700,textAlign:'right'}}>{s?.toFixed(0)||'—'}</div>
              <div style={{padding:'5px 6px'}}>
                <div style={{height:5,background:D.bo,borderRadius:4,overflow:'hidden'}}>
                  <div style={{width:`${s||0}%`,height:'100%',background:hc,borderRadius:4}}/>
                </div>
              </div>
            </div>
          );
        })}
        <div className="course-score-grid" style={{display:'grid',gridTemplateColumns:'1fr 32px 60px 48px',
          background:D.su,borderTop:`2px solid ${D.go}40`}}>
          <div style={{padding:'6px 8px',fontFamily:"ui-monospace,monospace",fontSize:'.62rem',color:D.tx,fontWeight:800}}>FScore Total</div>
          <div/>
          <div style={{padding:'6px 6px',fontFamily:"ui-monospace,monospace",fontSize:'.72rem',color:heatColor(RAW.fscore?.[YN]||0),fontWeight:800,textAlign:'right'}}>{RAW.fscore?.[YN]?.toFixed(1)||'—'}</div>
          <div/>
        </div>
      </div>
      <Bx label="Règle de lecture combinée" color={D.go}>
        <div style={{display:'flex',flexDirection:'column',gap:7}}>
          <div><span style={{fontFamily:"ui-monospace,monospace",fontSize:'.55rem',fontWeight:700,color:D.gb}}>FScore &gt; 75 · ROIC &gt; WACC · FCF positif</span><br/>Profil idéal : croissance créatrice de valeur et autofinancée. Signal fort d'accumulation.</div>
          <div><span style={{fontFamily:"ui-monospace,monospace",fontSize:'.55rem',fontWeight:700,color:D.am}}>FScore 50–75</span><br/>Identifier le pilier dominant. Vérifier si la dégradation est conjoncturelle (investissement temporaire) ou structurelle (business model fragile).</div>
          <div><span style={{fontFamily:"ui-monospace,monospace",fontSize:'.55rem',fontWeight:700,color:D.rb}}>FScore &lt; 40</span><br/>Analyser les risques de continuité (ICR, autonomie, trésorerie) avant toute décision. La qualité prime sur la valorisation apparente.</div>
        </div>
      </Bx>
      <Bx label={`Interprétation ${COMPANY_META.ticker} ${yr} — FScore ${RAW.fscore?.[YN]?.toFixed(1)}/100`} color={heatColor(RAW.fscore?.[YN]||0)}>
        {FSCORE_SUMMARY[yr]}
      </Bx>

    </div>
  );

  // ── RENDU FINAL : sidebar en overlay (fermée par défaut), sur le modèle MarketStructureCard ──
  return (
    <div style={{background:D.bg,height:'100vh',overflow:'hidden',position:'relative'}}>

      {/* ── CONTENU scrollable pleine largeur ── */}
      <div id="course-scroll-container" style={{fontFamily:"'Cormorant Garamond',serif",overflowY:'auto',height:'100vh',overflowX:'hidden'}}>
        <div style={{maxWidth:780,margin:'0 auto',padding:'0 clamp(.7rem,3vw,2rem) 5rem'}}>
          <Content/>
        </div>
      </div>

      {/* ── Backdrop : ferme la sidebar ── */}
      {menuOpen && (
        <div onClick={()=>setMenuOpen(false)} style={{
          position:'absolute',inset:0,zIndex:1100,background:'rgba(0,0,0,.35)'
        }}/>
      )}

      {/* ── Sidebar overlay chapitres ── */}
      {menuOpen && (
        <div onClick={e=>e.stopPropagation()} style={{
          position:'absolute',top:0,left:0,bottom:0,width:'min(280px,80vw)',zIndex:1200,
          background:D.su,borderRight:`1px solid ${D.bo}`,
          display:'flex',flexDirection:'column',boxShadow:'4px 0 24px rgba(0,0,0,.4)',
        }}>
          <div style={{padding:'12px 16px 10px',borderBottom:`1px solid ${D.bo}`,
            display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
            <span style={{fontFamily:"ui-monospace,monospace",fontSize:'.5rem',color:D.di,textTransform:'uppercase',
              letterSpacing:'.15em',fontWeight:700}}>Chapitres</span>
            <button onClick={()=>setMenuOpen(false)} style={{width:24,height:24,display:'flex',
              alignItems:'center',justifyContent:'center',background:'transparent',border:'none',
              cursor:'pointer',fontSize:15,color:D.di}}>×</button>
          </div>
          <div style={{flex:1,overflowY:'auto',padding:'6px 0'}}>
            {CHAPTERS.map(c=>(
              <button id={`sidebar-ch-${c.id}`} key={c.id} onClick={()=>scrollTo(c.id)} style={{
                width:'100%',textAlign:'left',padding:'10px 16px',
                background:activeChap===c.id?`${D.go}12`:'transparent',
                border:'none',borderLeft:activeChap===c.id?`3px solid ${D.go}`:'3px solid transparent',
                cursor:'pointer',display:'flex',gap:10,alignItems:'baseline',
                transition:'all .15s',
              }}>
                <span style={{fontFamily:"ui-monospace,monospace",fontSize:'.5rem',color:activeChap===c.id?D.go:D.mu,
                  fontWeight:700,flexShrink:0,minWidth:14,textAlign:'right'}}>{c.num}</span>
                <span style={{fontFamily:"ui-monospace,monospace",fontSize:'.7rem',
                  color:activeChap===c.id?D.tx:D.di,
                  fontWeight:activeChap===c.id?700:500,lineHeight:1.45}}>{c.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── FAB ☰ bas-droite : ouvre/ferme la sidebar ── */}
      <button onClick={()=>setMenuOpen(o=>!o)} style={{
        position:'absolute',bottom:24,right:20,zIndex:1300,
        width:52,height:52,borderRadius:16,
        background:D.su,color:D.go,
        border:`1px solid ${D.bo}`,boxShadow:'0 4px 20px rgba(0,0,0,.4)',
        display:'flex',alignItems:'center',justifyContent:'center',
        cursor:'pointer',fontSize:22,
      }}>
        {menuOpen ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : '☰'}
      </button>
    </div>
  );
}
function CourseModal({ onClose, dark }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const h = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', h); };
  }, [onClose]);

  const navBg  = dark ? '#0c0b09' : '#f5f2ec';
  const navBdr = dark ? '#1e1e26' : '#d4cfc4';
  const btnBdr = dark ? '#2a2a36' : '#c4bfb4';
  const btnCo  = dark ? '#8a8680' : '#5a5650';
  const logoCo = dark ? '#7a5e28' : '#6a5020';
  const titleCo= dark ? '#c4a35a' : '#8a6a2a';

  return (
    <div style={{ position:'fixed', inset:0, zIndex:10000, background:'rgba(0,0,0,.82)',
      backdropFilter:'blur(6px)', display:'flex', flexDirection:'column' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

      {/* Nav bar */}
      <div style={{ height:48, flexShrink:0, background:navBg, borderBottom:`1px solid ${navBdr}`,
        display:'flex', alignItems:'center', padding:'0 14px', gap:8 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={logoCo} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3.33 2 8.67 2 12 0v-5"/>
        </svg>
        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, color:logoCo, textTransform:'uppercase', letterSpacing:'.14em' }}>Cours 3.1</span>
        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, color:titleCo }}> — Analyse Fondamentale · {COMPANY_META.ticker} {COMPANY_META.fullName}</span>
        <div style={{ marginLeft:'auto' }}>
          <button onClick={onClose} style={{ width:32, height:32, display:'flex', alignItems:'center',
            justifyContent:'center', background:'transparent', border:`1px solid ${btnBdr}`,
            color:btnCo, cursor:'pointer', borderRadius:4, fontSize:17, flexShrink:0 }}>×</button>
        </div>
      </div>

      {/* Content avec sidebar intégrée */}
      <div id="af-course-scroll" style={{ flex:1, overflowY:'auto' }}>
        <CourseContent dark={dark}/>
      </div>
    </div>
  );
}




// ── V7 COMPOSANTS SÉMANTIQUES ─────────────────────────────────────────────────
// Nommés selon la convention V7 : rôle fonctionnel, pas technique.
// Chaque composant a une responsabilité unique et reçoit ses données par props.

/**
 * VerdictBanner — Bandeau de verdict synthétique (réponse avant preuves — V7 §2.1)
 * Affiche le résultat analytique en premier, les détails en dessous.
 */
function VerdictBanner({ score, label, signal, tone, icon, children }) {
  const color = getScoreColor(score);
  const sigColor = getStatusColor(tone || signal);
  return (
    <div style={{
      padding:'10px 14px',
      background:`${color}08`,
      border:`1.5px solid ${color}28`,
      borderRadius:8,
      display:'flex', flexDirection:'column', gap:6,
      marginBottom:10,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
        {icon && <span style={{ fontSize:15, color }}>{icon}</span>}
        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800, color }}>{label}</span>
        {signal && (
          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
            color:'var(--card)', background:sigColor,
            padding:'1px 7px', borderRadius:4 }}>{signal}</span>
        )}
        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800,
          color, marginLeft:'auto' }}>{Math.round(score)}/100</span>
      </div>
      {children && (
        <div style={{ fontSize:'clamp(11px,1.2vw,12px)', color:'var(--tx)', lineHeight:1.65 }}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * NarrativeBlock — Bloc de lecture économique (V7 §2.3)
 * Affiche un texte d'analyse structuré avec label, couleur et source.
 */
function NarrativeBlock({ label, color, source, children }) {
  const c = color || C.am;
  return (
    <div style={{
      margin:'.45rem 0',
      padding:'8px 12px',
      background:`${c}07`,
      border:`1px solid ${c}1a`,
      borderRadius:8,
    }}>
      {(label || source) && (
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
          {label && <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
            color:c, textTransform:'uppercase', letterSpacing:'.1em' }}>{label}</span>}
          {source && <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9.5, color:'var(--mu)' }}>· {source}</span>}
        </div>
      )}
      <div style={{ fontSize:'clamp(11px,1.2vw,12.5px)', color:'var(--tx)', lineHeight:1.7 }}>
        {children}
      </div>
    </div>
  );
}

/**
 * MetricBadge — Chip de métrique avec statut coloré (V7 §2.2)
 * Coloration selon le statut OK/NOK par rapport au seuil.
 */
function MetricBadge({ label, value, ok, color }) {
  const c = color || (ok === true ? C.g2 : ok === false ? C.r : C.am);
  return (
    <span style={{
      fontFamily:"ui-monospace,monospace", fontSize:10.5, fontWeight:700,
      color:c, background:`${c}12`,
      border:`1px solid ${c}30`,
      padding:'2px 8px', borderRadius:4,
      display:'inline-flex', alignItems:'center', gap:4,
    }}>
      {label}{value != null ? ` · ${value}` : ''}
    </span>
  );
}

/**
 * SectionLabel — Étiquette de section (V7 §2.4)
 */
function SectionLabel({ children, color }) {
  return (
    <div style={{
      fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
      color:color || C.am, textTransform:'uppercase',
      letterSpacing:'.12em', marginBottom:7, marginTop:14,
    }}>{children}</div>
  );
}

// ── PANNEAUX ──────────────────────────────────────────────────────────────────
export default function AnalyseFondamentaleCard({ symbol = DEMO_COMPANY_META.ticker, onBack } = {}) {
  const { darkMode: dark } = useInvelpsTheme();
  const [showCours, setShowCours] = useState(false);
  const [showCompass, setShowCompass] = useState(false);
  const [tab, setTab]             = useState("overview");
  const [expanded, setExpanded]   = useState(false);
  const bp  = useBreakpoint();
  const isMob = bp === 'xs';
  const isSm  = bp === 'xs' || bp === 'sm';
  const contentTopRef = useRef(null);
  const isFirstTab = useRef(true);

  // ── Chargement des données live depuis l'API (REQ1 + REQ2) ────────────────
  // 'loading' → fetch en cours (les données DEMO restent affichées entre-temps)
  // 'live'    → données réelles hydratées avec succès
  // 'demo'    → échec réseau/API, on reste sur les données de démonstration
  const [dataStatus, setDataStatus] = useState("loading");
  const [dataError, setDataError]   = useState(null);
  const [, forceRerender]           = useState(0);

  useEffect(() => {
    let cancelled = false;
    setDataStatus("loading");
    setDataError(null);
    Promise.all([fetchAFHeader(symbol), fetchAFSeries(symbol)])
      .then(([header, series]) => {
        if (cancelled) return;
        hydrateLiveData(header, series);
        forceRerender(v => v + 1);
        setDataStatus("live");
      })
      .catch(err => {
        if (cancelled) return;
        console.error("AnalyseFondamentaleCard — échec du chargement API, bascule sur les données de démonstration.", err);
        resetToDemoData();
        forceRerender(v => v + 1);
        setDataError(err.message || "Erreur inconnue");
        setDataStatus("demo");
      });
    return () => { cancelled = true; };
  }, [symbol]);

  useEffect(() => {
    if (isFirstTab.current) { isFirstTab.current = false; return; }
    contentTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [tab]);

  useEffect(() => { applyTheme(dark); }, [dark]);

  const activePillar = PILLARS.find(p => p.key === tab);
  const FSCORE       = RAW.fscore[YN] || 0;
  const qualColor    = s => s>=90?C.g2:s>=75?C.g:s>=55?C.am:s>=35?C.or:C.r;
  const pillarScore  = p => RAW[p.scoreKey]?.[YN] || 0;

  const ALL_TABS = ANALYSIS_ORDER.map(k => PILLARS.find(p => p.key === k)).filter(Boolean);

  return (
    <BpCtx.Provider value={bp}>
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0 }
        html, body {
          background:var(--bg); overflow-x:hidden; transition:background .3s;
          font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
        }
        button { font:inherit }
        table  { background:transparent }
        ::-webkit-scrollbar          { width:4px; height:4px }
        ::-webkit-scrollbar-thumb    { background:rgba(128,128,128,.25); border-radius:4px }
        ::selection                  { background:rgba(202,168,94,.22) }

        /* ── Nav piliers responsive ── */
        .pillar-nav-grid { grid-template-columns:repeat(9,minmax(0,1fr)) }
        @media(max-width:760px) { .pillar-nav-grid { grid-template-columns:repeat(3,minmax(0,1fr)) } }
        @media(max-width:420px) { .pillar-nav-grid { grid-template-columns:repeat(2,minmax(0,1fr)) } }

        /* ── Compass modal responsive ── */
        .compass-overlay { padding:clamp(7px,3vw,20px) !important }
        .compass-modal { width:min(960px,98vw) !important; max-height:92dvh !important }
        .compass-seqbar { display:flex }
        @media(max-width:720px) {
          .compass-overlay { align-items:stretch !important; padding:0 !important }
          .compass-modal { width:100vw !important; max-height:100dvh !important; height:100dvh !important; border-radius:0 !important; border-left:0 !important; border-right:0 !important }
          .compass-seqbar { display:none !important }
        }

        /* ── ProfilDescriptionRadarCard grid ── */
        .pdrc-grid { display:grid; grid-template-columns:1fr; gap:16px; width:100% }
        .pdrc-grid > * { min-width:0 }
        .pdrc-col-sep  { border-right:none; padding-right:0 }
        .pdrc-col-mid  { padding:0 }
        .pdrc-col-last { padding:0 }
        @media(min-width:640px) {
          .pdrc-grid     { grid-template-columns:1fr 1fr; gap:0 }
          .pdrc-col-sep  { border-right:1px solid var(--sep); padding-right:20px }
          .pdrc-col-mid  { padding:0 20px; border-right:1px solid var(--sep) }
          .pdrc-col-last { padding-left:20px }
        }
        @media(min-width:1024px) {
          .pdrc-grid { grid-template-columns:minmax(220px,.9fr) minmax(0,1.35fr) minmax(220px,.9fr) }
        }

        /* ── Responsive helpers ─────────────────────────── */
        .af-page   { min-height:100dvh; width:100%; max-width:1320px; margin:0 auto; background:var(--bg); padding:24px 16px 48px; box-sizing:border-box; transition:background .3s }
        .af-wrap   { width:100%; max-width:none; margin:0 auto }
        .af-card   { width:100%; min-width:0; background:var(--card); border:1px solid var(--bdr); border-radius:16px; box-shadow:0 1px 2px rgba(0,0,0,.06); overflow:hidden }
        .compass-box { width:100%; height:100%; display:inline-flex; align-items:center; justify-content:center; color:currentColor }
        @media(max-width:520px) {
          .af-page { padding:0 }
          .af-card { border-left:0; border-right:0; border-radius:0; min-height:100dvh }
        }

        /* Tabs scroll */
        .af-tabs   { display:flex; gap:2px; overflow-x:auto; -webkit-overflow-scrolling:touch;
                     scrollbar-width:none; padding:0 clamp(9px,2vw,19px);
                     border-bottom:1px solid var(--sep); background:var(--card) }
        .af-tabs::-webkit-scrollbar { display:none }

        /* Overview grid */
        .ov-top    { display:grid; grid-template-columns:1fr; gap:14px; align-items:start }
        @media(min-width:600px) { .ov-top { grid-template-columns:104px 1fr } }
        @media(min-width:1000px){ .ov-top { grid-template-columns:104px 1fr 220px } }

        .ov-pillar-bars { display:grid; grid-template-columns:1fr; gap:6px 14px }
        @media(min-width:500px) { .ov-pillar-bars { grid-template-columns:1fr 1fr } }

        .ov-forces { display:grid; grid-template-columns:1fr; gap:8px }
        @media(min-width:500px) { .ov-forces { grid-template-columns:1fr 1fr } }

        .ov-cards  { display:grid; grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); gap:8px }

        /* Pillar detail insight grid */
        .pv-insights { display:grid; grid-template-columns:1fr; gap:10px }
        @media(min-width:600px) { .pv-insights { grid-template-columns:1fr 1fr } }

        /* Pillar header badges */
        .pv-header { display:flex; align-items:flex-start; gap:10px; flex-wrap:wrap; margin-bottom:10px }
        .pv-badges { display:flex; align-items:center; gap:8px; flex-wrap:wrap }

        /* Mobile tabs: 2 rows × 4 cols calculator */
        .af-tabs-mob {
          display:grid;
          grid-template-columns:repeat(3,1fr);
          gap:3px;
          padding:6px;
          border-bottom:1px solid var(--sep);
          background:var(--card);
        }
        .af-tab-mob-btn {
          display:flex; flex-direction:column; align-items:center; justify-content:center;
          gap:1px; padding:5px 3px; border:none; border-radius:8px;
          cursor:pointer; transition:all .15s; background:var(--sur);
          min-height:0;
        }

        /* Mobile table: hide historical years, show only trend+2024+avg+seuil+signal */
        @media(max-width:479px) {
          .col-year-hist { display:none }
          .col-year-2024 { display:none }
          .ratio-name-full { display:none }
          .ratio-name-short { display:block !important }
        }
        @media(min-width:480px) {
          .ratio-name-full { display:block }
          .ratio-name-short { display:none }
          .col-year-2024 { display:table-cell }
        }


        /* ── Hardening responsive global ── */
        img, svg, canvas { max-width:100% }
        .af-card button, .af-card input, .af-card select { max-width:100% }
        .af-card p, .af-card span, .af-card div { overflow-wrap:anywhere }
        .af-table-scroll { width:100%; max-width:100%; overflow-x:auto; overscroll-behavior-inline:contain; scrollbar-gutter:stable }
        .af-table-scroll table { width:max-content; min-width:100%; }
        .course-score-grid { min-width:0 }

        @media (max-width: 1023px) {
          .af-page { padding:10px }
          .pdrc-grid { gap:14px }
          .pdrc-col-last { grid-column:1 / -1; padding:16px 0 0 !important; border-top:1px solid var(--sep) }
          .pdrc-col-last > div:last-child { min-height:220px !important }
        }

        @media (max-width: 767px) {
          .af-page { padding:6px }
          .af-card { border-radius:10px }
          .pillar-nav-grid { grid-template-columns:repeat(3,minmax(0,1fr)) !important }
          .pillar-nav-grid button { min-height:48px }
          .ov-cards { grid-template-columns:repeat(2,minmax(0,1fr)) }
          .compass-modal button { touch-action:manipulation }
          .course-score-grid { grid-template-columns:minmax(0,1fr) 28px 52px 42px !important }
        }

        @media (max-width: 639px) {
          .pdrc-grid { gap:0 !important; padding:12px !important }
          .pdrc-col-sep, .pdrc-col-mid, .pdrc-col-last {
            padding:0 !important; border-right:0 !important; min-width:0 !important;
          }
          .pdrc-col-mid, .pdrc-col-last { margin-top:14px; padding-top:14px !important; border-top:1px solid var(--sep) !important }
          .pdrc-col-last > div:last-child { min-height:210px !important; height:210px }
          .compass-modal { min-width:0 !important }
          .compass-modal > div:first-child { padding:9px 10px !important; gap:7px !important }
          .ov-top, .ov-pillar-bars, .ov-forces, .pv-insights { grid-template-columns:minmax(0,1fr) !important }
          .af-table-scroll { margin-inline:0 }
        }

        @media (max-width: 479px) {
          html { font-size:16px }
          .af-page { padding:0 }
          .af-card { border-radius:0; border-left:0; border-right:0 }
          .pillar-nav-grid { grid-template-columns:repeat(2,minmax(0,1fr)) !important }
          .pillar-nav-grid button { min-height:46px; padding:6px 4px !important }
          .ov-cards { grid-template-columns:minmax(0,1fr) }
          .af-tabs { padding-inline:6px }
          .af-tab-mob-btn { min-height:44px }
          .course-score-grid { grid-template-columns:minmax(0,1fr) 24px 46px 38px !important; column-gap:3px !important }
          .compass-overlay { padding:0 !important }
          .compass-modal { width:100% !important; height:100dvh !important; max-height:100dvh !important }
        }

        @media (max-width: 359px) {
          .pillar-nav-grid { grid-template-columns:minmax(0,1fr) !important }
          .course-score-grid { grid-template-columns:minmax(0,1fr) 22px 42px 34px !important }
          .pdrc-grid { padding:10px !important }
        }

        @media (hover:none) and (pointer:coarse) {
          button { min-height:36px }
          .af-table-scroll { -webkit-overflow-scrolling:touch }
        }
      `}</style>

      <div className="af-page">
        <div className="af-wrap">
          {onBack ? (
            <div style={{ padding: "8px 12px 0", maxWidth: 1320, margin: "0 auto" }}>
              <button type="button" onClick={onBack} style={{
                background: "none", border: "none", color: "var(--mu)", fontSize: 12, fontWeight: 700,
                marginBottom: 6, cursor: "pointer",
              }}>← Retour à l&apos;analyse</button>
            </div>
          ) : null}
          <div className="af-card" ref={contentTopRef}>

            {/* ── Statut chargement API — discret, non bloquant ── */}
            {dataStatus !== "live" && (
              <div style={{
                display:'flex', alignItems:'center', gap:8,
                padding:'6px 14px', fontFamily:'ui-monospace,monospace', fontSize:11.5,
                fontWeight:700, letterSpacing:'.03em',
                color: dataStatus === "loading" ? C.am : C.or,
                background: dataStatus === "loading" ? `${C.am}12` : `${C.or}12`,
                borderBottom:'1px solid var(--sep)',
              }}>
                {dataStatus === "loading"
                  ? <>⏳ Chargement des données live ({symbol})…</>
                  : <>⚠ API indisponible — données de démonstration affichées{dataError ? ` (${dataError})` : ''}</>}
              </div>
            )}

            {/* ── PROFIL DESCRIPTION RADAR — identique à TimingMarcheCard ── */}
            {(() => {
              const co = COMPANY_META;
              const isMtdUp   = co.mtdPct >= 0;
              const isYtdUp   = co.ytdPct >= 0;
              const scoreColor = colorForPct(co.iscore);
              const signalLbl  = FUND_META.signal?.[co.fiscalYear] || '—';
              const signalCol  = signalLbl==='BUY'?C.g:signalLbl==='HOLD'?C.am:signalLbl==='WEAK'?C.or:C.r;
              const PREVIEW = 320;
              const isLong  = co.description.length > PREVIEW;
              const displayed = expanded || !isLong
                ? co.description
                : co.description.slice(0, PREVIEW).trimEnd() + "…";
              const profilRows = [
                { label:"Région",         value:co.region,    tip:"Zone géographique principale d'activité et de génération de revenus." },
                { label:"Bourse",         value:co.exchange,  tip:"Marché boursier sur lequel l'action est cotée et échangée." },
                { label:"Secteur",        value:co.sector,    tip:"Grand domaine d'activité économique. Permet de comparer l'entreprise avec ses pairs sectoriels." },
                { label:"Industrie",      value:co.industry,  tip:"Sous-catégorie du secteur. Aide à identifier les concurrents directs." },
                { label:"Capitalisation", value:co.marketCap, tip:"Taille boursière. Large Cap >10 Md$, Mid Cap 2-10 Md$, Small Cap <2 Md$." },
                { label:"Style",          value:co.style,     tip:"Profil d'investissement : Croissance, Valeur ou Core (équilibré)." },
                { label:"Cycle",          value:co.cyclicals, tip:"Sensibilité au cycle économique. Cyclique suit la conjoncture, Défensif y résiste." },
              ];
              return (<>

              {/* ── HEADER ── */}
              <div style={{padding:"16px 20px 0"}}>

                {/* Row 1 : ticker gauche / prix droite */}
                <div style={{display:"flex", justifyContent:"space-between",
                  alignItems:"flex-start", gap:12, flexWrap:"wrap"}}>
                  <div style={{minWidth:0, flex:"1 1 220px"}}>
                    <div style={{fontSize:"clamp(20px,5vw,26px)", fontWeight:800,
                      letterSpacing:"-.02em", lineHeight:1.05, color:"var(--ts)",
                      overflowWrap:"anywhere"}}>{co.ticker}</div>
                    <div style={{fontSize:13, color:"var(--mu)", marginTop:3, fontWeight:500}}>
                      {co.fullName}
                    </div>
                  </div>
                  <div style={{textAlign:"right", flexShrink:0}}>
                    <div style={{fontFamily:"ui-monospace,monospace",
                      fontSize:"clamp(20px,5vw,26px)", fontWeight:800,
                      lineHeight:1, color:"var(--ts)"}}>
                      {co.price.toLocaleString("fr-FR",{minimumFractionDigits:2,maximumFractionDigits:2})}{" "}
                      <span style={{fontSize:19, fontWeight:700}}>{co.currency}</span>
                    </div>
                  </div>
                </div>

                {/* Row 2 : exchange·industry + MTD/YTD + badge + boutons */}
                <div style={{display:"flex", justifyContent:"space-between",
                  alignItems:"center", marginTop:6, flexWrap:"wrap", gap:8}}>
                  <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
                    <span style={{fontSize:13, fontWeight:600, color:C.am}}>
                      {co.exchange} · {co.industry}
                    </span>
                    <span style={{fontSize:13, fontWeight:700, color:isMtdUp?C.g2:C.r}}>
                      {isMtdUp?"+":""}{co.mtdPct.toFixed(1)}% MTD
                      <TipIconPdrc text="MTD (Month-to-Date) : variation depuis le 1er du mois. Indicateur de performance court terme."/>
                    </span>
                    <span style={{fontSize:13, fontWeight:700, color:isYtdUp?C.g2:C.r}}>
                      {isYtdUp?"+":""}{co.ytdPct.toFixed(1)}% YTD
                      <TipIconPdrc text="YTD (Year-to-Date) : variation depuis le 1er janvier. Performance de l'action sur l'année en cours."/>
                    </span>
                  </div>
                  {/* Signal badge + boutons */}
                  <div style={{display:"flex", gap:8, alignItems:"center",
                    flexWrap:"wrap", justifyContent:"flex-end"}}>
                    <Tip title="Signal fondamental"
                      body={`${FUND_META.summary_short?.[co.fiscalYear]||'—'} · Conviction : ${FUND_META.conviction?.[co.fiscalYear]||'—'}`}
                      src="Analysis_Fundamentals · Invelps">
                      <span style={{fontSize:12, fontWeight:700, padding:"3px 12px",
                        borderRadius:999, background:`${signalCol}18`,
                        color:signalCol, border:`1px solid ${signalCol}40`, cursor:"help"}}>
                        {signalLbl}
                      </span>
                    </Tip>
                    <div style={{display:"flex", alignItems:"center", gap:5}}>
                      <Tip title="Guide d'analyse" body="Ouvrir le guide : questions clés, ratios associés, seuils et logique d'interprétation pour chaque pilier.">
                        <button onClick={()=>setShowCompass(true)} style={{
                          display:"flex", alignItems:"center", justifyContent:"center",
                          width:28, height:28, borderRadius:8, cursor:"pointer",
                          border:`1px solid ${C.g}88`, background:`${C.g}18`, color:C.g,
                          fontSize:15,
                        }}>
                          🧭
                        </button>
                      </Tip>
                      <Tip title="Cours 3.1" body="Ouvrir le cours magistral : DuPont, ROIC/EVA, solvabilité, Income Quality.">
                        <button onClick={()=>setShowCours(true)} style={{
                          display:"flex", alignItems:"center", gap:5,
                          padding:"3px 11px", borderRadius:8, cursor:"pointer",
                          border:`1px solid ${C.edu}60`, background:`${C.edu}14`,
                          color:C.edu, fontSize:11, fontWeight:700,
                          fontFamily:"ui-monospace,monospace",
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                            stroke={C.edu} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                            <path d="M6 12v5c3.33 2 8.67 2 12 0v-5"/>
                          </svg>
                          Cours
                        </button>
                      </Tip>
                    </div>
                  </div>
                </div>

                {/* Row 3 : Mega Cap badge */}
                <div style={{marginTop:10}}>
                  <span style={{fontSize:12, fontWeight:700, padding:"3px 11px", borderRadius:8,
                    background:"rgba(30,40,34,.06)", color:"var(--mu)",
                    border:"1px solid var(--sep)"}}>{co.marketCap}</span>
                </div>

                <div style={{height:1, background:"var(--sep)", marginTop:12}}/>
              </div>

              {/* ── BODY : 3 colonnes Profil / Description / FScore+Radar ── */}
              <div style={{display:"grid", gap:0, padding:"16px 20px 20px", width:"100%"}}
                className="pdrc-grid">

                {/* COL 1 — Profil */}
                <div className="pdrc-col-sep">
                  <div style={{fontSize:11, fontWeight:800, textTransform:"uppercase",
                    letterSpacing:".1em", color:"var(--mu)",
                    fontFamily:"ui-monospace,monospace", marginBottom:10}}>Profil</div>
                  {profilRows.map(({label,value,tip},i,arr) => (
                    <div key={label}>
                      <div style={{display:"flex", alignItems:"center",
                        justifyContent:"space-between", padding:"7px 0", gap:8}}>
                        <span style={{fontSize:12, fontWeight:600, color:"var(--mu)",
                          display:"flex", alignItems:"center", gap:2, flexShrink:0}}>
                          {label}<TipIconPdrc text={tip}/>
                        </span>
                        <span style={{fontSize:13, fontWeight:700, color:"var(--ts)",
                          textAlign:"right"}}>{value}</span>
                      </div>
                      {i < arr.length-1 && <div style={{height:1, background:"var(--sep)"}}/>}
                    </div>
                  ))}
                </div>

                {/* COL 2 — Description */}
                <div className="pdrc-col-mid" style={{display:"flex", flexDirection:"column", gap:10}}>
                  <div style={{fontSize:11, fontWeight:800, textTransform:"uppercase",
                    letterSpacing:".1em", color:"var(--mu)",
                    fontFamily:"ui-monospace,monospace"}}>Description</div>
                  <div style={{borderRadius:10, padding:"12px 14px", flex:1,
                    background:"var(--sur)", border:"1px solid var(--sep)"}}>
                    <p style={{fontSize:13, lineHeight:1.72, color:"var(--ts)", margin:0}}>
                      {displayed}
                    </p>
                    {isLong && (
                      <button type="button" onClick={()=>setExpanded(v=>!v)}
                        style={{marginTop:8, fontSize:12, fontWeight:600,
                          color:C.g, background:"none", border:"none",
                          cursor:"pointer", padding:0}}>
                        {expanded ? "Réduire ↑" : "Voir plus ↓"}
                      </button>
                    )}
                  </div>
                </div>

                {/* COL 3 — FScore + Radar */}
                <div className="pdrc-col-last" style={{display:"flex", flexDirection:"column"}}>
                  <div style={{display:"flex", alignItems:"center",
                    justifyContent:"space-between", marginBottom:12}}>
                    <div style={{display:"flex", alignItems:"center", gap:4,
                      fontSize:11, fontWeight:800, textTransform:"uppercase",
                      letterSpacing:".1em", color:"var(--mu)",
                      fontFamily:"ui-monospace,monospace"}}>
                      FSCORE
                      <TipIconPdrc text="Score fondamental global (0–100) synthétisant 9 piliers : liquidité, profitabilité, croissance, efficacité, rentabilité, cash flow, solvabilité, allocation du capital et création de valeur. ≥70 = profil solide."/>
                    </div>
                    <div style={{display:"flex", alignItems:"baseline", gap:3}}>
                      <span style={{fontFamily:"ui-monospace,monospace", fontSize:28,
                        fontWeight:800, lineHeight:1,
                        color:heatColor(FSCORE)}}>{FSCORE.toFixed(1)}</span>
                      <span style={{fontSize:15, color:"var(--mu)", fontWeight:600}}>/100</span>
                    </div>
                  </div>
                  <div style={{flex:1, minHeight:240}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsRadarChart data={co.radarData} outerRadius="68%"
                        margin={{top:8,right:30,bottom:8,left:30}}>
                        <PolarGrid stroke="var(--sep)"/>
                        <PolarRadiusAxis angle={90} domain={[0,100]} tickCount={4}
                          tick={{fontSize:10,fill:"var(--mu)",fontFamily:"ui-monospace,monospace"}}
                          axisLine={false}/>
                        <PolarAngleAxis dataKey="axis" tick={(p)=><RadarAxisTick {...p}/>}/>
                        <RechartsTooltip content={<RadarTooltip/>}/>
                        <RechartsRadar dataKey="value"
                          stroke={C.g} fill={C.g}
                          strokeWidth={2} fillOpacity={0.22}
                          dot={{r:3, fill:C.g, strokeWidth:0}}/>
                      </RechartsRadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

              </div>
              </>);
            })()}

            {/* ── TABS : visibles uniquement sur la page d'un pilier ── */}
            {/* ── NAV PILIERS : 9 boutons en ligne, label sur desktop / abbr sur mobile ── */}
            {tab !== 'overview' && (
              <div style={{
                display:'flex', flexDirection:'column',
                gap:4, padding:'4px 8px',
                borderBottom:'1px solid var(--sep)',
              }}>
                {/* Bouton Synthèse — couleur alignée sur le FScore */}
                {(() => {
                  const sc = RAW.fscore?.[YN] || 0;
                  const hc = heatColor(sc);
                  return (
                    <button onClick={()=>setTab('overview')} style={{
                      fontFamily:"ui-monospace,monospace", fontSize:10.5, fontWeight:700,
                      padding:'5px 10px', cursor:'pointer',
                      border:`1px solid ${hc}55`,
                      background:`${hc}0e`, color:hc,
                      borderRadius:8, display:'flex', alignItems:'center',
                      justifyContent:'center', gap:6, transition:'all .15s',
                    }}>
                      <span style={{ fontSize:11 }}>←</span>
                      <span>Synthèse</span>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10.5, fontWeight:800 }}>{sc.toFixed(0)}</span>
                    </button>
                  );
                })()}
                {/* 9 piliers en grille horizontale */}
                <div className="pillar-nav-grid" style={{
                  display:'grid',
                  gap:3,
                }}>
                  {ALL_TABS.map(t => {
                    const active = tab === t.key;
                    const sc2    = pillarScore(t);
                    const hc     = heatColor(sc2);
                    const ac     = hc;   // couleur active = heatColor de la note
                    return (
                      <button key={t.key} onClick={()=>setTab(t.key)} style={{
                        display:'flex', flexDirection:'column',
                        alignItems:'center', justifyContent:'center',
                        gap:1, padding:'5px 3px',
                        border: active ? `1.5px solid ${ac}` : `1px solid ${hc}40`,
                        background: active ? `${ac}14` : `${hc}08`,
                        borderRadius:8, cursor:'pointer', transition:'all .15s',
                        minWidth:0, overflow:'hidden',
                      }}>
                        <span className="ratio-name-full" style={{
                          fontFamily:"ui-monospace,monospace", fontSize:'clamp(8.5px,0.9vw,10.5px)',
                          fontWeight:700, color: active ? ac : 'var(--mu)',
                          letterSpacing:'.02em', lineHeight:1.2,
                          whiteSpace:'nowrap', overflow:'hidden',
                          textOverflow:'ellipsis', maxWidth:'100%',
                          textAlign:'center',
                        }}>{t.label}</span>
                        <span className="ratio-name-short" style={{
                          fontFamily:"ui-monospace,monospace", fontSize:10, fontWeight:700,
                          color: active ? ac : 'var(--mu)',
                          letterSpacing:'.03em', lineHeight:1, display:'none',
                        }}>{t.abbr}</span>
                        <span style={{
                          fontFamily:"ui-monospace,monospace", fontSize:12, fontWeight:800,
                          color: active ? ac : hc, lineHeight:1,
                        }}>{Math.round(sc2)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── CONTENT ── */}
            <div style={{ padding:'0 clamp(5px,1.5vw,13px) clamp(9px,2vw,17px)' }}>
              {tab === "overview"
                ? <OverviewPanel onSelect={key => setTab(key)} bp={bp}/>
                : activePillar && <PillarView pillar={activePillar} bp={bp}/>
              }
            </div>

          </div>
        </div>
      </div>

      {showCompass && <CompassModal onClose={()=>setShowCompass(false)} dark={dark} currentPillarKey={tab}/>}
      {showCours && <CourseModal onClose={()=>setShowCours(false)} dark={dark}/>}
    </>
    </BpCtx.Provider>
  );
}
